import {
  initializeDocumentationTypography,
  refreshDocumentationTypography,
} from "./documentation_typography.js";
import { DesignerApiError, createDesignerApi } from "./designer_api.js";
import {
  NEXT_STEP,
  STEP_SEGMENT_FOLDERS,
  STEP_TARGETS,
  WORKFLOW_STEPS,
} from "./segment_registry.js";

let state = null;
let viewerReady = false;
let viewerInitialFitDone = false;
let publicationNetworkInitPromise = null;
const activePolls = new Set();
let activeNavFrame = 0;
const CUSTOM_TEMPLATE_ID = "__custom__";
const DEFAULT_STUDY_TEMPLATE_ID = "study5_box_breathing_pps";
const STATIC_RESOURCE_VERSION = "20260629-burst-source-mode";
const GOLD_STANDARD_SOURCE_PROFILE = "dynaspace_gaussian_burst_train";
const CONTINUOUS_SOURCE_PROFILE = "continuous_noise";
const BLOCK_RANDOMIZATION_STRATEGY = "seeded_gellermann_row_order_preserving";
const BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE = 2;
const BLOCK_CSV_DOWNLOAD_COLUMNS = [
  "block_index",
  "block_label",
  "block_trial_index",
  "trial_pool_index",
  "family",
  "family_label",
  "family_color_hex",
  "row_label",
  "row_color_hex",
  "folder_key",
  "folder_name",
  "noise_type",
  "noise_color_hex",
  "soa_ms",
  "soa_color_hex",
  "baseline_mode",
  "sequence_variant_key",
  "sequence_labels",
  "source_file_name",
  "trial_file_path",
  "source_sha256",
  "duration_ms",
  "duration_s",
  "looming_segment_onset_s",
  "tactile_onset_s",
  "repetition_index",
  "configured_repetitions",
  "fractional_extra",
  "channels",
  "tactile_channel",
];
const DEFAULT_LOUDNESS_POLICY = {
  schema: "pps-loudness-policy.v1",
  mode: "estimated_spl",
  calibration_mode: "estimate_allowed",
  calibration_status: "estimated_not_measured",
  start_spl_db: 55,
  end_spl_db: 75,
  instruction_offset_db: -6,
  movement_ramp_shape: "linear_db",
  hold_level_policy: "constant_start_and_endpoint",
  calibration_window: "final_500ms_active_movement_excluding_padding",
  calibration_window_s: 0.5,
  estimated_full_scale_spl_db: 109.2,
  audio_peak_ceiling_dbfs: -1,
  hardware: {
    audio_interface: "Native Instruments Komplete Audio 6 MK2",
    headphones: "Sennheiser HD 560S",
    headphone_knob: "maximum_clockwise",
    route: "ASIO / Komplete Audio ASIO Driver",
    runner_audio_volume_percent: 100,
    windows_volume_policy: "not_part_of_asio_calibration"
  }
};
const STATIC_REPO_ROOT = new URL(import.meta.url.includes("/compiled/assets/") ? "../../../../../" : "../../../", import.meta.url).href;
const STATIC_PRELOAD_INVENTORY_PATH = "/assets/preloads/preload_inventory.json";
const STATIC_TEMPLATE_DIR = "/study_templates/";
const HOSTED_TEMPLATE_DIRECTORY_URL = "https://github.com/GeorgeFejer91/pps-kit/tree/main/packages/pps-resources/study_templates";
const STATIC_AUDIT_SNAPSHOT_SCHEMA = "pps-static-dashboard-preview-audit-snapshot.v1";
const STATIC_AUDIT_QUERY_PARAM = "auditStaticPreview";
const STATIC_FORCE_QUERY_PARAM = "forceStaticPreview";
const TEMPLATE_QUERY_PARAM = "template";
const INITIAL_TEMPLATE_REQUEST = String(new URLSearchParams(window.location.search || "").get(TEMPLATE_QUERY_PARAM) || "").trim();
const STATIC_COMPANION_REQUIRED_MESSAGE =
  "This action needs the installed PPS Designer. Hosted mode can compose and export profiles, but cannot render audio, write workspace folders, or launch the Runner.";
const PROFILE_RECREATION_NOTICE =
  "Be aware: this is not the exact stimulus set used in the original study. This preload recreates the study's reported parameters within this interface, using the toolkit's local rendering and bundled profile assets.";
const DESIGNER_PROGRESS_KEY = "designer_progress";
const DESIGNER_PROGRESS_SCHEMA = "pps-designer-progress.v1";
const SEGMENT_INFO = {
  study: {
    kicker: "Segment 0",
    title: "Choose or Create Study",
    purpose: "Choose an existing study profile or create a new custom experiment design.",
    inputs: "Select a built-in template or custom profile, or name a new clean-slate design.",
    backendBeforeLink: "The application loads the selected profile from its ",
    backendLinkLabel: "Template Directory",
    backendAfterLink: ". New custom drafts are stored automatically in the researcher workspace. No folder selection is required.",
    output: "A new template starts a dedicated folder for storing assets used to run the experiment.",
    note: PROFILE_RECREATION_NOTICE
  },
  stimulus: {
    kicker: "Segment 1",
    title: "Build Looming Stimuli",
    purpose: "Define the auditory ingredients available to the experiment.",
    inputs: "Trajectory values, one generated or imported source, an ingredient name, and the loudness policy. Calibration assumptions are available as advanced settings.",
    backend: "Copies, imports, or bakes 1_core_audio_ingredients audio ingredients and records source metadata.",
    output: "Supplies stimulus ingredients for Trial Sequence Design."
  },
  trials: {
    kicker: "Segment 2",
    title: "Trial Sequence Design",
    purpose: "Arrange ingredients into within-trial audio sequences.",
    inputs: "Trial-family rows, left-to-right box order, fixed clips, alternatives within a box, and jitter values.",
    backend: "Bakes 2_trial_sequence_designs row-level sequence WAVs and a manifest mapping each file to its components and timing.",
    output: "Feeds exact trial sequences into baseline/tactile trial generation."
  },
  baseline: {
    kicker: "Segment 3",
    title: "Baseline and Tactile Trial Design",
    purpose: "Add tactile timing and define the baseline, catch, and auditory-only trial families.",
    inputs: "SOAs, one explicit baseline strategy, optional custom baseline SOAs, and separate catch/auditory-only choices.",
    backend: "Creates 3_tactile_and_baseline_trials target and baseline WAVs with tactile on channel 3, optional audio-only families, and row-preserving manifests.",
    output: "Supplies final trial files for repetition-pool construction."
  },
  block: {
    kicker: "Segment 4",
    title: "Trial Repetition Pool",
    purpose: "Decide how many times each trial family/file should appear.",
    inputs: "Global, folder, or file-level repetition counts.",
    backend: "Writes the 4_trial_repetition_pool CSV and manifest without duplicating WAV files.",
    output: "Provides the trial rows that will be arranged into blocks."
  },
  schedule: {
    kicker: "Segment 5",
    title: "Generate and Review Blocks",
    purpose: "Generate block CSVs and accept the final block set.",
    inputs: "Block count, generated seed, reproducibility review, and regenerate/accept decision.",
    backend: "Creates 5_block_csv_preview block CSV previews, then finalizes accepted block CSVs.",
    output: "Unlocks profile validation and provides the accepted block definitions."
  },
  run: {
    kicker: "Segment 6",
    title: "Profile Validation and Save",
    purpose: "Validate and save a reusable design without fixing the eventual study size.",
    inputs: "Part membership, instruction policy, example-order preview count, and the versioned block-order policy.",
    backend: "Stores the profile contract and deterministic order policy. The Runner materializes an immutable participant session later.",
    output: "Exports a portable .pps-profile or registers the profile for Runner discovery."
  }
};
const DOWNSTREAM_STEPS = {
  stimulus: ["trials", "baseline", "block", "schedule", "run"],
  trials: ["baseline", "block", "schedule", "run"],
  baseline: ["block", "schedule", "run"],
  block: ["schedule", "run"],
  schedule: ["run"],
  run: []
};
const LOCAL_BACKEND_DEFAULT = "http://127.0.0.1:8766";
const COMPANION_TOKEN_STORAGE_KEY = "ppsDashboard.companionToken";
const STATIC_AUDIT_CONTROL_IDS = [
  "edit-mode-button",
  "start-new-custom-design",
  "import-audio-spatialize",
  "import-audio-preserve",
  "bake-stimulus",
  "bake-trial-sequences",
  "bake-trial-files",
  "apply-trial-pool-repetitions",
  "bake-trial-pool",
  "regenerate-block-csvs",
  "accept-block-csvs",
  "regenerate-run-sequence",
  "save-study-profile",
  "export-profile-bundle",
  "export-data-acquisition-folder",
  "export-output-folder",
  "prepare-experiment"
];
const PANEL_RESIZE_SNAP_PX = 8;
const PANEL_HEIGHT_MIN = 150;
const PANEL_HEIGHT_MAX = 1000;
const SPLIT_DEFAULTS = {
  sideWidth: 460,
  ordersWidth: 420
};
const SPLIT_LIMITS = {
  sideWidth: { min: 320, max: 760 },
  ordersWidth: { min: 300, max: 760 }
};
const PROCEDURAL_NOISE_TYPES = [
  { value: "white", label: "White" },
  { value: "pink", label: "Pink" },
  { value: "blue", label: "Blue" },
  { value: "violet", label: "Violet" },
  { value: "brown", label: "Brown" }
];
const STIMULUS_TRAJECTORY_COLORS = {
  pink: "#d783b5",
  blue: "#4b7fc4",
  white: "#d8dde2",
  brown: "#8b623f",
  violet: "#8364b9",
  custom_audio: "#1c7a86",
  preserve: "#1c7a86",
  spatialize: "#1c7a86",
  prestimulus: "#7b8288"
};
const SOURCE_COLOR_OPTIONS = [
  { value: "pink", label: "Pink" },
  { value: "blue", label: "Blue" },
  { value: "white", label: "White" },
  { value: "brown", label: "Brown" },
  { value: "violet", label: "Violet" },
  { value: "custom_audio", label: "Teal / custom" }
];
// Keep in sync with --family-* tokens in styles.css (:root).
const TRIAL_FAMILY_COLORS = {
  audio_tactile: "#1c7a86",
  baseline: "#4b5fa8",
  catch: "#a4631b",
  auditory_only: "#86622d"
};
const RUN_INSTRUCTION_SLOTS = [
  { slot: "before_experiment", label: "Before experiment" },
  { slot: "before_each_block", label: "Before each block" },
  { slot: "after_each_block", label: "After each block" },
  { slot: "between_conditions", label: "Between conditions" },
  { slot: "after_experiment", label: "After experiment" }
];
const RUN_INSTRUCTION_CONTINUE_MODES = [
  { value: "click", label: "Click target" },
  { value: "delay", label: "Timed delay" },
  { value: "button", label: "Runner button" }
];
const IMPORTED_AUDIO_HANDLING = [
  { value: "spatialize", label: "Custom looming tone" },
  { value: "preserve", label: "Custom audio clip" }
];
const STIMULUS_SNIPPET_PLACEMENTS = [
  { value: "before", label: "Before stimulus" },
  { value: "after", label: "After stimulus" }
];
const BASELINE_STRATEGY_NOTES = {
  "": {
    label: "Baseline decision required",
    note: "Choose the baseline family before preparing a custom run."
  },
  none: {
    label: "No baseline trials",
    note: "Only audio-tactile trial files are baked from the trial-sequence variants."
  },
  min_anchor: {
    label: "Minimum SOA anchor",
    note: "The full trial audio is preserved and tactile is added at the first entered SOA."
  },
  max_anchor: {
    label: "Maximum SOA anchor",
    note: "The full trial audio is preserved and tactile is added at the last entered SOA."
  },
  tactile_only: {
    label: "Full SOA tactile-only",
    note: "Fixed clips and jitter are preserved, looming segments are silenced, and tactile is added at every SOA."
  },
  stationary_burst: {
    label: "Full SOA stationary bursts",
    note: "Fixed clips and jitter are preserved, looming segments are replaced by stationary burst trains, and tactile is added at every SOA."
  },
  min_max: {
    label: "Minimum + maximum SOA anchors",
    note: "The full trial audio is preserved and tactile is added at the first and last entered SOA."
  },
  soa_zero: {
    label: "Single anchor: sound onset",
    note: "Baseline tactile cues occur at auditory onset, giving a synchronous/minimum-timing anchor."
  },
  sound_offset: {
    label: "Single anchor: sound offset",
    note: "Baseline tactile cues occur at the end of the sound window, giving a late/maximum-timing anchor."
  },
  custom: {
    label: "Custom timings",
    note: "Custom baseline timings use the entered SOAs and the selected tactile-only/audio-tactile mode."
  }
};
const TRAJECTORY_FIELD_IDS = [
  "start-distance",
  "end-distance",
  "start-rotation",
  "end-rotation",
  "movement-duration",
  "start-hold",
  "end-hold"
];
const TRIAL_PREVIEW_LIMIT = 240;

const $ = (id) => document.getElementById(id);
const cssEscape = (value) => (window.CSS && window.CSS.escape ? window.CSS.escape(String(value)) : String(value).replace(/["\\]/g, "\\$&"));
const BOUNDED_SELECT_SELECTOR = "select:not([multiple]):not(.state-only):not([aria-hidden=\"true\"])";
const boundedSelectButtons = new WeakMap();
let boundedSelectMenu = null;
let activeBoundedSelect = null;
let activeBoundedOptionIndex = -1;
let boundedSelectTypeahead = "";
let boundedSelectTypeaheadTimer = 0;
let boundedSelectSequence = 0;
let apiBase = "";
let companionToken = "";
let templateLoadInFlight = false;
let initialTemplateRequestHandled = !INITIAL_TEMPLATE_REQUEST;
let pendingAudioImportMode = "preserve";
let pendingInstructionSlot = "";
let pendingBakeRecipe = null;
let selectedIngredientLabel = "";
let ingredientEditorDirty = false;
let ingredientEditorMode = "idle";
let audioColorModalReturnFocus = null;
let pendingDisplayColorHex = "#1C7A86";
let activeTrialRowPreviewAudio = null;
let activeSourcePreviewAudio = null;
let activeSourcePreviewNode = null;
let activeSourcePreviewButton = null;
let activeSourcePreviewClearTimer = null;
let sourcePreviewAudioContext = null;
let customizeModalReturnFocus = null;
let customizeModalMode = "copy";
let saveProfileModalReturnFocus = null;
let segmentInfoModalReturnFocus = null;
let trialPoolRepetitionDraft = {
  defaultRepetitions: 1,
  familyRepetitions: {},
  folderRepetitions: {},
  fileRepetitionOverrides: {},
  fractionalSeed: 20250604
};
let trialPoolDraftSourceHash = "";
let trialPoolDraftInitialized = false;

function boundedSelectLabel(select) {
  const option = select.selectedOptions?.[0] || select.options?.[select.selectedIndex];
  return String(option?.textContent || "Choose an option").trim();
}

function boundedSelectFieldLabel(select) {
  const explicitLabel = select.id ? document.querySelector(`label[for="${cssEscape(select.id)}"]`) : null;
  return String(
    select.getAttribute("aria-label")
    || explicitLabel?.textContent
    || select.closest(".field-row, .trial-condition-field, .source-card-field")?.querySelector("label")?.textContent
    || "Choose an option"
  ).trim();
}

function isBoundedSelectOptionDisabled(option) {
  return option.disabled || (option.parentElement?.tagName === "OPTGROUP" && option.parentElement.disabled);
}

function enabledBoundedSelectItems() {
  if (!boundedSelectMenu) return [];
  return [...boundedSelectMenu.querySelectorAll('[data-bounded-option-index]:not([aria-disabled="true"])')];
}

function closeBoundedSelect({ restoreFocus = false } = {}) {
  if (!boundedSelectMenu || !activeBoundedSelect) return;
  const button = boundedSelectButtons.get(activeBoundedSelect);
  boundedSelectMenu.hidden = true;
  boundedSelectMenu.replaceChildren();
  button?.setAttribute("aria-expanded", "false");
  activeBoundedSelect = null;
  activeBoundedOptionIndex = -1;
  boundedSelectTypeahead = "";
  window.clearTimeout(boundedSelectTypeaheadTimer);
  if (restoreFocus) button?.focus();
}

function selectBoundedOption(optionIndex) {
  if (!activeBoundedSelect) return;
  const option = activeBoundedSelect.options[optionIndex];
  if (!option || isBoundedSelectOptionDisabled(option) || option.hidden) return;
  const select = activeBoundedSelect;
  select.value = option.value;
  closeBoundedSelect({ restoreFocus: true });
  syncBoundedSelect(select);
  select.dispatchEvent(new Event("input", { bubbles: true }));
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

function focusBoundedOption(direction) {
  if (!boundedSelectMenu || !activeBoundedSelect) return;
  const enabled = enabledBoundedSelectItems();
  if (!enabled.length) return;
  const current = enabled.findIndex((item) => Number(item.dataset.boundedOptionIndex) === activeBoundedOptionIndex);
  let next = current;
  if (direction === "first") next = 0;
  else if (direction === "last") next = enabled.length - 1;
  else if (direction === "next") next = current < 0 ? 0 : (current + 1) % enabled.length;
  else if (direction === "previous") next = current < 0 ? enabled.length - 1 : (current - 1 + enabled.length) % enabled.length;
  const item = enabled[next];
  activeBoundedOptionIndex = Number(item.dataset.boundedOptionIndex);
  for (const option of enabled) option.tabIndex = -1;
  item.tabIndex = 0;
  item.focus({ preventScroll: true });
  item.scrollIntoView({ block: "nearest" });
}

function focusBoundedOptionByText(query) {
  const cleanQuery = String(query || "").trim().toLowerCase();
  if (!cleanQuery || !boundedSelectMenu) return;
  const items = enabledBoundedSelectItems();
  const item = items.find((candidate) => candidate.textContent.trim().toLowerCase().startsWith(cleanQuery));
  if (!item) return;
  for (const option of items) option.tabIndex = -1;
  item.tabIndex = 0;
  activeBoundedOptionIndex = Number(item.dataset.boundedOptionIndex);
  item.focus({ preventScroll: true });
  item.scrollIntoView({ block: "nearest" });
}

function moveFocusFromBoundedSelect(reverse = false) {
  const button = activeBoundedSelect ? boundedSelectButtons.get(activeBoundedSelect) : null;
  closeBoundedSelect();
  if (!button) return;
  const focusable = [...document.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => (
    !element.hidden
    && element.tabIndex >= 0
    && !element.closest('[inert], [aria-hidden="true"]')
    && element.getClientRects().length > 0
  ));
  const current = focusable.indexOf(button);
  const next = focusable[current + (reverse ? -1 : 1)];
  (next || button).focus();
}

function positionBoundedSelectMenu(button) {
  const rect = button.getBoundingClientRect();
  const viewportInset = 8;
  const menuGap = 4;
  const availableBelow = Math.max(0, window.innerHeight - rect.bottom - viewportInset - menuGap);
  const availableAbove = Math.max(0, rect.top - viewportInset - menuGap);
  const openAbove = availableBelow < 200 && availableAbove > availableBelow;
  const available = openAbove ? availableAbove : availableBelow;
  const menuHeight = Math.max(96, Math.min(320, available));
  boundedSelectMenu.style.left = `${rect.left}px`;
  boundedSelectMenu.style.width = `${rect.width}px`;
  boundedSelectMenu.style.maxHeight = `${menuHeight}px`;
  if (openAbove) {
    boundedSelectMenu.style.top = "auto";
    boundedSelectMenu.style.bottom = `${window.innerHeight - rect.top + menuGap}px`;
  } else {
    boundedSelectMenu.style.top = `${rect.bottom + menuGap}px`;
    boundedSelectMenu.style.bottom = "auto";
  }
}

function openBoundedSelect(select) {
  const button = boundedSelectButtons.get(select);
  if (!button) return;
  if (select.disabled) {
    if (isProfileReadonlyMode()) openCustomizeModal();
    return;
  }
  if (activeBoundedSelect === select && !boundedSelectMenu.hidden) {
    closeBoundedSelect({ restoreFocus: true });
    return;
  }
  closeBoundedSelect();
  activeBoundedSelect = select;
  activeBoundedOptionIndex = select.selectedIndex;
  boundedSelectMenu.replaceChildren();
  let lastGroup = null;
  let groupContainer = boundedSelectMenu;
  [...select.options].forEach((option, optionIndex) => {
    const group = option.parentElement?.tagName === "OPTGROUP" ? option.parentElement : null;
    if (group && group !== lastGroup) {
      groupContainer = document.createElement("div");
      groupContainer.className = "bounded-select-group-options";
      groupContainer.setAttribute("role", "group");
      groupContainer.setAttribute("aria-label", group.label);
      const heading = document.createElement("div");
      heading.className = "bounded-select-group";
      heading.textContent = group.label;
      heading.setAttribute("aria-hidden", "true");
      groupContainer.appendChild(heading);
      boundedSelectMenu.appendChild(groupContainer);
    } else if (!group) {
      groupContainer = boundedSelectMenu;
    }
    lastGroup = group;
    if (option.hidden) return;
    const optionDisabled = isBoundedSelectOptionDisabled(option);
    const item = document.createElement("div");
    item.className = "bounded-select-option";
    item.dataset.boundedOptionIndex = String(optionIndex);
    item.setAttribute("role", "option");
    item.setAttribute("aria-selected", String(option.selected));
    item.tabIndex = !optionDisabled && optionIndex === select.selectedIndex ? 0 : -1;
    item.textContent = option.textContent;
    if (optionDisabled) {
      item.classList.add("disabled");
      item.setAttribute("aria-disabled", "true");
    } else {
      item.addEventListener("click", () => selectBoundedOption(optionIndex));
    }
    groupContainer.appendChild(item);
  });
  boundedSelectMenu.setAttribute("aria-label", `${boundedSelectFieldLabel(select)} options`);
  positionBoundedSelectMenu(button);
  boundedSelectMenu.hidden = false;
  button.setAttribute("aria-expanded", "true");
  window.requestAnimationFrame(() => {
    const selected = boundedSelectMenu.querySelector(`[data-bounded-option-index="${select.selectedIndex}"]`);
    const target = selected && selected.getAttribute("aria-disabled") !== "true"
      ? selected
      : enabledBoundedSelectItems()[0];
    if (target) {
      target.tabIndex = 0;
      activeBoundedOptionIndex = Number(target.dataset.boundedOptionIndex);
      target.focus({ preventScroll: true });
    }
  });
}

function syncBoundedSelect(select) {
  const button = boundedSelectButtons.get(select);
  if (!button) return;
  const label = boundedSelectLabel(select);
  button.querySelector(".bounded-select-value").textContent = label;
  button.title = label;
  const fieldLabel = boundedSelectFieldLabel(select);
  // The custom proxy must mirror the native select's interaction lock. The
  // one exception is an inherited read-only profile, where the proxy remains
  // actionable so it can open the "create a custom copy" dialog.
  const opensCustomize = select.disabled && isProfileReadonlyMode();
  button.disabled = select.disabled && !opensCustomize;
  if (opensCustomize) {
    button.setAttribute("role", "button");
    button.setAttribute("aria-haspopup", "dialog");
    button.setAttribute("aria-controls", "customize-modal");
    button.removeAttribute("aria-disabled");
    button.setAttribute("aria-label", `${fieldLabel === "Choose an option" ? label : fieldLabel}: ${label}. Create a custom copy to edit.`);
  } else {
    button.setAttribute("role", "combobox");
    button.setAttribute("aria-haspopup", "listbox");
    button.setAttribute("aria-controls", "bounded-select-menu");
    button.setAttribute("aria-disabled", String(select.disabled));
    button.setAttribute("aria-label", fieldLabel);
  }
  button.classList.toggle("disabled", select.disabled);
  if (activeBoundedSelect === select && select.disabled) closeBoundedSelect();
}

function upgradeBoundedSelect(select) {
  if (!select.matches(BOUNDED_SELECT_SELECTOR) || boundedSelectButtons.has(select)) return;
  const shell = document.createElement("div");
  shell.className = "bounded-select-shell";
  select.before(shell);
  shell.appendChild(select);
  select.classList.add("bounded-select-native");
  select.tabIndex = -1;
  select.setAttribute("aria-hidden", "true");

  const button = document.createElement("button");
  const controlId = select.id || `bounded-select-${++boundedSelectSequence}`;
  button.type = "button";
  button.id = `${controlId}-combobox`;
  button.className = "bounded-select-button";
  button.dataset.boundedSelectFor = controlId;
  button.setAttribute("role", "combobox");
  button.setAttribute("aria-haspopup", "listbox");
  button.setAttribute("aria-controls", "bounded-select-menu");
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-label", boundedSelectFieldLabel(select));
  button.innerHTML = '<span class="bounded-select-value"></span><span class="bounded-select-arrow" aria-hidden="true"></span>';
  shell.appendChild(button);
  boundedSelectButtons.set(select, button);
  button.addEventListener("click", () => openBoundedSelect(select));
  button.addEventListener("keydown", (event) => {
    if (!["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) return;
    event.preventDefault();
    openBoundedSelect(select);
    if (event.key === "ArrowUp") window.requestAnimationFrame(() => focusBoundedOption("previous"));
  });
  syncBoundedSelect(select);
}

function upgradeBoundedSelects(root = document) {
  if (root instanceof HTMLSelectElement) upgradeBoundedSelect(root);
  for (const select of root.querySelectorAll?.(BOUNDED_SELECT_SELECTOR) || []) upgradeBoundedSelect(select);
  for (const select of document.querySelectorAll(".bounded-select-native")) syncBoundedSelect(select);
}

function initializeBoundedSelects() {
  boundedSelectMenu = document.createElement("div");
  boundedSelectMenu.id = "bounded-select-menu";
  boundedSelectMenu.className = "bounded-select-menu";
  boundedSelectMenu.setAttribute("role", "listbox");
  boundedSelectMenu.hidden = true;
  document.body.appendChild(boundedSelectMenu);
  boundedSelectMenu.addEventListener("keydown", (event) => {
    const action = { ArrowDown: "next", ArrowUp: "previous", Home: "first", End: "last" }[event.key];
    if (action) {
      event.preventDefault();
      focusBoundedOption(action);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectBoundedOption(activeBoundedOptionIndex);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeBoundedSelect({ restoreFocus: true });
    } else if (event.key === "Tab") {
      event.preventDefault();
      moveFocusFromBoundedSelect(event.shiftKey);
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      boundedSelectTypeahead += event.key.toLowerCase();
      window.clearTimeout(boundedSelectTypeaheadTimer);
      boundedSelectTypeaheadTimer = window.setTimeout(() => { boundedSelectTypeahead = ""; }, 650);
      focusBoundedOptionByText(boundedSelectTypeahead);
    }
  });
  document.addEventListener("pointerdown", (event) => {
    const button = activeBoundedSelect ? boundedSelectButtons.get(activeBoundedSelect) : null;
    if (!activeBoundedSelect || boundedSelectMenu.contains(event.target) || button?.contains(event.target)) return;
    closeBoundedSelect();
  });
  document.addEventListener("change", (event) => {
    if (event.target instanceof HTMLSelectElement) syncBoundedSelect(event.target);
  });
  document.addEventListener("scroll", (event) => {
    if (activeBoundedSelect && !boundedSelectMenu.contains(event.target)) closeBoundedSelect();
  }, true);
  window.addEventListener("resize", () => closeBoundedSelect());
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes" && mutation.target instanceof HTMLSelectElement) {
        syncBoundedSelect(mutation.target);
      }
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) upgradeBoundedSelects(node);
      }
      if (mutation.target instanceof HTMLSelectElement) syncBoundedSelect(mutation.target);
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["disabled"] });
  upgradeBoundedSelects();
}
let runSequencePreviewTimer = null;
let activePage = "toolkit";
let editModeActive = false;
let staticModeActive = false;
let designerDirty = false;
let workflowSaveInFlight = false;
let staticModeReason = "";
let staticPreloadInventory = null;
let staticTemplatesCache = null;
const staticTemplateCache = new Map();
const PAGE_TABS = ["toolkit", "documentation", "downloads"];
const PAGE_ROUTE_ALIASES = {
  "": "toolkit",
  toolkit: "toolkit",
  app: "toolkit",
  documentation: "documentation",
  docs: "documentation",
  download: "downloads",
  downloads: "downloads"
};
const PAGE_ROUTE_SEGMENTS = {
  toolkit: "",
  documentation: "documentation",
  downloads: "download"
};

async function api(path, options = {}) {
  const { skipStaticGuard = false, rawResponse = false, ...fetchOptions } = options;
  if (staticModeActive && !skipStaticGuard) {
    throw new Error(STATIC_COMPANION_REQUIRED_MESSAGE);
  }
  let response;
  try {
    const headers = { "Content-Type": "application/json", ...(fetchOptions.headers || {}) };
    if (companionToken) headers["X-PPS-Companion-Token"] = companionToken;
    response = await fetch(apiUrl(path), {
      ...fetchOptions,
      headers
    });
  } catch (error) {
    setConnectionStatus(false);
    throw error;
  }
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    let boundaryError = {};
    try {
      const raw = await response.text();
      const data = raw ? JSON.parse(raw) : {};
      detail = data.detail || detail;
      boundaryError = data.error && typeof data.error === "object" ? data.error : {};
    } catch (_err) {
      // Keep the status line when the response is not JSON, such as a static-site 404.
    }
    // HTTP errors still mean the companion answered; only fetch failures are disconnected.
    setConnectionStatus(true);
    throw new DesignerApiError(detail, {
      code: boundaryError.code,
      retryable: boundaryError.retryable,
      segmentKey: boundaryError.segment_key,
      status: response.status,
    });
  }
  setConnectionStatus(true);
  return rawResponse ? response : response.json();
}

const designerApi = createDesignerApi(api);

function apiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiBase}${path}`;
}

function staticResourceUrl(path) {
  const cleanPath = String(path || "").trim().replace(/\\/g, "/").replace(/^\/+/, "");
  if (!cleanPath) return "";
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
  return new URL(cleanPath, STATIC_REPO_ROOT).href;
}

async function fetchStaticJson(path) {
  const url = new URL(path, STATIC_REPO_ROOT);
  url.searchParams.set("v", STATIC_RESOURCE_VERSION);
  const response = await fetch(url.href, { headers: { "Accept": "application/json" } });
  if (!response.ok) {
    throw new Error(`Static preload asset unavailable: ${path}`);
  }
  return response.json();
}

function isLocalDashboardOrigin() {
  return ["127.0.0.1", "localhost", "[::1]", "::1"].includes(window.location.hostname);
}

function isCompanionDashboardOrigin() {
  return isLocalDashboardOrigin() && window.location.pathname.replace(/\\/g, "/").startsWith("/dashboard/");
}

function loadApiBase() {
  const companionOrigin = isCompanionDashboardOrigin();
  const stored = localStorage.getItem("ppsDashboard.apiBase");
  apiBase = companionOrigin ? "" : (stored || LOCAL_BACKEND_DEFAULT);
  $("backend-url").value = apiBase || (companionOrigin ? window.location.origin : LOCAL_BACKEND_DEFAULT);
}

function saveApiBase(value) {
  const trimmed = String(value || "").trim().replace(/\/+$/, "");
  const companionOrigin = isCompanionDashboardOrigin();
  apiBase = trimmed && !(companionOrigin && trimmed === window.location.origin) ? trimmed : "";
  if (apiBase) {
    localStorage.setItem("ppsDashboard.apiBase", apiBase);
  } else {
    localStorage.removeItem("ppsDashboard.apiBase");
  }
  $("backend-url").value = apiBase || (companionOrigin ? window.location.origin : LOCAL_BACKEND_DEFAULT);
}

function loadCompanionToken() {
  const params = new URLSearchParams(window.location.search || "");
  companionToken = String(params.get("ppsToken") || localStorage.getItem(COMPANION_TOKEN_STORAGE_KEY) || "").trim();
  const field = $("companion-token");
  if (field) field.value = companionToken;
}

function saveCompanionToken(value) {
  companionToken = String(value || "").trim();
  if (companionToken) {
    localStorage.setItem(COMPANION_TOKEN_STORAGE_KEY, companionToken);
  } else {
    localStorage.removeItem(COMPANION_TOKEN_STORAGE_KEY);
  }
  const field = $("companion-token");
  if (field) field.value = companionToken;
}

function setConnectionStatus(connected, label = "") {
  const status = $("connection-status");
  if (!status) return;
  status.textContent = label || (connected ? "connected" : "disconnected");
  status.className = `status-label ${connected ? "ready" : label ? "optional" : "required"}`;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function isCustomMode() {
  return Boolean(state?.custom_workflow?.is_custom);
}

function isProfileReadonlyMode() {
  return Boolean(state && (!isCustomMode() || state.custom_workflow?.is_finalized));
}

function isProfileFinalized() {
  if (!state) return false;
  if (state.custom_workflow?.is_custom) return Boolean(state.custom_workflow.is_finalized);
  return true;
}

function customViewModeLocked() {
  return Boolean(isCustomMode() && !editModeActive);
}

function normalizedDesignerProgress() {
  const workflow = state?.custom_workflow || {};
  const stored = state?.design?.study_profile_reference_parameters?.[DESIGNER_PROGRESS_KEY] || {};
  const fallbackStep = WORKFLOW_STEPS.includes(workflow.current_step) ? workflow.current_step : "stimulus";
  const editStep = WORKFLOW_STEPS.includes(workflow.edit_step)
    ? workflow.edit_step
    : WORKFLOW_STEPS.includes(stored.edit_step)
      ? stored.edit_step
      : fallbackStep;
  const confirmed = new Set(workflow.confirmed_steps || stored.confirmed_steps || WORKFLOW_STEPS.slice(0, WORKFLOW_STEPS.indexOf(editStep)));
  confirmed.add("study");
  confirmed.delete(editStep);
  const needsReview = new Set(workflow.needs_review_steps || stored.needs_review_steps || []);
  needsReview.delete(editStep);
  for (const stepId of confirmed) needsReview.delete(stepId);
  return {
    schema: DESIGNER_PROGRESS_SCHEMA,
    edit_step: editStep,
    confirmed_steps: WORKFLOW_STEPS.filter((stepId) => confirmed.has(stepId)),
    needs_review_steps: WORKFLOW_STEPS.filter((stepId) => needsReview.has(stepId)),
    revision: Math.max(0, Number(workflow.review_revision ?? stored.revision ?? 0)),
  };
}

function setHostedDesignerProgress(progress) {
  if (!state?.design) return;
  state.design.study_profile_reference_parameters = state.design.study_profile_reference_parameters || {};
  state.design.study_profile_reference_parameters[DESIGNER_PROGRESS_KEY] = clone(progress);
  state.custom_workflow = state.custom_workflow || {};
  state.custom_workflow.edit_step = progress.edit_step;
  state.custom_workflow.confirmed_steps = [...progress.confirmed_steps];
  state.custom_workflow.needs_review_steps = [...progress.needs_review_steps];
  state.custom_workflow.review_revision = progress.revision;
  const confirmed = new Set(progress.confirmed_steps);
  const needsReview = new Set(progress.needs_review_steps);
  state.custom_workflow.steps = (state.custom_workflow.steps || []).map((step) => ({
    ...step,
    decision_state: step.id === progress.edit_step
      ? "editing"
      : confirmed.has(step.id)
        ? "confirmed"
        : needsReview.has(step.id)
          ? "needs_review"
          : "locked",
  }));
}

function refreshHostedWorkflowSteps() {
  if (!staticModeActive || !state?.custom_workflow?.is_custom) return;
  const design = state.design || {};
  const protocol = design.protocol || {};
  const sources = [
    ...(design.noises || []),
    ...(design.custom_looming_files || []),
    ...(design.prestimulus_files || []),
  ];
  const strips = protocol.trial_strips || [];
  const steps = WORKFLOW_STEPS.map((stepId) => {
    const artifact = profileStepStatus(stepId);
    let complete = artifact.complete;
    let missing = [...(artifact.missing || [])];
    if (stepId === "study") {
      complete = Boolean(String(design.name || "").trim());
      missing = complete ? [] : ["Name this study before continuing."];
    } else if (stepId === "stimulus" && !sources.length) {
      complete = false;
      missing = ["Add at least one stimulus or instruction source."];
    } else if (stepId === "trials" && (!strips.length || strips.some((strip) => stripPreviewVariants(strip).length < 1))) {
      complete = false;
      missing = ["Add at least one complete trial family."];
    } else if (stepId === "baseline" && !(protocol.soa_values_ms || []).length) {
      complete = false;
      missing = ["Add at least one SOA value."];
    }
    return {
      id: stepId,
      label: SEGMENT_INFO[stepId]?.title || humanize(stepId),
      complete,
      missing: complete ? [] : missing,
    };
  });
  const firstIncomplete = steps.find((step) => !step.complete);
  state.custom_workflow.steps = steps;
  state.custom_workflow.current_step = firstIncomplete?.id || "run";
  state.custom_workflow.ready_to_render = steps.slice(0, -1).every((step) => step.complete);
  state.custom_workflow.ready_to_prepare = steps.every((step) => step.complete);
  state.custom_workflow.missing = steps.flatMap((step) => step.missing || []);
}

function initializeHostedDesignerProgress(editStep = "stimulus") {
  refreshHostedWorkflowSteps();
  setHostedDesignerProgress({
    schema: DESIGNER_PROGRESS_SCHEMA,
    edit_step: editStep,
    confirmed_steps: ["study"],
    needs_review_steps: [],
    revision: 0,
  });
}

function markDesignerDirty() {
  if (!state || !isCustomMode() || isProfileReadonlyMode() || !editModeActive || designerDirty) return;
  designerDirty = true;
  document.dispatchEvent(new CustomEvent("pps-designer-dirty"));
}

function markDesignerSaved() {
  designerDirty = false;
  document.dispatchEvent(new CustomEvent("pps-designer-saved"));
}

function discardIngredientEditorDraft() {
  ingredientEditorDirty = false;
  const selected = ingredientEntryForLabel(selectedIngredientLabel);
  if (selected) {
    hydrateIngredientEditor(selected);
    return;
  }
  selectedIngredientLabel = "";
  ingredientEditorMode = "idle";
  pendingBakeRecipe = null;
  pendingDisplayColorHex = "#1C7A86";
  if (state) applyTrajectoryControls(state.trajectory_controls || {});
  if ($("bake-label")) $("bake-label").value = "";
}

function confirmDiscardUnsaved(actionLabel = "continue") {
  if (!designerDirty && !ingredientEditorDirty) return true;
  const message = ingredientEditorDirty && designerDirty
    ? `Discard the uncommitted Segment 1 ingredient draft and other unsaved changes, then ${actionLabel}?`
    : ingredientEditorDirty
      ? `Discard the uncommitted Segment 1 ingredient draft and ${actionLabel}?`
      : `Discard unsaved changes and ${actionLabel}?`;
  if (!window.confirm(message)) return false;
  if (ingredientEditorDirty) discardIngredientEditorDraft();
  return true;
}

async function restorePersistedDraftForView() {
  if (staticModeActive) {
    const stored = await window.PPSDesigner?.drafts?.load?.().catch(() => null);
    if (!stored?.design || stored?.project?.project_id !== state?.project?.project_id) {
      throw new Error("The last saved browser draft could not be restored. Stay in Edit mode and export the draft before leaving.");
    }
    state = clone(stored);
    editModeActive = false;
    markDesignerSaved();
    renderAll();
    updateViewer();
    return;
  }
  editModeActive = false;
  markDesignerSaved();
  await loadState({ resetEditMode: true });
}

async function reconnectBackendFromControls({ includeToken = true } = {}) {
  if (!confirmDiscardUnsaved("reconnect to the companion")) return;
  saveApiBase($("backend-url").value);
  if (includeToken) saveCompanionToken($("companion-token")?.value || "");
  await loadState({ resetEditMode: true });
}

async function setEditMode(active) {
  if (active) {
    if (!state) {
      showToast("Wait for the dashboard state to load before editing.");
      editModeActive = false;
      renderEditModePanel();
      return false;
    }
    if (isProfileReadonlyMode()) {
      editModeActive = false;
      renderEditModePanel();
      openCustomizeModal();
      showToast("Name a new custom study before editing this profile.");
      return false;
    }
    editModeActive = true;
  } else {
    if (!confirmDiscardUnsaved("return to View mode")) {
      renderEditModePanel();
      return editModeActive;
    }
    if (designerDirty) {
      await restorePersistedDraftForView();
      return false;
    }
    editModeActive = false;
  }
  renderAll();
  updateViewer();
  return editModeActive;
}

function resetEditMode() {
  editModeActive = false;
  designerDirty = false;
  ingredientEditorDirty = false;
}

function renderEditModePanel() {
  const editButton = $("edit-mode-button");
  const panel = $("profile-mode-panel");
  if (!editButton || !panel) return;
  if (editModeActive && isProfileReadonlyMode()) editModeActive = false;
  const canEnterEdit = Boolean(state);
  panel.dataset.lockState = editModeActive ? "open" : "closed";
  editButton.setAttribute("aria-checked", String(editModeActive));
  editButton.disabled = !canEnterEdit;
  editButton.title = editModeActive
    ? "Return to locked View mode."
    : isProfileReadonlyMode()
      ? "Create a named custom copy and unlock Edit mode."
      : "Unlock this custom draft for editing.";
  editButton.setAttribute("aria-label", editModeActive
    ? "Editing unlocked. Switch to View mode."
    : isProfileReadonlyMode()
      ? "Editing locked. Create a named custom copy and switch to Edit mode."
      : "Editing locked. Switch this custom draft to Edit mode.");
  if (isProfileReadonlyMode()) {
    editButton.setAttribute("aria-haspopup", "dialog");
  } else {
    editButton.removeAttribute("aria-haspopup");
  }
}

// Editing the trajectory (typing distances/rotations OR dragging the preview
// markers) is allowed only when the trajectory inputs are editable. This ties
// drag-to-edit in the preview to the same gate as the input fields: if the
// fields are locked (read-only profile or a not-yet-reached custom step), the
// preview can still be viewed but the trajectory cannot be changed.
function trajectoryEditingEnabled() {
  if (staticModeActive) return false;
  const input = $("start-distance");
  const dynamicDraft = Boolean(pendingBakeRecipe && pendingBakeRecipe.kind !== "fixed_audio");
  return Boolean(dynamicDraft && input && !input.disabled);
}

function ensureEditableProject() {
  if (!isProfileReadonlyMode() && editModeActive) return true;
  if (!isProfileReadonlyMode()) {
    showToast("Switch to Edit mode before changing this study.");
    return false;
  }
  openCustomizeModal();
  showToast("Name a new custom study before editing this profile.");
  return false;
}

function setWorkflowDisabled(control, disabled) {
  if (!control) return;
  if (disabled) {
    if (!control.disabled) {
      control.dataset.workflowLockDisabled = "true";
    }
    control.disabled = true;
    return;
  }
  if (control.dataset.workflowLockDisabled === "true") {
    control.disabled = false;
    delete control.dataset.workflowLockDisabled;
  }
}

function profileReadonlyControlAllowed(control) {
  if (!control) return false;
  if (control.matches?.("[data-preview-source-label], .mobile-table-more")) return true;
  return Boolean(
    control.id
    && (
      control.id.startsWith("open-")
      || control.id === "download-block-randomization"
      || control.id === "save-study-profile"
      || control.id === "export-profile-bundle"
      || control.id === "export-experiment-json"
    )
  );
}

function viewModeControlAllowed(control) {
  if (!control) return false;
  if (control.matches?.("[data-preview-source-label], [data-select-ingredient], .mobile-table-more")) return true;
  if (control.id?.startsWith("open-")) return true;
  if (control.id === "download-block-randomization") return true;
  return Boolean(
    isProfileFinalized()
    && ["export-profile-bundle", "export-experiment-json"].includes(control.id)
  );
}

function profileStepStatus(stepId) {
  const segment = projectSegment(STEP_SEGMENT_FOLDERS[stepId]);
  const label = getWorkflowStep(stepId)?.label || humanize(stepId);
  let complete = segment.status === "ready";
  if (stepId === "schedule") {
    complete = segment.status === "ready" && Boolean(segment.accepted || state.block_csv_preview?.accepted);
  }
  const message = segment.last_validation_message || segment.message || `${label} is not ready.`;
  return {
    id: stepId,
    label,
    complete,
    missing: complete ? [] : [message]
  };
}

function segmentHasMaterializedOutput(stepId) {
  const segment = projectSegment(STEP_SEGMENT_FOLDERS[stepId]);
  if (!segment || !Object.keys(segment).length) return false;
  return Boolean(
    segment.status === "ready"
    || segment.status === "stale"
    || segment.status === "preview"
    || segment.manifest_exists
    || segment.csv_exists
    || segment.wav_count
    || segment.file_count
    || segment.total_count
    || segment.block_count
    || segment.accepted
  );
}

function confirmDownstreamInvalidation(actionLabel, fromStep) {
  if (!isCustomMode()) return true;
  const affected = (DOWNSTREAM_STEPS[fromStep] || []).filter(segmentHasMaterializedOutput);
  if (!affected.length) return true;
  const labels = affected.map((step) => getWorkflowStep(step)?.label || humanize(step));
  return window.confirm(`${actionLabel} will invalidate downstream segment outputs: ${labels.join(", ")}. Continue?`);
}

function showToast(message) {
  const toast = $("toast");
  toast.textContent = message;
  toast.classList.add("visible");
  window.clearTimeout(showToast._timer);
  showToast._timer = window.setTimeout(() => toast.classList.remove("visible"), 3200);
}

function numberValue(id, fallback = 0) {
  const value = Number($(id).value);
  return Number.isFinite(value) ? value : fallback;
}

function parseIntegerList(value) {
  const seen = new Set();
  return String(value || "")
    .split(/[,\s;]+/)
    .map((item) => Number.parseInt(item.trim(), 10))
    .filter((item) => {
      if (!Number.isFinite(item) || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

function parseNumberList(value) {
  return String(value || "")
    .split(",")
    .map((item) => Number.parseFloat(item.trim()))
    .filter((item) => Number.isFinite(item));
}

function formatList(items) {
  return (items || []).join(", ");
}

async function loadStaticInventory() {
  if (!staticPreloadInventory) {
    staticPreloadInventory = await fetchStaticJson(STATIC_PRELOAD_INVENTORY_PATH);
    staticPreloadInventory.profiles = Array.isArray(staticPreloadInventory.profiles)
      ? staticPreloadInventory.profiles
      : [];
  }
  return staticPreloadInventory;
}

function staticInventoryProfile(templateId, inventory = staticPreloadInventory) {
  return (inventory?.profiles || []).find((entry) => entry.template_id === templateId) || null;
}

async function loadStaticTemplateData(templateId) {
  const cleanId = String(templateId || "").trim();
  if (!/^[a-z0-9_]+$/i.test(cleanId)) {
    throw new Error(`Static template id is not valid: ${cleanId}`);
  }
  if (!staticTemplateCache.has(cleanId)) {
    staticTemplateCache.set(cleanId, fetchStaticJson(`${STATIC_TEMPLATE_DIR}${cleanId}.json`));
  }
  return clone(await staticTemplateCache.get(cleanId));
}

async function loadStaticTemplates() {
  if (staticTemplatesCache) return staticTemplatesCache;
  const inventory = await loadStaticInventory();
  const loaded = await Promise.all(
    (inventory.profiles || []).map(async (entry) => {
      const status = staticProfileAssetStatus(entry.template_id, inventory);
      try {
        const data = await loadStaticTemplateData(entry.template_id);
        return staticTemplatePayload(data, status);
      } catch (_error) {
        return staticTemplatePayloadFromInventory(entry, status);
      }
    })
  );
  staticTemplatesCache = loaded.sort((left, right) => {
    const leftPriority = studyProtocolPriority(left.template_id);
    const rightPriority = studyProtocolPriority(right.template_id);
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    const leftVerified = left.verification_status !== "verified" ? 1 : 0;
    const rightVerified = right.verification_status !== "verified" ? 1 : 0;
    if (leftVerified !== rightVerified) return leftVerified - rightVerified;
    return String(left.title || "").localeCompare(String(right.title || ""));
  });
  return staticTemplatesCache;
}

function studyProtocolPriority(templateId) {
  if (templateId === DEFAULT_STUDY_TEMPLATE_ID) return 0;
  return 100;
}

function staticProfileAssetStatus(templateId, inventory = staticPreloadInventory) {
  const entry = staticInventoryProfile(templateId, inventory);
  if (!entry) {
    return {
      template_id: templateId,
      status: "not_indexed",
      ready: false,
      asset_mode: "not_indexed",
      retrieval_strategy: "generate_on_local_companion",
      profile_manifest: "",
      profile_parameters_manifest: "",
      visible_variant_label: "",
      variant_display: templateId,
      recreation_status: {},
      runner_readiness: "",
      profile_checks_passed: false,
      segment_0_to_4_profile_checks_passed: false,
      finished_profile: false,
      segment_6_launchable: false,
      profile_completion_status: "unfinished_preload",
      primary_recreation_category: "",
      missing_parameter_count: 0,
      unsupported_structure_count: 0,
      local_only: true,
      asset_count: 0,
      ready_asset_count: 0,
      assets: [],
      catalog_segments: [],
      message: "No static preload entry exists for this profile."
    };
  }
  const assets = (entry.assets || []).map((asset) => {
    const path = String(asset.path || asset.local_path || "").replace(/\\/g, "/");
    return {
      label: String(asset.label || path.split("/").pop() || "Asset"),
      path,
      url: asset.url || staticResourceUrl(path),
      exists: Boolean(path),
      sha256: asset.sha256 || "",
      expected_sha256: asset.sha256 || "",
      sha256_ok: Boolean(asset.sha256),
      duration_s: asset.duration_s,
      sample_rate: asset.sample_rate,
      channels: asset.channels,
      source_kind: asset.source_kind || "",
      noise_type: asset.noise_type || "",
      tone_type: asset.tone_type || asset.noise_type || "",
      motion_mode: asset.motion_mode || "",
      render_mode: asset.render_mode || "",
      include_tactile: asset.include_tactile,
      trajectory_snapshot: asset.trajectory_snapshot || {}
    };
  });
  const status = assets.length ? "ready" : "recipe_only";
  const runnerReadiness = entry.runner_readiness || entry.recreation_status?.runner_readiness || "";
  const profileChecksPassed = Boolean(entry.profile_checks_passed ?? entry.recreation_status?.profile_checks_passed);
  const segmentGatePassed = Boolean(entry.segment_0_to_4_profile_checks_passed ?? entry.recreation_status?.segment_0_to_4_profile_checks_passed);
  const finishedProfile = runnerReadiness === "ready" && profileChecksPassed && segmentGatePassed;
  return {
    template_id: templateId,
    status,
    ready: status === "ready",
    asset_mode: entry.asset_mode || "bundled_local",
    retrieval_strategy: entry.retrieval_strategy || "bundled_or_download_from_github_pages",
    profile_manifest: entry.profile_manifest || "",
    profile_parameters_manifest: entry.profile_parameters_manifest || "",
    visible_variant_label: entry.visible_variant_label || "",
    variant_display: entry.variant_display || entry.title || templateId,
    recreation_status: clone(entry.recreation_status || {}),
    runner_readiness: runnerReadiness,
    profile_checks_passed: profileChecksPassed,
    segment_0_to_4_profile_checks_passed: segmentGatePassed,
    finished_profile: finishedProfile,
    segment_6_launchable: finishedProfile,
    profile_completion_status: finishedProfile ? "finished_segment_6_launchable" : "unfinished_preload",
    primary_recreation_category: entry.primary_recreation_category || entry.recreation_status?.primary_category || "",
    missing_parameter_count: Number(entry.missing_parameter_count ?? entry.recreation_status?.missing_parameter_count ?? 0),
    unsupported_structure_count: Number(entry.unsupported_structure_count ?? entry.recreation_status?.unsupported_structure_count ?? 0),
    local_only: Boolean(entry.local_only ?? true),
    asset_count: assets.length,
    ready_asset_count: assets.length,
    assets,
    catalog_segments: clone(entry.catalog_segments || []),
    message: status === "ready"
      ? "Static GitHub preload assets are available for browser inspection."
      : (entry.message || "This profile is indexed, but its assets require the local companion.")
  };
}

function staticTemplatePayload(data, status) {
  const payload = {
    template_id: data.template_id,
    title: data.title || status.variant_display || data.template_id,
    citation_label: staticCitationLabel(data),
    citation: data.citation || "",
    bibtex: staticBibtex(data),
    csl_json: staticCslJson(data),
    doi: data.doi || "",
    source_url: data.source_url || "",
    verification_status: data.verification_status || "unverified",
    notes: data.notes || ""
  };
  return attachStaticProfileStatus(payload, status);
}

function staticTemplatePayloadFromInventory(entry, status) {
  return attachStaticProfileStatus({
    template_id: entry.template_id,
    title: entry.title || entry.variant_display || entry.template_id,
    citation_label: `${entry.variant_display || entry.title || entry.template_id} [static preload]`,
    citation: entry.title || "",
    bibtex: "",
    csl_json: "",
    doi: "",
    source_url: entry.profile_manifest || "",
    verification_status: entry.recreation_status?.gui_recreatable ? "verified" : "indexed",
    notes: entry.message || ""
  }, status);
}

function attachStaticProfileStatus(payload, status) {
  return {
    ...payload,
    preload_asset_status: status,
    profile_parameters_manifest: status.profile_parameters_manifest,
    recreation_status: clone(status.recreation_status || {}),
    runner_readiness: status.runner_readiness || "",
    profile_checks_passed: Boolean(status.profile_checks_passed),
    segment_0_to_4_profile_checks_passed: Boolean(status.segment_0_to_4_profile_checks_passed),
    finished_profile: Boolean(status.finished_profile),
    segment_6_launchable: Boolean(status.segment_6_launchable),
    profile_completion_status: status.profile_completion_status || "unfinished_preload",
    primary_recreation_category: status.primary_recreation_category || "",
    missing_parameter_count: Number(status.missing_parameter_count || 0),
    unsupported_structure_count: Number(status.unsupported_structure_count || 0)
  };
}

function staticCitationLabel(data) {
  if (data.template_id === DEFAULT_STUDY_TEMPLATE_ID) {
    return `PPS Toolkit Study 5 white/pink protocol (2026) - White and pink looming sources [${data.verification_status || "verified"}]`;
  }
  const citation = String(data.citation || "").replace(/\s+/g, " ").trim();
  const match = citation.match(/^(.+?)\s*\((\d{4})\)[.,]?\s*(.*)$/);
  if (!match) return `${data.title || data.template_id} [${data.verification_status || "unverified"}]`;
  const rest = String(match[3] || "").trim();
  const title = (rest.split(/\.\s+/)[0] || data.title || "").replace(/[. ]+$/g, "");
  return `${match[1].replace(/[ ,.]+$/g, "")} (${match[2]}) - ${title || data.title} [${data.verification_status || "unverified"}]`;
}

function staticBibtex(data) {
  return `@misc{${data.template_id},\n  title = {${data.title || data.template_id}},\n  note = {PPS Toolkit static study profile},\n}\n`;
}

function staticCslJson(data) {
  return JSON.stringify({
    id: data.template_id,
    type: "document",
    title: data.title || data.template_id,
    URL: data.source_url || "",
    note: `PPS Toolkit static study profile; verification status: ${data.verification_status || "unverified"}`
  }, null, 2);
}

async function staticStateForTemplate(templateId) {
  const cleanId = String(templateId || DEFAULT_STUDY_TEMPLATE_ID).trim();
  if (!cleanId || cleanId === CUSTOM_TEMPLATE_ID) {
    throw new Error("Start the local companion backend before creating custom studies.");
  }
  const inventory = await loadStaticInventory();
  const templates = await loadStaticTemplates();
  const data = await loadStaticTemplateData(cleanId);
  const status = staticProfileAssetStatus(cleanId, inventory);
  const design = normalizeStaticTemplateDesign(data, status);
  const project = staticProjectPayload(design, status);
  const trialFileBake = staticTrialFileBakePayload(design, status);
  const trialPoolBake = staticTrialPoolBakePayload(design, trialFileBake);
  const blockCsvPreview = staticBlockCsvPreviewPayload(design, trialPoolBake, status);
  return {
    static_mode: true,
    static_mode_reason: staticModeReason,
    design,
    design_path: "",
    template_directory: {
      kind: "hosted",
      path: "",
      url: HOSTED_TEMPLATE_DIRECTORY_URL,
    },
    project,
    custom_projects: [],
    profile_catalog: { entries: [] },
    participant_id: "P001",
    templates,
    selected_template: cleanId,
    custom_workflow: {
      is_custom: false,
      ready_to_prepare: Boolean(status.finished_profile),
      current_step: "run",
      steps: [],
      missing: status.finished_profile ? [] : [status.message || "This static profile is not launchable yet."]
    },
    trajectory_controls: staticTrajectoryControls(design),
    viewer_payload: staticViewerPayload(design),
    protocol_summary: {},
    trial_preview: [],
    participant_orders: staticParticipantOrders(design),
    validation: [],
    render: staticRenderStatus(status),
    project_segments: staticProjectSegments(status, trialFileBake, trialPoolBake, blockCsvPreview),
    trial_sequence_bake: {},
    trial_file_bake: trialFileBake,
    trial_pool_bake: trialPoolBake,
    block_csv_preview: blockCsvPreview,
    run_sequence_setup: staticRunSetupPreview(design, status),
    preload_inventory: status,
    preflight: {
      render_ready: Boolean(status.ready),
      errors: [],
      warnings: status.ready ? [] : [status.message || "Static preload assets are not ready."]
    },
    session: null,
    jobs: []
  };
}

function staticSourceInventory(design) {
  const inventory = new Map();
  const addSource = (source, defaults = {}) => {
    const label = String(source?.label || "").trim();
    if (!label) return;
    const noiseType = String(source.noise_type || source.tone_type || defaults.noise_type || defaults.tone_type || "").toLowerCase();
    inventory.set(label, {
      label,
      kind: defaults.kind || source.source_kind || source.render_mode || "",
      noise_type: noiseType,
      duration_ms: Math.max(0, Math.round(Number(source.duration_s ?? source.target_duration_s ?? source.duration ?? defaults.duration_s ?? 4) * 1000)),
    });
  };
  for (const noise of design.noises || []) addSource(noise, { kind: "generated_noise", duration_s: 4 });
  for (const audio of design.custom_looming_files || []) addSource(audio, { kind: "custom_looming", duration_s: 4 });
  for (const clip of design.prestimulus_files || []) addSource(clip, { kind: "prestimulus", tone_type: "prestimulus", duration_s: 4 });
  return inventory;
}

function staticVariantDurationMs(variant, sourceInventory) {
  return variant.reduce((total, choice) => {
    if (choice.kind === "jitter") return total + Math.max(0, Number(choice.value || 0));
    return total + Math.max(0, Number(sourceInventory.get(choice.label)?.duration_ms || 0));
  }, 0);
}

function staticVariantNoiseType(variant, sourceInventory) {
  for (const choice of [...variant].reverse()) {
    if (choice.kind !== "audio") continue;
    const source = sourceInventory.get(choice.label);
    if (source?.noise_type && source.noise_type !== "prestimulus") return source.noise_type;
  }
  return "";
}

function staticTrialFileRow({
  templateId,
  rowIndex,
  rowLabel,
  family,
  folderName,
  variant,
  variantIndex,
  durationMs,
  soaMs = "",
  baselineMode = "",
  noiseType = "",
}) {
  const rowFolderName = `row_${String(rowIndex + 1).padStart(2, "0")}__${slugify(rowLabel)}`;
  const sequenceLabels = variant.map((choice) => choice.label).join(" | ");
  const variantKey = previewVariantKey(variant);
  const folderKey = slugify(`${rowFolderName}__${folderName}`);
  const familySuffix = family === "audio_tactile"
    ? `soa${soaMs}ms_tac120ms_ch3`
    : (family === "baseline" ? `baseline_${baselineMode}_soa${soaMs}ms_tac120ms_ch3` : `${family}_audio`);
  const sourceFileName = `${slugify(variantKey)}_${familySuffix}_total${durationMs}ms.wav`;
  const filePath = `static://${templateId}/${rowFolderName}/${folderName}/${sourceFileName}`;
  const fileKey = `${family}|${rowFolderName}|${variantIndex}|${soaMs}|${baselineMode}`;
  return {
    family,
    folder_key: folderKey,
    folder_name: folderName,
    row_folder_name: rowFolderName,
    row_label: rowLabel,
    file_key: fileKey,
    source_file_name: sourceFileName,
    file_path: filePath,
    sha256: "",
    duration_ms: durationMs,
    duration_s: Math.round((durationMs / 1000) * 1000) / 1000,
    looming_segment_onset_s: "",
    tactile_onset_s: Number.isFinite(Number(soaMs)) ? Math.round((Number(soaMs) / 1000) * 1000) / 1000 : "",
    soa_ms: soaMs,
    baseline_mode: baselineMode,
    sequence_variant_key: variantKey,
    sequence_labels: sequenceLabels,
    source_labels: sequenceLabels,
    noise_type: noiseType,
    channels: ["catch", "auditory_only"].includes(family) ? 2 : 3,
    tactile_channel: ["catch", "auditory_only"].includes(family) ? "" : 3,
  };
}

function staticTrialFileBakePayload(design, status) {
  const protocol = design.protocol || {};
  const sourceInventory = staticSourceInventory(design);
  const soaValues = (protocol.soa_values_ms || []).filter((value) => Number.isFinite(Number(value))).map((value) => Number(value));
  const baselineSoas = staticBaselineSoaValues(protocol, soaValues);
  const includeCatch = Boolean(protocol.include_catch_trials);
  const includeAuditoryOnly = Boolean(protocol.include_auditory_only_trials);
  const baselineCrossesVariants = protocol.baseline_crosses_sequence_variants !== false;
  const catchCrossesVariants = protocol.catch_crosses_sequence_variants !== false;
  const auditoryOnlyCrossesVariants = protocol.auditory_only_crosses_sequence_variants !== false;
  const files = [];
  let variantCounter = 1;
  for (const [rowIndex, strip] of (protocol.trial_strips || []).entries()) {
    const variants = stripPreviewVariants(strip);
    const rowLabel = strip.label || `Trial type ${rowIndex + 1}`;
    for (const [variantListIndex, variant] of variants.entries()) {
      const durationMs = staticVariantDurationMs(variant, sourceInventory);
      const noiseType = staticVariantNoiseType(variant, sourceInventory);
      const variantIndex = variantCounter;
      variantCounter += 1;
      for (const soaMs of staticStripSoaValues(strip, soaValues)) {
        files.push(staticTrialFileRow({
          templateId: design.study_profile_id || DEFAULT_STUDY_TEMPLATE_ID,
          rowIndex,
          rowLabel,
          family: "audio_tactile",
          folderName: "target_audio_tactile",
          variant,
          variantIndex,
          durationMs,
          soaMs,
          noiseType,
        }));
      }
      if (baselineCrossesVariants || variantListIndex === 0) {
        for (const soaMs of baselineSoas) {
          files.push(staticTrialFileRow({
            templateId: design.study_profile_id || DEFAULT_STUDY_TEMPLATE_ID,
            rowIndex,
            rowLabel,
            family: "baseline",
            folderName: "baseline",
            variant,
            variantIndex,
            durationMs,
            soaMs,
            baselineMode: protocol.baseline_custom_trial_mode || protocol.baseline_strategy || "tactile_only",
            noiseType,
          }));
        }
      }
      if (includeCatch && (catchCrossesVariants || variantListIndex === 0)) {
        files.push(staticTrialFileRow({
          templateId: design.study_profile_id || DEFAULT_STUDY_TEMPLATE_ID,
          rowIndex,
          rowLabel,
          family: "catch",
          folderName: "catch_trials",
          variant,
          variantIndex,
          durationMs,
          noiseType,
        }));
      }
      if (includeAuditoryOnly && (auditoryOnlyCrossesVariants || variantListIndex === 0)) {
        files.push(staticTrialFileRow({
          templateId: design.study_profile_id || DEFAULT_STUDY_TEMPLATE_ID,
          rowIndex,
          rowLabel,
          family: "auditory_only",
          folderName: "auditory_only",
          variant,
          variantIndex,
          durationMs,
          soaMs: 0,
          baselineMode: "auditory_only",
          noiseType,
        }));
      }
    }
  }
  const counts = {
    audio_tactile: files.filter((file) => file.family === "audio_tactile").length,
    baseline: files.filter((file) => file.family === "baseline").length,
    catch: files.filter((file) => file.family === "catch").length,
    auditory_only: files.filter((file) => file.family === "auditory_only").length,
  };
  return {
    root: "",
    manifest_path: status.profile_parameters_manifest || "",
    manifest_exists: Boolean(status.profile_checks_passed),
    schema: "pps-static-trial-files-preview.v1",
    status: status.profile_checks_passed ? "static_preload" : "missing",
    audio_tactile_count: counts.audio_tactile,
    baseline_count: counts.baseline,
    catch_count: counts.catch,
    auditory_only_count: counts.auditory_only,
    total_count: files.length,
    rows: [],
    files,
    manifest_sha256: `static-${design.study_profile_id || DEFAULT_STUDY_TEMPLATE_ID}-${files.length}`,
  };
}

function staticStripSoaValues(strip, fallbackSoas) {
  const stripSoas = (strip?.soa_values_ms || [])
    .filter((value) => Number.isFinite(Number(value)))
    .map((value) => Number(value));
  return stripSoas.length ? stripSoas : fallbackSoas;
}

function staticBaselineSoaValues(protocol, soaValues) {
  if (!protocol.include_baseline_trials) return [];
  const strategy = protocol.baseline_strategy || "none";
  if (!strategy || strategy === "none") return [];
  const custom = (protocol.baseline_soa_values_ms || []).filter((value) => Number.isFinite(Number(value))).map((value) => Number(value));
  if (strategy === "custom") return custom;
  if (strategy === "min_anchor") return soaValues.length ? [soaValues[0]] : [];
  if (strategy === "max_anchor") return soaValues.length ? [soaValues[soaValues.length - 1]] : [];
  if (strategy === "min_max") return Array.from(new Set([soaValues[0], soaValues[soaValues.length - 1]].filter((value) => Number.isFinite(Number(value)))));
  if (strategy === "tactile_only" || strategy === "stationary_burst") return custom.length ? custom : soaValues;
  if (strategy === "soa_zero") return [0];
  if (strategy === "custom") return custom;
  return custom.length ? custom : soaValues;
}

function staticTrialPoolSettings(protocol) {
  const defaults = protocol.trial_pool_repetition_defaults || {};
  const defaultRepetitions = normalizeRepetitionValue(defaults.default ?? protocol.repetitions_per_condition ?? 1, 1);
  const familyRepetitions = {};
  for (const [key, value] of Object.entries(defaults)) {
    const family = trialPoolFamilyKey(key);
    if (["audio_tactile", "baseline", "catch", "auditory_only"].includes(family)) {
      familyRepetitions[family] = normalizeRepetitionValue(value, defaultRepetitions);
    }
  }
  const exactFamilyCounts = trialPoolExactFamilyCounts(protocol);
  const settings = {
    default_repetitions: defaultRepetitions,
    family_repetitions: familyRepetitions,
    folder_repetitions: {},
    file_repetition_overrides: {},
    fractional_seed: normalizedRandomizationSeed(protocol.random_seed),
  };
  if (Object.keys(exactFamilyCounts).length) settings.exact_family_trial_counts = exactFamilyCounts;
  return settings;
}

function staticConfiguredRepetitions(file, settings) {
  const family = trialPoolFamilyKey(file.family);
  return normalizeRepetitionValue(settings.family_repetitions?.[family] ?? settings.default_repetitions ?? 1, 1);
}

function trialPoolExactFamilyCounts(source = {}) {
  const raw = (
    source?.exact_family_trial_counts
    && typeof source.exact_family_trial_counts === "object"
    && !Array.isArray(source.exact_family_trial_counts)
  ) ? source.exact_family_trial_counts : source;
  const exactCounts = {};
  for (const [family, key] of [["baseline", "baseline_trials_exact"], ["catch", "catch_trials_exact"], ["auditory_only", "auditory_only_trials_exact"]]) {
    const value = raw?.[family] ?? raw?.[key];
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) exactCounts[family] = Math.max(0, Math.trunc(parsed));
  }
  return exactCounts;
}

function trialPoolExactRecordSortKey(record, seed, family) {
  const file = record?.file || {};
  const fileKey = String(file.file_key || "");
  const sequenceKey = String(file.sequence_variant_key || file.variant_key || "");
  const sourceFileName = String(file.source_file_name || (file.file_path || "").split(/[\\/]/).pop() || "");
  const orderKey = `${seed}|exact_family_count|${family}|${fileKey}|${sequenceKey}|${sourceFileName}`;
  return [stableHashInt(orderKey), fileKey, sequenceKey, sourceFileName];
}

function applyTrialPoolExactFamilyCounts(records, settings = {}, warnings = []) {
  const exactCounts = trialPoolExactFamilyCounts(settings);
  if (!Object.keys(exactCounts).length) return {};
  const seed = normalizedRandomizationSeed(settings.fractional_seed ?? state?.design?.protocol?.random_seed);
  for (const family of Object.keys(exactCounts).sort()) {
    const targetCount = exactCounts[family];
    const familyRecords = records.filter((record) => trialPoolFamilyKey(record?.file?.family) === family);
    if (!familyRecords.length) {
      if (targetCount) warnings.push(`Exact ${family} trial count ${targetCount} could not be applied because Segment 3 has no ${family} files.`);
      continue;
    }
    const baseCount = Math.floor(targetCount / familyRecords.length);
    const extraCount = targetCount % familyRecords.length;
    const ordered = [...familyRecords].sort((left, right) => {
      const leftKey = trialPoolExactRecordSortKey(left, seed, family);
      const rightKey = trialPoolExactRecordSortKey(right, seed, family);
      for (let index = 0; index < leftKey.length; index += 1) {
        if (leftKey[index] === rightKey[index]) continue;
        if (typeof leftKey[index] === "number" && typeof rightKey[index] === "number") {
          return leftKey[index] - rightKey[index];
        }
        return String(leftKey[index]).localeCompare(String(rightKey[index]));
      }
      return 0;
    });
    for (const [index, record] of ordered.entries()) {
      const occurrenceCount = baseCount + (index < extraCount ? 1 : 0);
      record.configured = occurrenceCount;
      record.baseRepetitions = occurrenceCount;
      record.fractionalRemainder = 0;
      record.fractionalExtra = false;
      record.balancingStratum = `${family}|exact_count|target${targetCount}|files${familyRecords.length}`;
      record.balancingParentStratum = record.balancingStratum;
    }
  }
  return exactCounts;
}

function staticTrialPoolRows(files, settings) {
  const records = files.map((file, index) => {
    const configured = staticConfiguredRepetitions(file, settings);
    const baseRepetitions = Math.floor(configured);
    const fractionalRemainder = normalizeRepetitionValue(configured - baseRepetitions, 0);
    return {
      file,
      index,
      configured,
      baseRepetitions,
      fractionalRemainder,
      fractionalExtra: false,
      balancingStratum: fractionalRemainder ? trialPoolFractionalStratum(file, fractionalRemainder) : "",
      balancingParentStratum: fractionalRemainder ? trialPoolFractionalParentStratum(file, fractionalRemainder) : "",
      sourceLineage: trialPoolSourceLineage(file),
    };
  });
  const fractionalGroups = new Map();
  for (const record of records) {
    if (!record.fractionalRemainder) continue;
    if (!fractionalGroups.has(record.balancingStratum)) fractionalGroups.set(record.balancingStratum, []);
    fractionalGroups.get(record.balancingStratum).push(record);
  }
  const seed = normalizedRandomizationSeed(settings.fractional_seed);
  for (const [stratum, group] of fractionalGroups.entries()) {
    const extraCount = Math.max(0, Math.min(group.length, Math.floor(group.length * group[0].fractionalRemainder + 0.5)));
    if (!extraCount) continue;
    const ordered = [...group].sort((a, b) => {
      const left = `${a.sourceLineage}|${a.file.sequence_variant_key || ""}|${a.file.source_file_name || ""}`;
      const right = `${b.sourceLineage}|${b.file.sequence_variant_key || ""}|${b.file.source_file_name || ""}`;
      return left.localeCompare(right);
    });
    const offset = stableHashInt(`${seed}|${stratum}`) % ordered.length;
    const rotated = ordered.slice(offset).concat(ordered.slice(0, offset));
    for (const record of rotated.slice(0, extraCount)) record.fractionalExtra = true;
  }
  applyTrialPoolExactFamilyCounts(records, settings);
  const rows = [];
  let trialPoolIndex = 1;
  for (const record of records) {
    const occurrenceCount = record.baseRepetitions + (record.fractionalExtra ? 1 : 0);
    for (let repetitionIndex = 1; repetitionIndex <= occurrenceCount; repetitionIndex += 1) {
      rows.push({
        trial_pool_index: trialPoolIndex,
        family: trialPoolFamilyKey(record.file.family),
        folder_key: record.file.folder_key || "",
        folder_name: record.file.folder_name || "",
        row_label: record.file.row_label || "",
        source_file_name: record.file.source_file_name || "",
        trial_file_path: record.file.file_path || "",
        source_sha256: record.file.sha256 || "",
        duration_ms: record.file.duration_ms || 0,
        duration_s: record.file.duration_s || "",
        looming_segment_onset_s: record.file.looming_segment_onset_s || "",
        tactile_onset_s: record.file.tactile_onset_s || "",
        repetition_index: repetitionIndex,
        configured_repetitions: record.configured,
        base_repetitions: record.baseRepetitions,
        fractional_remainder: record.fractionalRemainder,
        fractional_extra: repetitionIndex > record.baseRepetitions ? 1 : 0,
        balancing_seed: seed,
        balancing_stratum: record.balancingStratum,
        balancing_signature: `static-${seed}`,
        source_lineage: record.sourceLineage,
        soa_ms: record.file.soa_ms || "",
        baseline_mode: record.file.baseline_mode || "",
        sequence_variant_key: record.file.sequence_variant_key || "",
        sequence_labels: record.file.sequence_labels || "",
        noise_type: record.file.noise_type || "",
        channels: record.file.channels || "",
        tactile_channel: record.file.tactile_channel || "",
      });
      trialPoolIndex += 1;
    }
  }
  return rows;
}

function staticTrialPoolBakePayload(design, trialFileBake) {
  const protocol = design.protocol || {};
  const settings = staticTrialPoolSettings(protocol);
  const poolRows = staticTrialPoolRows(trialFileBake.files || [], settings);
  const familyCounts = { audio_tactile: 0, baseline: 0, catch: 0, auditory_only: 0 };
  for (const row of poolRows) {
    if (Object.prototype.hasOwnProperty.call(familyCounts, row.family)) familyCounts[row.family] += 1;
  }
  const totalDurationMs = poolRows.reduce((total, row) => total + Number(row.duration_ms || 0), 0);
  return {
    root: "",
    manifest_path: "",
    csv_path: "",
    manifest_exists: Boolean(poolRows.length),
    schema: "pps-static-trial-repetition-pool-preview.v1",
    status: poolRows.length ? "static_preload" : "missing",
    settings,
    unique_file_count: trialFileBake.files?.length || 0,
    total_count: poolRows.length,
    total_trials: poolRows.length,
    audio_tactile_count: familyCounts.audio_tactile,
    baseline_count: familyCounts.baseline,
    catch_count: familyCounts.catch,
    auditory_only_count: familyCounts.auditory_only,
    estimated_total_duration_ms: totalDurationMs,
    family_counts: familyCounts,
    preview_rows: poolRows,
  };
}

function staticRowColor(value) {
  const hue = stableHashInt(value || "row") % 360;
  return `hsl(${hue} 45% 48%)`;
}

function staticCountBy(rows, key) {
  const counts = {};
  for (const row of rows || []) {
    const value = String(row[key] ?? "");
    if (!value) continue;
    counts[value] = (counts[value] || 0) + 1;
  }
  return counts;
}

function blockCsvAssignmentKey(row) {
  return ["family", "row_label", "soa_ms", "source_lineage", "sequence_variant_key", "baseline_mode"]
    .map((key) => String(row?.[key] ?? ""))
    .join("|");
}

function blockCsvFeatureKeys(row) {
  return [
    `family:${row?.family || ""}`,
    `row:${row?.row_label || ""}`,
    `soa:${row?.soa_ms || ""}`,
    `source:${row?.source_lineage || ""}`,
    `variant:${row?.sequence_variant_key || ""}`,
  ];
}

function blockCsvSortableTrialIndex(row) {
  const index = Number(row?.trial_pool_index || 0);
  return Number.isFinite(index) ? index : 0;
}

function blockCsvRowKey(row) {
  const folderKey = String(row?.folder_key || "");
  const parts = folderKey.split("__");
  if (parts.length >= 2 && /^row_\d+$/i.test(parts[0])) return parts.slice(0, 2).join("__").toLowerCase();
  const label = String(row?.row_label || row?.row_folder_name || "").trim();
  return slugify(label).toLowerCase() || "row_01";
}

function blockCsvRowSortTuple(rowKey, firstSeen) {
  const match = String(rowKey || "").match(/row[_-]?(\d+)/i);
  const rowNumber = match ? Number(match[1]) : firstSeen + 1;
  return [rowNumber, firstSeen, String(rowKey || "")];
}

function compareTuples(left, right) {
  for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
    if (left[index] < right[index]) return -1;
    if (left[index] > right[index]) return 1;
  }
  return 0;
}

function blockCsvRowOrder(poolRows) {
  const seen = new Map();
  for (const row of poolRows || []) {
    const rowKey = blockCsvRowKey(row);
    if (!seen.has(rowKey)) {
      seen.set(rowKey, {
        row_key: rowKey,
        row_label: String(row.row_label || rowKey),
        first_seen: seen.size,
      });
    }
  }
  return Array.from(seen.values())
    .sort((left, right) => compareTuples(
      blockCsvRowSortTuple(left.row_key, left.first_seen),
      blockCsvRowSortTuple(right.row_key, right.first_seen)
    ))
    .map(({ first_seen: _firstSeen, ...rowInfo }) => rowInfo);
}

function blockCsvRunFeatures(row) {
  return {
    condition: blockCsvAssignmentKey(row),
    family: trialPoolFamilyKey(row?.family || ""),
    soa: String(row?.soa_ms || ""),
    source: String(row?.source_lineage || row?.noise_type || ""),
    variant: String(row?.sequence_variant_key || row?.source_file_name || ""),
    baseline: String(row?.baseline_mode || ""),
  };
}

function blockCsvImmediateExactRepeat(row, orderedRows) {
  const previous = orderedRows[orderedRows.length - 1];
  return Boolean(previous && blockCsvRunFeatures(previous).condition === blockCsvRunFeatures(row).condition);
}

function blockCsvFeatureRunExceeded(row, orderedRows, maxRun) {
  const safeMaxRun = Math.max(1, Number(maxRun || BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE));
  if (orderedRows.length < safeMaxRun) return false;
  const candidate = blockCsvRunFeatures(row);
  const recent = orderedRows.slice(-safeMaxRun);
  for (const key of ["family", "soa", "source", "variant", "baseline"]) {
    const value = candidate[key];
    if (!value) continue;
    if (recent.every((previous) => blockCsvRunFeatures(previous)[key] === value)) return true;
  }
  return false;
}

function blockCsvViolatesGellermann(row, orderedRows, maxRun) {
  return blockCsvImmediateExactRepeat(row, orderedRows) || blockCsvFeatureRunExceeded(row, orderedRows, maxRun);
}

function seededGellermannOrderRows(rows, seedText, maxRun = BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE) {
  const remaining = [...(rows || [])].sort((left, right) => {
    const leftHash = stableHashInt(`${seedText}|${left.trial_pool_index || ""}|${blockCsvAssignmentKey(left)}`);
    const rightHash = stableHashInt(`${seedText}|${right.trial_pool_index || ""}|${blockCsvAssignmentKey(right)}`);
    return leftHash - rightHash || blockCsvSortableTrialIndex(left) - blockCsvSortableTrialIndex(right);
  });
  const ordered = [];
  while (remaining.length) {
    let selectedIndex = remaining.findIndex((row) => !blockCsvViolatesGellermann(row, ordered, maxRun));
    if (selectedIndex < 0) {
      selectedIndex = remaining.findIndex((row) => !blockCsvImmediateExactRepeat(row, ordered));
    }
    if (selectedIndex < 0) {
      selectedIndex = remaining.findIndex((row) => !blockCsvFeatureRunExceeded(row, ordered, maxRun));
    }
    if (selectedIndex < 0) selectedIndex = 0;
    ordered.push(remaining.splice(selectedIndex, 1)[0]);
  }
  return ordered;
}

function distributeBlockCsvRowFamily(rowsForRowFamily, blockCount, seed, rowKey, maxRun) {
  const groups = new Map();
  for (const row of rowsForRowFamily || []) {
    const groupKey = blockCsvAssignmentKey(row);
    if (!groups.has(groupKey)) groups.set(groupKey, []);
    groups.get(groupKey).push(row);
  }
  const rowBlocks = Array.from({ length: blockCount }, () => []);
  const featureCounts = Array.from({ length: blockCount }, () => ({}));
  for (const groupKey of Array.from(groups.keys()).sort()) {
    const rows = [...(groups.get(groupKey) || [])].sort(
      (left, right) => blockCsvSortableTrialIndex(left) - blockCsvSortableTrialIndex(right)
    );
    const offset = stableHashInt(`${seed}|${rowKey}|${groupKey}`) % blockCount;
    const rotation = Array.from({ length: blockCount }, (_unused, index) => (offset + index) % blockCount);
    const rotationRank = new Map(rotation.map((blockIndex, rank) => [blockIndex, rank]));
    for (const row of rows) {
      const featureKeys = blockCsvFeatureKeys(row);
      let bestBlock = rotation[0];
      let bestScore = null;
      for (const candidate of rotation) {
        const featureLoad = featureKeys.reduce(
          (total, key) => total + Number(featureCounts[candidate][key] || 0),
          0
        );
        const score = [
          rowBlocks[candidate].length,
          featureLoad,
          rotationRank.get(candidate) || 0,
        ];
        if (!bestScore || compareTuples(score, bestScore) < 0) {
          bestScore = score;
          bestBlock = candidate;
        }
      }
      rowBlocks[bestBlock].push(row);
      for (const key of featureKeys) {
        featureCounts[bestBlock][key] = Number(featureCounts[bestBlock][key] || 0) + 1;
      }
    }
  }
  return rowBlocks.map((rows, index) => seededGellermannOrderRows(
    rows,
    `${seed}|${rowKey}|block:${index + 1}`,
    maxRun
  ));
}

function seededGellermannBlockRows(poolRows, blockCount, seed, maxRun = BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE) {
  const rowOrder = blockCsvRowOrder(poolRows || []);
  if (!rowOrder.length) {
    return {
      blocks: Array.from({ length: blockCount }, () => []),
      rowOrder,
    };
  }
  const rowsByRowKey = new Map(rowOrder.map((rowInfo) => [rowInfo.row_key, []]));
  for (const row of poolRows || []) {
    const rowKey = blockCsvRowKey(row);
    if (!rowsByRowKey.has(rowKey)) rowsByRowKey.set(rowKey, []);
    rowsByRowKey.get(rowKey).push(row);
  }
  const distributedByRow = new Map();
  for (const rowInfo of rowOrder) {
    distributedByRow.set(rowInfo.row_key, distributeBlockCsvRowFamily(
      rowsByRowKey.get(rowInfo.row_key) || [],
      blockCount,
      seed,
      rowInfo.row_key,
      maxRun
    ));
  }
  const blocks = Array.from({ length: blockCount }, () => []);
  for (let blockIndex = 0; blockIndex < blockCount; blockIndex += 1) {
    const rowQueues = new Map(rowOrder.map((rowInfo) => [
      rowInfo.row_key,
      [...(distributedByRow.get(rowInfo.row_key)?.[blockIndex] || [])],
    ]));
    while (Array.from(rowQueues.values()).some((queue) => queue.length)) {
      for (const rowInfo of rowOrder) {
        const queue = rowQueues.get(rowInfo.row_key) || [];
        if (queue.length) blocks[blockIndex].push(queue.shift());
      }
    }
  }
  return { blocks, rowOrder };
}

function staticBlockPreviewRow(row, blockIndex, blockTrialIndex, soaMin, soaMax) {
  const family = trialPoolFamilyKey(row.family);
  const soaValue = Number(row.soa_ms || 0);
  return {
    block_index: blockIndex,
    block_label: `Block ${String(blockIndex).padStart(2, "0")}`,
    block_trial_index: blockTrialIndex,
    trial_pool_index: row.trial_pool_index,
    family,
    family_label: familyDisplayName(family),
    family_color_hex: TRIAL_FAMILY_COLORS[family] || "#5e695f",
    row_label: row.row_label || "",
    row_color_hex: staticRowColor(row.row_label || row.folder_key || ""),
    folder_key: row.folder_key || "",
    folder_name: row.folder_name || "",
    noise_type: row.noise_type || "",
    noise_color_hex: row.noise_type ? sourceColor(row.noise_type) : "#d8dde2",
    soa_ms: row.soa_ms || "",
    soa_color_hex: soaValue > 0 ? staticSoaColor(soaValue, soaMin, soaMax) : "#d8dde2",
    baseline_mode: row.baseline_mode || "",
    sequence_variant_key: row.sequence_variant_key || "",
    sequence_labels: row.sequence_labels || "",
    source_file_name: row.source_file_name || "",
    trial_file_path: row.trial_file_path || "",
    source_sha256: row.source_sha256 || "",
    duration_ms: row.duration_ms || 0,
    duration_s: row.duration_s || "",
    looming_segment_onset_s: row.looming_segment_onset_s || "",
    tactile_onset_s: row.tactile_onset_s || "",
    repetition_index: row.repetition_index || "",
    configured_repetitions: row.configured_repetitions || "",
    fractional_extra: row.fractional_extra || "",
    channels: row.channels || "",
    tactile_channel: row.tactile_channel || "",
  };
}

function staticSoaColor(value, minimum, maximum) {
  const min = Number(minimum || 0);
  const max = Number(maximum || 0);
  const t = max > min ? Math.max(0, Math.min(1, (Number(value || 0) - min) / (max - min))) : 0.5;
  const start = [226, 237, 255];
  const end = [37, 86, 151];
  const rgb = start.map((component, index) => Math.round(component + (end[index] - component) * t));
  return `#${rgb.map((component) => component.toString(16).padStart(2, "0")).join("")}`;
}

function staticBlockCsvPreviewPayload(design, trialPoolBake, status) {
  const poolRows = trialPoolBake.preview_rows || [];
  if (!status.finished_profile || !poolRows.length) return {};
  const blockCount = Math.max(1, Math.round(Number(design.protocol?.blocks || 1)));
  const seed = normalizedRandomizationSeed(design.protocol?.random_seed);
  const soaValues = poolRows.map((row) => Number(row.soa_ms || 0)).filter((value) => value > 0);
  const soaMin = soaValues.length ? Math.min(...soaValues) : 0;
  const soaMax = soaValues.length ? Math.max(...soaValues) : 0;
  const scheduled = seededGellermannBlockRows(
    poolRows,
    blockCount,
    seed,
    BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE
  );
  const blocks = scheduled.blocks.map((rows, index) => {
    const blockIndex = index + 1;
    const previewRows = rows.map((row, rowIndex) => staticBlockPreviewRow(row, blockIndex, rowIndex + 1, soaMin, soaMax));
    const durationMs = previewRows.reduce((total, row) => total + Number(row.duration_ms || 0), 0);
    return {
      block_index: blockIndex,
      block_label: `Block ${String(blockIndex).padStart(2, "0")}`,
      csv_path: "",
      csv_file_name: `static_block_${String(blockIndex).padStart(2, "0")}.csv`,
      trial_count: previewRows.length,
      duration_ms: durationMs,
      family_counts: staticCountBy(previewRows, "family"),
      row_label_counts: staticCountBy(previewRows, "row_label"),
      soa_counts: staticCountBy(previewRows, "soa_ms"),
      noise_type_counts: staticCountBy(previewRows, "noise_type"),
      preview_rows: previewRows,
      preview_row_count: previewRows.length,
    };
  });
  return {
    status: "static_preload",
    accepted: true,
    block_count: blockCount,
    csv_count: blocks.length,
    total_count: poolRows.length,
    total_trials: poolRows.length,
    randomization_seed: seed,
    randomization_strategy: BLOCK_RANDOMIZATION_STRATEGY,
    max_consecutive_same_feature: BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE,
    balancing_strategy: "row_order_preserving_minimum_divergence_by_family_soa_source_lineage_with_gellermann_constraints",
    row_sequence_strategy: "cycle_preserved_segment_row_order_within_each_block",
    row_order: scheduled.rowOrder,
    csv_columns: BLOCK_CSV_DOWNLOAD_COLUMNS,
    estimated_total_duration_ms: blocks.reduce((total, block) => total + Number(block.duration_ms || 0), 0),
    blocks,
  };
}

function normalizeStaticTemplateDesign(data, status) {
  const design = clone(data.design || {});
  design.study_profile_id = data.template_id || "";
  design.study_profile_title = data.title || "";
  design.study_profile_notes = data.notes || "";
  design.study_profile_reference_parameters = clone(data.reference_parameters || {});
  design.noises = Array.isArray(design.noises) ? design.noises : [];
  design.custom_looming_files = Array.isArray(design.custom_looming_files) ? design.custom_looming_files : [];
  design.prestimulus_files = Array.isArray(design.prestimulus_files) ? design.prestimulus_files : [];
  design.trajectory = design.trajectory || {};
  const rawProtocol = design.protocol || {};
  design.protocol = {
    repetitions_per_condition: 1,
    trial_pool_repetition_defaults: {},
    soa_values_ms: [],
    spatial_values_cm: [],
    pair_spatial_values_with_soas: false,
    auditory_motion_directions: ["looming"],
    tactile_sites: ["hand"],
    include_catch_trials: false,
    catch_trial_percentage: 10,
    catch_trials_exact: null,
    catch_crosses_sequence_variants: true,
    include_auditory_only_trials: false,
    auditory_only_trial_percentage: 0,
    auditory_only_trials_exact: null,
    auditory_only_crosses_sequence_variants: true,
    include_baseline_trials: true,
    baseline_strategy: "tactile_only",
    baseline_trials_exact: null,
    baseline_trial_percentage: 0,
    baseline_soa_values_ms: [],
    baseline_custom_trial_mode: "tactile_only",
    baseline_crosses_sequence_variants: true,
    respiratory_phases: ["Inhale", "Exhale"],
    blocks: 1,
    block_specs: [],
    distribute_trial_pool_across_blocks: false,
    trial_strips: [],
    trial_randomization_strategy: "no_immediate_repeats",
    block_order_randomization: "counterbalanced_rotation",
    max_consecutive_same_trial_type: 2,
    participants: 1,
    random_seed: 20250604,
    ...rawProtocol
  };
  if (!Object.prototype.hasOwnProperty.call(rawProtocol, "include_catch_trials")) {
    const catchExact = rawProtocol.catch_trials_exact;
    design.protocol.include_catch_trials = Boolean(
      Number(rawProtocol.catch_trial_percentage || 0) > 0
      || (catchExact !== null && catchExact !== undefined && catchExact !== 0 && catchExact !== "")
    );
  }
  if (!Object.prototype.hasOwnProperty.call(rawProtocol, "include_auditory_only_trials")) {
    const auditoryExact = rawProtocol.auditory_only_trials_exact;
    design.protocol.include_auditory_only_trials = Boolean(
      Number(rawProtocol.auditory_only_trial_percentage || 0) > 0
      || (auditoryExact !== null && auditoryExact !== undefined && auditoryExact !== 0 && auditoryExact !== "")
    );
  }
  hydrateStaticSourceAssets(design, status);
  return design;
}

function hydrateStaticSourceAssets(design, status) {
  const assetsByLabel = new Map();
  const consumed = new Set();
  const directionLabels = new Set();
  for (const asset of status.assets || []) {
    const key = normalizeSourceKey(asset.label);
    if (!key) continue;
    assetsByLabel.set(key, asset);
    const directionLabel = String(asset.direction_label || "").trim();
    if (directionLabel) directionLabels.add(directionLabel);
  }
  for (const noise of design.noises || []) {
    const key = normalizeSourceKey(noise.label);
    const asset = assetsByLabel.get(key);
    if (!asset) continue;
    consumed.add(key);
    noise.prebaked_path = noise.prebaked_path || asset.path;
    noise.noise_type = noise.noise_type || asset.noise_type || asset.tone_type || "pink";
    noise.motion_mode = noise.motion_mode || asset.motion_mode || "looming";
    noise.trajectory_snapshot = Object.keys(noise.trajectory_snapshot || {}).length
      ? noise.trajectory_snapshot
      : clone(asset.trajectory_snapshot || {});
  }
  for (const source of design.custom_looming_files || []) {
    const key = normalizeSourceKey(source.label);
    const asset = assetsByLabel.get(key);
    if (!asset) continue;
    consumed.add(key);
    source.path = source.path || asset.path;
    source.tone_type = source.tone_type || asset.tone_type || asset.noise_type || "custom_audio";
    source.motion_mode = source.motion_mode || asset.motion_mode || "looming";
    source.trajectory_snapshot = Object.keys(source.trajectory_snapshot || {}).length
      ? source.trajectory_snapshot
      : clone(asset.trajectory_snapshot || {});
  }
  for (const [key, asset] of assetsByLabel.entries()) {
    if (consumed.has(key)) continue;
    const label = String(asset.label || "").trim();
    const path = String(asset.path || "").trim();
    if (!label || !path) continue;
    design.custom_looming_files.push({
      label,
      path,
      target_duration_s: Number(asset.duration_s || design.trajectory?.total_duration_s || 4),
      render_mode: "preserve",
      tone_type: asset.noise_type || asset.tone_type || "custom_audio",
      gain: 1.0,
      motion_mode: asset.motion_mode || "looming",
      trajectory_snapshot: clone(asset.trajectory_snapshot || {}),
    });
  }
  expandStaticTrialStripSourceLabels(
    design,
    [...assetsByLabel.values()].map((asset) => String(asset.label || "").trim()).filter(Boolean)
  );
  if (directionLabels.size > 1) {
    design.protocol.auditory_motion_directions = ["source_trajectory"];
  }
}

function expandStaticTrialStripSourceLabels(design, availableSourceLabels) {
  const available = (availableSourceLabels || []).map((label) => String(label || "").trim()).filter(Boolean);
  if (!available.length) return;
  for (const strip of design.protocol?.trial_strips || []) {
    for (const element of strip.elements || []) {
      if (element.kind !== "looming_stimulus") continue;
      const labels = Array.isArray(element.source_labels) && element.source_labels.length
        ? element.source_labels
        : [element.source_label || element.label || ""];
      const expanded = [];
      for (const rawLabel of labels) {
        const label = String(rawLabel || "").trim();
        if (!label) continue;
        const matches = available.filter((candidate) => candidate === label || candidate.startsWith(`${label} - `));
        expanded.push(...(matches.length ? matches : [label]));
      }
      element.source_labels = [...new Set(expanded)];
      element.source_label = element.source_labels[0] || "";
    }
  }
}

function staticProjectPayload(design, status) {
  return {
    project_id: `static_profile_${design.study_profile_id || DEFAULT_STUDY_TEMPLATE_ID}`,
    project_kind: "profile",
    project_label: design.study_profile_title || design.name || "Static profile",
    source_template_id: design.study_profile_id || "",
    created_at: "",
    registry_root: "",
    project_dir: "",
    profile_dir: "",
    segment_folders: Object.fromEntries(Object.values(STEP_SEGMENT_FOLDERS).map((folder) => [folder, ""])),
    shared_tactile_path: "",
    preload_profile_manifest: status.profile_manifest || "",
    preload_profile_parameters_manifest: status.profile_parameters_manifest || ""
  };
}

function staticProjectSegments(status, trialFileBake = {}, trialPoolBake = {}, blockCsvPreview = {}) {
  const profileGate = Boolean(status.profile_checks_passed && status.segment_0_to_4_profile_checks_passed);
  const launchable = Boolean(status.finished_profile && status.segment_6_launchable);
  const segment = (folderName, ready, message, extra = {}) => ({
    folder_name: folderName,
    folder_path: "",
    status: ready ? "ready" : "static_only",
    message,
    last_validation_message: ready ? "" : message,
    manifest_exists: ready,
    ...extra
  });
  return {
    "0_profile": segment("0_profile", true, "Static profile metadata loaded from GitHub assets."),
    "1_core_audio_ingredients": segment(
      "1_core_audio_ingredients",
      Boolean(status.ready),
      status.ready ? "Static source assets are available online." : "Profile source assets require the local companion."
    ),
    "2_trial_sequence_designs": segment(
      "2_trial_sequence_designs",
      profileGate,
      profileGate ? "Trial sequence parameters loaded from the static profile." : "This profile has not passed the Segment 0-4 gate."
    ),
    "3_tactile_and_baseline_trials": segment(
      "3_tactile_and_baseline_trials",
      profileGate,
      profileGate ? "Baseline and tactile parameters loaded from the static profile." : "This profile has not passed the Segment 0-4 gate.",
      {
        audio_tactile_count: trialFileBake.audio_tactile_count || 0,
        baseline_count: trialFileBake.baseline_count || 0,
        catch_count: trialFileBake.catch_count || 0,
        auditory_only_count: trialFileBake.auditory_only_count || 0,
        total_count: trialFileBake.total_count || 0,
      }
    ),
    "4_trial_repetition_pool": segment(
      "4_trial_repetition_pool",
      profileGate,
      profileGate ? "Trial-pool parameters loaded from the static profile." : "This profile has not passed the Segment 0-4 gate.",
      {
        source_file_count: trialPoolBake.unique_file_count || trialFileBake.total_count || 0,
        trial_count: trialPoolBake.total_count || 0,
        total_count: trialPoolBake.total_count || 0,
        audio_tactile_count: trialPoolBake.audio_tactile_count || 0,
        baseline_count: trialPoolBake.baseline_count || 0,
        catch_count: trialPoolBake.catch_count || 0,
        auditory_only_count: trialPoolBake.auditory_only_count || 0,
      }
    ),
    "5_block_csv_preview": segment(
      "5_block_csv_preview",
      launchable,
      launchable ? "Block CSVs are materialized by the local companion when the profile is launched." : "This profile is not Segment 6 launchable yet.",
      {
        accepted: launchable,
        block_count: blockCsvPreview.block_count || 0,
        csv_count: blockCsvPreview.csv_count || blockCsvPreview.blocks?.length || 0,
        total_count: blockCsvPreview.total_count || trialPoolBake.total_count || 0,
      }
    ),
    "6_experiment_run_setup": segment(
      "6_experiment_run_setup",
      launchable,
      launchable ? "Start the local companion to materialize and open this finished profile." : "This profile is not Segment 6 launchable yet."
    )
  };
}

function staticTrajectoryControls(design) {
  const trajectory = design.trajectory || {};
  const start = staticTrajectoryPoint(trajectory, "start");
  const end = staticTrajectoryPoint(trajectory, "end");
  const movementDuration = Number(trajectory.propagation_speed_mps) > 0
    ? Number(trajectory.path_length_m || distance3d(start, end)) / Number(trajectory.propagation_speed_mps)
    : Number(trajectory.movement_duration_s || 3);
  return {
    start_distance_cm: Math.round(staticPointRadius(start) * 10000) / 100,
    end_distance_cm: Math.round(staticPointRadius(end) * 10000) / 100,
    start_rotation_deg: Math.round(staticPointRotation(start) * 10000) / 10000,
    end_rotation_deg: Math.round(staticPointRotation(end) * 10000) / 10000,
    start_height_cm: Math.round(Number(start.z_m || 0) * 10000) / 100,
    end_height_cm: Math.round(Number(end.z_m || 0) * 10000) / 100,
    movement_duration_s: Math.round((Number.isFinite(movementDuration) ? movementDuration : 3) * 10000) / 10000,
    start_hold_s: Number(trajectory.padding_pre_s ?? 0.5),
    end_hold_s: Number(trajectory.padding_post_s ?? 0.5)
  };
}

function staticTrajectoryPoint(trajectory, prefix) {
  const x = trajectory[`${prefix}_x_m`];
  const y = trajectory[`${prefix}_y_m`];
  const z = trajectory[`${prefix}_z_m`];
  if (Number.isFinite(Number(x)) && Number.isFinite(Number(y))) {
    return { x_m: Number(x), y_m: Number(y), z_m: Number(z || 0) };
  }
  const radius = Number(trajectory[`${prefix}_radius_m`] ?? (prefix === "start" ? trajectory.start_radius_m : trajectory.end_radius_m) ?? 0);
  const azimuth = Number(trajectory[prefix === "start" ? "azimuth_start_deg" : "azimuth_end_deg"] || 0);
  return pointFromDistanceRotation(radius * 100, azimuth);
}

function staticPointRadius(point) {
  return Math.sqrt(Number(point.x_m || 0) ** 2 + Number(point.y_m || 0) ** 2 + Number(point.z_m || 0) ** 2);
}

function staticPointRotation(point) {
  return normalizeRotationDeg(Math.atan2(Number(point.x_m || 0), Number(point.y_m || 0)) * 180 / Math.PI);
}

function staticViewerPayload(design) {
  const controls = staticTrajectoryControls(design);
  const start = pointFromDistanceRotation(controls.start_distance_cm, controls.start_rotation_deg);
  const end = pointFromDistanceRotation(controls.end_distance_cm, controls.end_rotation_deg);
  const source_trajectories = [];
  for (const noise of design.noises || []) {
    const row = sourceTrajectoryRow({
      label: noise.label || "Generated noise",
      sourceKind: "generated_noise",
      toneType: noise.noise_type || "pink",
      localPath: noise.prebaked_path || "",
      snapshot: noise.trajectory_snapshot || {}
    });
    if (row) source_trajectories.push(row);
  }
  for (const source of design.custom_looming_files || []) {
    const row = sourceTrajectoryRow({
      label: source.label || "Custom source",
      sourceKind: "imported_audio",
      toneType: source.tone_type || "custom_audio",
      localPath: source.path || "",
      snapshot: source.trajectory_snapshot || {}
    });
    if (row) source_trajectories.push(row);
  }
  return {
    preview_mode: "2d",
    radius_m: Math.max(0.1, controls.start_distance_cm / 100, controls.end_distance_cm / 100, maxSourceTrajectoryRadius(source_trajectories)),
    path_length_m: distance3d(start, end),
    movement_duration_s: controls.movement_duration_s,
    start,
    end,
    source_trajectories
  };
}

function staticRenderStatus(status) {
  return {
    render_dir: "",
    manifest_path: status.profile_manifest || "",
    manifest_exists: Boolean(status.profile_manifest),
    status: status.ready ? "static_preload" : status.status || "missing",
    render_engine: "static-github-preload",
    wav_count: status.assets?.length || 0,
    wavs: (status.assets || []).map((asset) => ({
      label: asset.label,
      path: asset.path,
      sha256: asset.sha256 || asset.expected_sha256 || "",
      duration_s: asset.duration_s,
      sample_rate: asset.sample_rate,
      channels: asset.channels,
      source_kind: asset.source_kind,
      noise_type: asset.noise_type,
      tone_type: asset.tone_type,
      trajectory_snapshot: asset.trajectory_snapshot || {}
    }))
  };
}

function staticRunSetupPreview(design, status) {
  const runSetup = design.study_profile_reference_parameters?.dashboard_run_setup || {};
  const experimentStructure = runSetup.experiment_structure || (design.study_profile_id === DEFAULT_STUDY_TEMPLATE_ID ? "pre_post" : "single");
  const participantCount = Number(design.protocol?.participants || 1);
  const blockCount = Number(design.protocol?.blocks || design.protocol?.block_specs?.length || 1);
  const partCount = experimentStructure === "pre_post" ? 2 : 1;
  const rows = [];
  for (let index = 1; index <= Math.min(participantCount, 12); index += 1) {
    const participant = `P${String(index).padStart(3, "0")}`;
    const order = Array.from({ length: blockCount }, (_item, blockIndex) => `Block ${blockIndex + 1}`).join(" -> ");
    rows.push({ participant, part: partCount === 2 ? "Part 1 / Part 2" : "Single", block_count: blockCount, block_order: order });
  }
  return {
    ready: Boolean(status.finished_profile),
    prepared: Boolean(status.finished_profile),
    experiment_structure: experimentStructure,
    participant_count: participantCount,
    parts_per_participant: partCount,
    total_block_runs: participantCount * partCount * blockCount,
    rows,
    seed: runSetup.seed || design.protocol?.random_seed || 0,
    instruction_profile: runSetup.instruction_profile || {},
    message: status.finished_profile
      ? "Static profile loaded; start the local companion to materialize and open the runner."
      : (status.message || "This static profile is not launchable yet.")
  };
}

function staticParticipantOrders(design) {
  const blockCount = Number(design.protocol?.blocks || design.protocol?.block_specs?.length || 1);
  const blocks = Array.from({ length: blockCount }, (_item, index) => `Block ${index + 1}`);
  const participants = Number(design.protocol?.participants || 1);
  return Array.from({ length: Math.min(participants, 80) }, (_item, index) => ({
    participant: `P${String(index + 1).padStart(3, "0")}`,
    block_order: blocks.join(" -> ")
  }));
}

function queryFlagEnabled(name) {
  const params = new URLSearchParams(window.location.search || "");
  if (!params.has(name)) return false;
  const value = String(params.get(name) || "1").trim().toLowerCase();
  return !["0", "false", "no", "off"].includes(value);
}

function staticPreviewAuditEnabled() {
  return queryFlagEnabled(STATIC_AUDIT_QUERY_PARAM);
}

function forceStaticPreviewEnabled() {
  return queryFlagEnabled(STATIC_FORCE_QUERY_PARAM);
}

function pendingInitialTemplateRequest(templates = []) {
  if (initialTemplateRequestHandled) return { id: "", error: "" };
  if (!/^[a-z0-9_]+$/i.test(INITIAL_TEMPLATE_REQUEST)) {
    return { id: "", error: "The requested Toolkit template ID is not valid." };
  }
  const available = templates.some((template) => template.template_id === INITIAL_TEMPLATE_REQUEST);
  if (!available) {
    return { id: "", error: "The requested Toolkit template is not available in this Designer." };
  }
  return { id: INITIAL_TEMPLATE_REQUEST, error: "" };
}

function auditControlSnapshot(id) {
  const control = $(id);
  return {
    exists: Boolean(control),
    disabled: control ? Boolean(control.disabled) : null,
    text: control ? String(control.textContent || control.value || "").trim() : "",
    title: control ? String(control.title || "") : ""
  };
}

function auditSourceSnapshot() {
  const source = [];
  for (const item of state?.design?.noises || []) {
    source.push({
      kind: "noise",
      label: item.label || "",
      noise_type: item.noise_type || item.tone_type || "",
      motion_mode: item.motion_mode || "",
      path: item.prebaked_path || "",
      has_trajectory_snapshot: Boolean(item.trajectory_snapshot && Object.keys(item.trajectory_snapshot).length)
    });
  }
  for (const item of state?.design?.custom_looming_files || []) {
    source.push({
      kind: "custom_looming",
      label: item.label || "",
      noise_type: item.tone_type || item.noise_type || "",
      motion_mode: item.motion_mode || "",
      path: item.path || "",
      has_trajectory_snapshot: Boolean(item.trajectory_snapshot && Object.keys(item.trajectory_snapshot).length)
    });
  }
  for (const item of state?.design?.prestimulus_files || []) {
    source.push({
      kind: "prestimulus",
      label: item.label || "",
      noise_type: item.tone_type || item.noise_type || "prestimulus",
      motion_mode: item.motion_mode || "stationary",
      path: item.path || "",
      has_trajectory_snapshot: false
    });
  }
  return source;
}

function auditTrialStripSnapshot(strip, index) {
  const variants = stripPreviewVariants(strip);
  return {
    index: index + 1,
    label: strip?.label || `Trial type ${index + 1}`,
    variant_count: variants.length,
    variants: variants.map((variant) => variant.map((choice) => ({
      kind: choice.kind || "",
      label: choice.label || "",
      value: choice.value ?? ""
    })))
  };
}

function auditPoolRows(rows = []) {
  return rows.map((row) => ({
    trial_pool_index: row.trial_pool_index || "",
    family: row.family || "",
    row_label: row.row_label || "",
    folder_name: row.folder_name || "",
    source_file_name: row.source_file_name || "",
    duration_ms: row.duration_ms || 0,
    repetition_index: row.repetition_index || "",
    configured_repetitions: row.configured_repetitions || "",
    fractional_extra: row.fractional_extra || 0,
    soa_ms: row.soa_ms || "",
    baseline_mode: row.baseline_mode || "",
    sequence_variant_key: row.sequence_variant_key || "",
    sequence_labels: row.sequence_labels || "",
    noise_type: row.noise_type || "",
    channels: row.channels || "",
    tactile_channel: row.tactile_channel || ""
  }));
}

function auditBlockRows(rows = []) {
  return rows.map((row) => ({
    block_index: row.block_index || "",
    block_trial_index: row.block_trial_index || "",
    trial_pool_index: row.trial_pool_index || "",
    family: row.family || "",
    row_label: row.row_label || "",
    folder_name: row.folder_name || "",
    source_file_name: row.source_file_name || "",
    duration_ms: row.duration_ms || 0,
    repetition_index: row.repetition_index || "",
    configured_repetitions: row.configured_repetitions || "",
    fractional_extra: row.fractional_extra || 0,
    soa_ms: row.soa_ms || "",
    baseline_mode: row.baseline_mode || "",
    sequence_variant_key: row.sequence_variant_key || "",
    sequence_labels: row.sequence_labels || "",
    noise_type: row.noise_type || "",
    channels: row.channels || "",
    tactile_channel: row.tactile_channel || ""
  }));
}

function auditBlockSnapshot(block) {
  return {
    block_index: block.block_index || "",
    block_label: block.block_label || "",
    trial_count: block.trial_count || 0,
    duration_ms: block.duration_ms || 0,
    family_counts: clone(block.family_counts || {}),
    row_label_counts: clone(block.row_label_counts || {}),
    soa_counts: clone(block.soa_counts || {}),
    noise_type_counts: clone(block.noise_type_counts || {}),
    preview_rows: auditBlockRows(block.preview_rows || [])
  };
}

function dashboardAuditSnapshot() {
  const setup = state?.run_sequence_setup || {};
  const protocol = state?.design?.protocol || {};
  const segments = {};
  for (const folder of Object.values(STEP_SEGMENT_FOLDERS)) {
    const segment = state?.project_segments?.[folder] || {};
    segments[folder] = {
      status: segment.status || "",
      accepted: Boolean(segment.accepted),
      manifest_exists: Boolean(segment.manifest_exists),
      message: segment.message || segment.last_validation_message || "",
      audio_tactile_count: segment.audio_tactile_count || 0,
      baseline_count: segment.baseline_count || 0,
      catch_count: segment.catch_count || 0,
      auditory_only_count: segment.auditory_only_count || 0,
      total_count: segment.total_count || segment.trial_count || 0,
      block_count: segment.block_count || 0,
      csv_count: segment.csv_count || 0
    };
  }
  return {
    schema: STATIC_AUDIT_SNAPSHOT_SCHEMA,
    loaded: Boolean(state),
    static_mode: Boolean(staticModeActive),
    edit_mode_active: Boolean(editModeActive),
    selected_template: state?.selected_template || "",
    template_count: state?.templates?.length || 0,
    templates: (state?.templates || []).map((template) => ({
      template_id: template.template_id || "",
      citation_label: template.citation_label || "",
      title: template.title || "",
      doi: template.doi || "",
      preload_asset_status: clone(template.preload_asset_status || {})
    })),
    profile: {
      name: state?.design?.name || "",
      study_profile_id: state?.design?.study_profile_id || "",
      title: state?.design?.study_profile_title || "",
      doi: selectedProfileChoice().kind === "bundled"
        ? (state?.templates || []).find((template) => template.template_id === state.selected_template)?.doi || ""
        : "",
      preload_inventory: clone(state?.preload_inventory || {}),
      custom_missing: clone(state?.custom_workflow?.missing || [])
    },
    controls: Object.fromEntries(STATIC_AUDIT_CONTROL_IDS.map((id) => [id, auditControlSnapshot(id)])),
    segments,
    sources: auditSourceSnapshot(),
    trajectory_controls: clone(state?.trajectory_controls || {}),
    viewer_payload: clone(state?.viewer_payload || {}),
    protocol: {
      participants: protocol.participants || 0,
      blocks: protocol.blocks || 0,
      soa_values_ms: clone(protocol.soa_values_ms || []),
      include_baseline_trials: Boolean(protocol.include_baseline_trials),
      baseline_strategy: protocol.baseline_strategy || "",
      baseline_soa_values_ms: clone(protocol.baseline_soa_values_ms || []),
      baseline_custom_trial_mode: protocol.baseline_custom_trial_mode || "",
      baseline_crosses_sequence_variants: protocol.baseline_crosses_sequence_variants !== false,
      include_catch_trials: Boolean(protocol.include_catch_trials),
      catch_crosses_sequence_variants: protocol.catch_crosses_sequence_variants !== false,
      include_auditory_only_trials: Boolean(protocol.include_auditory_only_trials),
      auditory_only_crosses_sequence_variants: protocol.auditory_only_crosses_sequence_variants !== false,
      distribute_trial_pool_across_blocks: Boolean(protocol.distribute_trial_pool_across_blocks),
      trial_pool_repetition_defaults: clone(protocol.trial_pool_repetition_defaults || {}),
      trial_strips: (protocol.trial_strips || []).map(auditTrialStripSnapshot)
    },
    trial_file_bake: {
      status: state?.trial_file_bake?.status || "",
      audio_tactile_count: state?.trial_file_bake?.audio_tactile_count || 0,
      baseline_count: state?.trial_file_bake?.baseline_count || 0,
      catch_count: state?.trial_file_bake?.catch_count || 0,
      auditory_only_count: state?.trial_file_bake?.auditory_only_count || 0,
      total_count: state?.trial_file_bake?.total_count || 0
    },
    trial_pool_bake: {
      status: state?.trial_pool_bake?.status || "",
      unique_file_count: state?.trial_pool_bake?.unique_file_count || 0,
      total_count: state?.trial_pool_bake?.total_count || state?.trial_pool_bake?.total_trials || 0,
      audio_tactile_count: state?.trial_pool_bake?.audio_tactile_count || 0,
      baseline_count: state?.trial_pool_bake?.baseline_count || 0,
      catch_count: state?.trial_pool_bake?.catch_count || 0,
      auditory_only_count: state?.trial_pool_bake?.auditory_only_count || 0,
      estimated_total_duration_ms: state?.trial_pool_bake?.estimated_total_duration_ms || 0,
      family_counts: clone(state?.trial_pool_bake?.family_counts || {}),
      preview_rows: auditPoolRows(state?.trial_pool_bake?.preview_rows || [])
    },
    block_csv_preview: {
      status: state?.block_csv_preview?.status || "",
      accepted: Boolean(state?.block_csv_preview?.accepted),
      block_count: state?.block_csv_preview?.block_count || 0,
      csv_count: state?.block_csv_preview?.csv_count || 0,
      total_count: state?.block_csv_preview?.total_count || 0,
      estimated_total_duration_ms: state?.block_csv_preview?.estimated_total_duration_ms || 0,
      blocks: (state?.block_csv_preview?.blocks || []).map(auditBlockSnapshot)
    },
    run_sequence_setup: {
      ready: Boolean(setup.ready),
      prepared: Boolean(setup.prepared),
      participant_count: setup.participant_count || 0,
      experiment_structure: setup.experiment_structure || "",
      parts_per_participant: setup.parts_per_participant || 0,
      total_block_runs: setup.total_block_runs || 0,
      rows: clone(setup.rows || []),
      message: setup.message || ""
    },
    disabled_summary: {
      edit_disabled: Boolean($("edit-mode-button")?.disabled),
      mutating_enabled: STATIC_AUDIT_CONTROL_IDS
        .filter((id) => id !== "edit-mode-button")
        .filter((id) => {
          const control = $(id);
          return control && !control.disabled;
        })
    }
  };
}

function exposeDashboardAuditHook() {
  if (!staticPreviewAuditEnabled()) return;
  window.PPSDashboardAudit = Object.freeze({
    snapshot: dashboardAuditSnapshot
  });
}

async function loadStaticState(fallbackError = null) {
  staticModeReason = fallbackError?.message || "";
  const templates = await loadStaticTemplates();
  const request = pendingInitialTemplateRequest(templates);
  if (request.error) initialTemplateRequestHandled = true;
  const selected = request.id || (state?.selected_template && state.selected_template !== CUSTOM_TEMPLATE_ID
    ? state.selected_template
    : DEFAULT_STUDY_TEMPLATE_ID);
  state = await staticStateForTemplate(selected);
  if (request.id) initialTemplateRequestHandled = true;
  staticModeActive = true;
  setConnectionStatus(false, "static profile");
  renderAll();
  updateViewer();
  showToast(
    request.error
      || (request.id
        ? "Opened the paper's Toolkit template in Experiment Designer."
        : "Loaded committed preload assets from GitHub. Start the local companion for local generation or runner launch.")
  );
  return true;
}

function mergeStaticProfileSegment(localSegment = {}, staticSegment = {}) {
  return {
    ...staticSegment,
    folder_path: localSegment.folder_path || staticSegment.folder_path || "",
    manifest_path: localSegment.manifest_path || staticSegment.manifest_path || "",
    folder_exists: localSegment.folder_exists ?? staticSegment.folder_exists,
    manifest_exists: localSegment.manifest_exists || staticSegment.manifest_exists,
    static_profile_preview: true,
    last_validation_message: "Committed profile preview; local companion will materialize files for runner launch."
  };
}

async function applyStaticProfileInspectionPreview() {
  if (staticModeActive || !state || !isProfileReadonlyMode()) return;
  const profileId = state.selected_template || state.design?.study_profile_id || "";
  if (!profileId || profileId === CUSTOM_TEMPLATE_ID || !state.preload_inventory?.finished_profile) return;
  const staticPreview = await staticStateForTemplate(profileId);
  const localSegments = state.project_segments || {};
  const staticSegments = staticPreview.project_segments || {};
  state = {
    ...state,
    static_profile_preview: true,
    trial_sequence_bake: staticPreview.trial_sequence_bake || state.trial_sequence_bake,
    trial_file_bake: staticPreview.trial_file_bake,
    trial_pool_bake: staticPreview.trial_pool_bake,
    block_csv_preview: staticPreview.block_csv_preview,
    project_segments: {
      ...localSegments,
      "2_trial_sequence_designs": mergeStaticProfileSegment(localSegments["2_trial_sequence_designs"], staticSegments["2_trial_sequence_designs"]),
      "3_tactile_and_baseline_trials": mergeStaticProfileSegment(localSegments["3_tactile_and_baseline_trials"], staticSegments["3_tactile_and_baseline_trials"]),
      "4_trial_repetition_pool": mergeStaticProfileSegment(localSegments["4_trial_repetition_pool"], staticSegments["4_trial_repetition_pool"]),
      "5_block_csv_preview": mergeStaticProfileSegment(localSegments["5_block_csv_preview"], staticSegments["5_block_csv_preview"]),
    }
  };
}

async function loadState(options = {}) {
  const resetMode = options.resetEditMode === true;
  if (forceStaticPreviewEnabled()) {
    if (resetMode) resetEditMode();
    await loadStaticState(new Error("Forced static preview audit mode."));
    return;
  }
  try {
    state = await designerApi.state({ skipStaticGuard: true });
    staticModeActive = false;
    staticModeReason = "";
    const request = pendingInitialTemplateRequest(state.templates || []);
    if (request.error) {
      initialTemplateRequestHandled = true;
    } else if (request.id) {
      const alreadySelected = state.selected_template === request.id && state.design?.study_profile_id === request.id;
      if (!alreadySelected) {
        state = await designerApi.loadTemplate(request.id);
      }
      initialTemplateRequestHandled = true;
    }
    await applyStaticProfileInspectionPreview();
    if (resetMode) resetEditMode();
    renderAll();
    updateViewer();
    if (request.error) showToast(request.error);
    if (request.id) showToast("Opened the paper's Toolkit template in Experiment Designer.");
  } catch (error) {
    try {
      if (resetMode) resetEditMode();
      await loadStaticState(error);
    } catch (staticError) {
      console.error(staticError);
      reportError(error);
    }
  }
}

function renderAll() {
  if (!state) return;
  document.body.classList.toggle("static-dashboard-mode", staticModeActive);
  document.body.classList.toggle("dashboard-edit-mode", editModeActive);
  document.body.classList.toggle("dashboard-view-mode", !editModeActive);
  renderEditModePanel();
  renderPageTabs();
  renderStudy();
  renderStimulus();
  renderTrials();
  renderBaseline();
  renderBlockCsvPreview();
  renderRun();
  renderPreviewTables();
  renderSegmentRegistryOutputs();
  renderWorkflow();
  renderCapabilityLocks();
  upgradeBoundedSelects();
}

function renderCapabilityLocks() {
  if (!staticModeActive) return;
  const unavailable = [
    "start-distance", "end-distance", "start-rotation", "end-rotation",
    "movement-duration", "start-hold", "end-hold", "generated-noise-select",
    "bake-stimulus", "import-audio-spatialize", "regenerate-run-sequence",
    "export-output-folder", "prepare-experiment",
  ];
  for (const id of unavailable) {
    const control = $(id);
    if (!control) continue;
    control.disabled = true;
    control.title = STATIC_COMPANION_REQUIRED_MESSAGE;
  }
}

function normalizePageRoute(value) {
  const token = String(value || "")
    .trim()
    .replace(/^#/, "")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
  return PAGE_ROUTE_ALIASES[token] || "";
}

function topLevelLocation() {
  try {
    if (window.parent && window.parent !== window && window.parent.location.origin === window.location.origin) {
      return window.parent.location;
    }
  } catch (_err) {
    // Cross-origin parents cannot be inspected; direct dashboard routing remains local.
  }
  return window.location;
}

function topLevelHistory() {
  try {
    if (window.parent && window.parent !== window && window.parent.location.origin === window.location.origin) {
      return window.parent.history;
    }
  } catch (_err) {
    // Keep using the iframe/window history when parent access is not available.
  }
  return window.history;
}

function topLevelHash() {
  return topLevelLocation().hash || window.location.hash || "";
}

function pageFromPath(pathname) {
  const parts = String(pathname || "").replace(/\/+$/g, "").split("/").filter(Boolean);
  const last = parts[parts.length - 1] || "";
  if (last.toLowerCase() === "index.html") {
    return normalizePageRoute(parts[parts.length - 2] || "");
  }
  return normalizePageRoute(last);
}

function pageFromHash(hashValue = topLevelHash()) {
  const hash = String(hashValue || "").replace(/^#/, "");
  const pageHash = normalizePageRoute(hash);
  if (pageHash) return pageHash;
  const target = hash ? $(hash) : null;
  const panel = target?.closest("[data-page-panel]");
  const page = panel?.dataset.pagePanel || "";
  return PAGE_TABS.includes(page) ? page : "";
}

function pageFromLocation() {
  const queryPage = normalizePageRoute(new URLSearchParams(window.location.search).get("page"));
  if (queryPage) return queryPage;
  const routePage = pageFromPath(topLevelLocation().pathname);
  if (routePage) return routePage;
  return pageFromHash();
}

function publicRouteNavigationAvailable() {
  const location = topLevelLocation();
  if (!/^https?:$/i.test(location.protocol)) return false;
  return window.parent && window.parent !== window && location.origin === window.location.origin;
}

function publicRouteBasePath() {
  let path = String(topLevelLocation().pathname || "/")
    .replace(/\/index\.html$/i, "/")
    .replace(/\/(documentation|download|downloads)\/?$/i, "/");
  if (!path.endsWith("/")) path += "/";
  return path;
}

function pageRouteUrl(page, sectionId = "") {
  const nextPage = PAGE_TABS.includes(page) ? page : "toolkit";
  const cleanSection = String(sectionId || "").replace(/^#/, "");
  if (!publicRouteNavigationAvailable()) {
    const hash = cleanSection || (nextPage === "toolkit" ? "" : nextPage);
    return `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ""}`;
  }
  const base = publicRouteBasePath();
  const segment = PAGE_ROUTE_SEGMENTS[nextPage] || "";
  const routePath = segment ? `${base}${segment}` : base;
  return `${routePath}${cleanSection ? `#${cleanSection}` : ""}`;
}

function replaceRouteForPage(page, sectionId = "") {
  const url = pageRouteUrl(page, sectionId);
  try {
    topLevelHistory().replaceState(null, "", url);
  } catch (_err) {
    window.history.replaceState(null, "", url);
  }
}

function setActivePage(page, options = {}) {
  const nextPage = PAGE_TABS.includes(page) ? page : "toolkit";
  activePage = nextPage;
  if (nextPage === "documentation" && !window.HARDWARE_PIXEL_ART) {
    import("./hardware_pixel_art.js").then(renderHardwarePixelArt).catch(console.error);
  }
  document.body.dataset.activePage = nextPage;
  document.body.classList.toggle("info-page-active", nextPage !== "toolkit");
  for (const button of document.querySelectorAll("[data-page-tab]")) {
    const active = button.dataset.pageTab === nextPage;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
    button.tabIndex = active ? 0 : -1;
  }
  for (const panel of document.querySelectorAll("[data-page-panel]")) {
    panel.hidden = panel.dataset.pagePanel !== nextPage;
    panel.classList.toggle("active", panel.dataset.pagePanel === nextPage);
  }
  if (nextPage === "documentation") refreshDocumentationTypography();
  syncRailForPage(nextPage);
  if (!options.preserveMobileDisclosures) closeMobileRailDisclosures();
  if (options.updateHash) {
    replaceRouteForPage(nextPage);
  }
  if (options.scrollTop) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  updateActiveNav();
  if (options.scrollTop && nextPage !== "toolkit") {
    const firstPageLink = document.querySelector(`[data-page-section-page="${cssEscape(nextPage)}"]`);
    setActivePageSection(firstPageLink?.dataset.pageSectionLink || "");
  }
}

function syncRailForPage(page) {
  for (const group of document.querySelectorAll("[data-rail-page]")) {
    const active = group.dataset.railPage === page;
    group.hidden = !active;
    group.classList.toggle("active", active);
  }
  syncMobileRailSummary();
}

function closeMobileRailDisclosures() {
  const rail = document.querySelector(".rail");
  if (!rail) return;
  rail.classList.remove("mobile-sections-open", "mobile-companion-open");
  $("mobile-rail-nav-toggle")?.setAttribute("aria-expanded", "false");
  $("mobile-companion-toggle")?.setAttribute("aria-expanded", "false");
}

function mobileSectionsDisclosureOpen() {
  return window.matchMedia("(max-width: 760px)").matches
    && document.querySelector(".rail")?.classList.contains("mobile-sections-open");
}

function focusSectionNavigationTarget(target) {
  if (!target) return;
  const originalTabIndex = target.getAttribute("tabindex");
  if (originalTabIndex === null) target.tabIndex = -1;
  target.focus({ preventScroll: true });
  if (originalTabIndex !== null) return;
  if (document.activeElement !== target) {
    target.removeAttribute("tabindex");
    return;
  }
  target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
}

function toggleMobileRailDisclosure(kind) {
  const rail = document.querySelector(".rail");
  if (!rail) return;
  const sections = kind === "sections";
  const className = sections ? "mobile-sections-open" : "mobile-companion-open";
  const otherClass = sections ? "mobile-companion-open" : "mobile-sections-open";
  const toggle = $(sections ? "mobile-rail-nav-toggle" : "mobile-companion-toggle");
  const otherToggle = $(sections ? "mobile-companion-toggle" : "mobile-rail-nav-toggle");
  const expanded = !rail.classList.contains(className);
  rail.classList.toggle(className, expanded);
  rail.classList.remove(otherClass);
  toggle?.setAttribute("aria-expanded", String(expanded));
  otherToggle?.setAttribute("aria-expanded", "false");
}

function syncMobileRailSummary() {
  const current = $("mobile-rail-nav-current");
  if (!current) return;
  const selector = activePage === "toolkit"
    ? '[data-step-link].active'
    : `[data-page-section-page="${cssEscape(activePage)}"].active`;
  const fallback = document.querySelector(`[data-rail-page="${cssEscape(activePage)}"] a`);
  const link = document.querySelector(selector) || fallback;
  current.textContent = link?.querySelector("span")?.textContent?.trim()
    || (activePage === "toolkit" ? "Experiment workflow" : humanize(activePage));
}

function setActivePageSection(sectionId) {
  if (!sectionId) return;
  for (const link of document.querySelectorAll("[data-page-section-link]")) {
    link.classList.toggle(
      "active",
      link.dataset.pageSectionPage === activePage && link.dataset.pageSectionLink === sectionId
    );
  }
}

function scrollToHashTarget() {
  const hash = String(topLevelHash() || "").replace(/^#/, "");
  if (!hash || PAGE_TABS.includes(hash)) return;
  const target = $(hash);
  if (!target) return;
  setActivePageSection(hash);
  window.requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => setActivePageSection(hash), 350);
  });
}

function renderPageTabs() {
  setActivePage(activePage);
}

function initializePageTabs() {
  setActivePage(pageFromLocation() || "toolkit");
  scrollToHashTarget();
}

function initializeLazySurfaces() {
  const frame = $("trajectory-frame");
  if (!frame?.dataset.lazySrc) return;
  const load = () => {
    if (!frame.dataset.lazySrc) return;
    frame.src = frame.dataset.lazySrc;
    delete frame.dataset.lazySrc;
  };
  if (!("IntersectionObserver" in window)) {
    load();
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    load();
  }, { rootMargin: "320px" });
  observer.observe(frame);
}

function initializePublicationNetworkSurface() {
  const root = document.querySelector("[data-publication-network-root]");
  if (!root) return;
  root.dataset.publicationNetworkState = "idle";
  const load = () => {
    if (publicationNetworkInitPromise) return publicationNetworkInitPromise;
    root.dataset.publicationNetworkState = "loading";
    publicationNetworkInitPromise = import("./publication_network.js")
      .then(({ initializePublicationNetwork }) => initializePublicationNetwork(root))
      .catch((error) => {
        root.dataset.publicationNetworkState = "error";
        const loading = $("publication-network-loading");
        if (loading) {
          loading.hidden = false;
          loading.classList.add("error");
          const message = loading.querySelector("span:last-child");
          if (message) message.textContent = "The publication network could not be loaded.";
        }
        console.error(error);
        throw error;
      });
    return publicationNetworkInitPromise;
  };
  if (!("IntersectionObserver" in window)) {
    load();
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    load();
  }, { rootMargin: "480px" });
  observer.observe(root);
}

function renderStudy() {
  const select = $("template-select");
  select.innerHTML = "";
  const activeProjectId = isCustomMode() ? String(state.project?.project_id || "") : "";
  const bundledGroup = document.createElement("optgroup");
  bundledGroup.label = "Built-in Study Templates";
  for (const template of state.templates) {
    const option = document.createElement("option");
    option.value = template.template_id;
    option.textContent = template.citation_label;
    option.selected = !activeProjectId && template.template_id === state.selected_template;
    bundledGroup.appendChild(option);
  }
  select.appendChild(bundledGroup);
  const customEntries = customProfileEntries();
  if (customEntries.length) {
    const customGroup = document.createElement("optgroup");
    customGroup.label = "My Custom Designs";
    for (const entry of customEntries) {
      const option = document.createElement("option");
      option.value = customProfileSelectValue(entry.profile_id);
      const status = entry.is_finalized ? "Finalized" : "Draft";
      option.textContent = `${entry.display_name || entry.profile_id} — ${status}`;
      option.selected = entry.profile_id === activeProjectId;
      customGroup.appendChild(option);
    }
    select.appendChild(customGroup);
  }
  if (activeProjectId && [...select.options].some((option) => option.value === customProfileSelectValue(activeProjectId))) {
    select.value = customProfileSelectValue(activeProjectId);
  }
  renderProfileSummary();
}

function profileCatalogEntries() {
  return Array.isArray(state.profile_catalog?.entries) ? state.profile_catalog.entries : [];
}

function customProfileSelectValue(profileId) {
  return `custom-profile:${profileId}`;
}

function customProfileEntries() {
  const entries = new Map();
  for (const project of state.custom_projects || []) {
    entries.set(project.project_id, {
      profile_id: project.project_id,
      display_name: project.project_label || project.project_id,
      created_at: project.created_at || "",
      is_finalized: false,
    });
  }
  for (const entry of profileCatalogEntries().filter((item) => item.kind === "custom")) {
    entries.set(entry.profile_id, {
      ...(entries.get(entry.profile_id) || {}),
      ...entry,
      is_finalized: Boolean(entry.profile_ready || entry.profile_status === "finalized"),
    });
  }
  if (isCustomMode()) {
    const profileId = String(state.project?.project_id || "browser_draft");
    entries.set(profileId, {
      ...(entries.get(profileId) || {}),
      profile_id: profileId,
      display_name: state.design?.name || state.project?.project_label || profileId,
      is_finalized: Boolean(state.custom_workflow?.is_finalized),
    });
  }
  return [...entries.values()].sort((left, right) =>
    String(left.display_name || left.profile_id).localeCompare(String(right.display_name || right.profile_id))
  );
}

function selectedProfileChoice(value = $("template-select")?.value || "") {
  const raw = String(value || "");
  if (raw.startsWith("custom-profile:")) {
    return { kind: "custom", id: raw.slice("custom-profile:".length) };
  }
  return { kind: raw === CUSTOM_TEMPLATE_ID ? "manual" : "bundled", id: raw };
}

function renderProfileSummary() {
  const choice = selectedProfileChoice();
  const selectedId = choice.kind === "bundled" ? choice.id : "";
  const current = state.templates.find((item) => item.template_id === selectedId);
  const custom = isCustomMode();
  const params = state.design?.study_profile_reference_parameters || {};
  const profileId = custom
    ? String(state.project?.project_id || choice.id || "browser_draft")
    : String(state.design?.study_profile_id || selectedId || "");
  const sourceId = custom ? String(
    params.customized_from_profile_id
    || params.customized_from_project_id
    || state.project?.source_template_id
    || state.project?.source_profile_id
    || ""
  ) : "";
  const citation = $("profile-citation");
  if (citation) citation.textContent = custom ? "" : String(current?.citation || current?.citation_label || "");
  const href = current?.doi ? doiUrl(current.doi) : "";
  const summary = $("profile-summary");
  summary.hidden = !href;
  summary.innerHTML = href
    ? `DOI: <a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(current.doi)}</a>`
    : "";
  const idField = $("profile-inspection-id");
  if (idField) idField.textContent = profileId || "—";
  const sourceDetail = $("profile-source-detail");
  const sourceField = $("profile-inspection-source");
  if (sourceDetail) sourceDetail.hidden = !sourceId;
  if (sourceField) sourceField.textContent = sourceId || "—";
}

function renderDataAcquisitionBridge() {
  const context = state?.data_acquisition || {};
  const active = Boolean(context.active && context.root && context.diary_path);
  const status = $("data-acquisition-status");
  const exportButton = $("export-data-acquisition-folder");
  const openButton = $("open-data-acquisition-folder");
  if (status) {
    status.textContent = active ? "linked" : "not linked";
    status.className = `status-label ${active ? "ready" : "required"}`;
  }
  if (exportButton) {
    exportButton.disabled = staticModeActive;
    exportButton.textContent = active ? "Update Data Acquisition Folder" : "Export Settings to Data Acquisition Folder";
  }
  if (openButton) {
    openButton.disabled = !active;
    openButton.dataset.folderPath = active ? context.root : "";
  }
}

function renderStimulus() {
  const inventory = ingredientInventoryEntries();
  if (selectedIngredientLabel && !inventory.some((entry) => entry.label === selectedIngredientLabel)) {
    selectedIngredientLabel = "";
    ingredientEditorMode = "idle";
    ingredientEditorDirty = false;
    pendingBakeRecipe = null;
  }
  if (!selectedIngredientLabel && !pendingBakeRecipe && inventory.length) {
    hydrateIngredientEditor(inventory[0], { preserveMode: false });
  } else if (!pendingBakeRecipe || !ingredientEditorDirty) {
    const selected = ingredientEntryForLabel(selectedIngredientLabel);
    if (selected) hydrateIngredientEditor(selected, { preserveMode: true });
    else applyTrajectoryControls(state.trajectory_controls || {});
  }
  syncPreviewModeControls($("preview-mode").value || "2d");
  renderLoudnessControls();
  renderGeneratedNoiseSelect();
  renderNoiseTable();
  renderAudioTable();
  renderBakePanel();
  refreshAssemblyTargetOptions();
  renderSourceCounts();
}

function ingredientInventoryEntries() {
  const entries = [];
  for (const source of state?.design?.noises || []) {
    entries.push({ label: sourceCardLabelFromSpec(source, "Generated noise"), kind: "generated_noise", source });
  }
  for (const source of state?.design?.custom_looming_files || []) {
    entries.push({
      label: sourceCardLabelFromSpec(source, "Custom looming tone"),
      kind: "imported_audio",
      source,
    });
  }
  for (const source of state?.design?.prestimulus_files || []) {
    entries.push({ label: sourceCardLabelFromSpec(source, "Custom audio clip"), kind: "fixed_audio", source });
  }
  return entries;
}

function ingredientEntryForLabel(label) {
  const key = normalizeSourceKey(label);
  return ingredientInventoryEntries().find((entry) => normalizeSourceKey(entry.label) === key) || null;
}

function applyTrajectoryControls(controls = {}) {
  $("start-distance").value = controls.start_distance_cm ?? 110;
  $("end-distance").value = controls.end_distance_cm ?? 10;
  $("start-rotation").value = controls.start_rotation_deg ?? 0;
  $("end-rotation").value = controls.end_rotation_deg ?? 0;
  $("movement-duration").value = controls.movement_duration_s ?? 3;
  $("start-hold").value = controls.start_hold_s ?? 0.5;
  $("end-hold").value = controls.end_hold_s ?? 0.5;
}

function recipeForIngredientEntry(entry) {
  if (!entry) return null;
  const source = clone(entry.source || {});
  if (entry.kind === "generated_noise") {
    return {
      kind: "generated_noise",
      action: "remake",
      original_label: entry.label,
      label: entry.label,
      noise_type: source.noise_type || "pink",
      gain: Number(source.gain || 1),
      source_profile: source.source_profile || GOLD_STANDARD_SOURCE_PROFILE,
      source_profile_parameters: source.source_profile_parameters || {},
      loudness_policy: collectLoudnessPolicy(),
    };
  }
  return {
    kind: entry.kind === "fixed_audio" ? "fixed_audio" : "imported_audio",
    action: "remake",
    original_label: entry.label,
    render_mode: entry.kind === "fixed_audio" ? "preserve" : "spatialize",
    label: entry.label,
    audio: source,
    gain: Number(source.gain || 1),
    display_color_hex: displayColorForSource(source),
    loudness_policy: collectLoudnessPolicy(),
  };
}

function hydrateIngredientEditor(entry, { preserveMode = false } = {}) {
  if (!entry) return;
  selectedIngredientLabel = entry.label;
  ingredientEditorMode = preserveMode && ingredientEditorMode === "remake" ? "remake" : (editModeActive ? "remake" : "inspect");
  ingredientEditorDirty = false;
  pendingBakeRecipe = recipeForIngredientEntry(entry);
  const snapshot = entry.source?.trajectory_snapshot || {};
  applyTrajectoryControls(entry.kind === "fixed_audio" ? (state.trajectory_controls || {}) : { ...(state.trajectory_controls || {}), ...snapshot });
  const color = displayColorForSource(entry.source, entry.kind === "fixed_audio" ? "prestimulus" : "custom_audio");
  pendingDisplayColorHex = color;
  $("bake-label").value = entry.label;
  if ($("fixed-audio-duration")) {
    $("fixed-audio-duration").value = Number(entry.source?.target_duration_s || entry.source?.duration_s || 4);
  }
}

function confirmDiscardIngredientDraft(actionLabel = "continue") {
  if (!ingredientEditorDirty) return true;
  return window.confirm(`Discard the uncommitted Segment 1 ingredient draft and ${actionLabel}?`);
}

function selectIngredient(label, { force = false } = {}) {
  const entry = ingredientEntryForLabel(label);
  if (!entry) return false;
  if (!force && !confirmDiscardIngredientDraft(`inspect “${entry.label}”`)) return false;
  hydrateIngredientEditor(entry);
  renderNoiseTable();
  renderAudioTable();
  renderBakePanel();
  updateViewer();
  return true;
}

function dbToLinear(db) {
  return Math.pow(10, Number(db || 0) / 20);
}

function normalizeLoudnessPolicy(policy = {}, controls = null) {
  const resolvedControls = controls || {
    start_hold_s: Number(state?.trajectory_controls?.start_hold_s ?? 0.5),
    movement_duration_s: Number(state?.trajectory_controls?.movement_duration_s ?? 3),
    end_hold_s: Number(state?.trajectory_controls?.end_hold_s ?? 0.5)
  };
  const merged = {
    ...clone(DEFAULT_LOUDNESS_POLICY),
    ...(policy && typeof policy === "object" ? clone(policy) : {})
  };
  merged.hardware = {
    ...clone(DEFAULT_LOUDNESS_POLICY.hardware),
    ...(policy?.hardware && typeof policy.hardware === "object" ? clone(policy.hardware) : {})
  };
  merged.schema = "pps-loudness-policy.v1";
  merged.mode = ["estimated_spl", "measured_spl", "digital_dbfs"].includes(String(merged.mode || "")) ? merged.mode : "estimated_spl";
  merged.calibration_mode = ["estimate_allowed", "measured_required", "digital_only"].includes(String(merged.calibration_mode || "")) ? merged.calibration_mode : "estimate_allowed";
  merged.calibration_status = ["estimated_not_measured", "measured", "digital_only"].includes(String(merged.calibration_status || "")) ? merged.calibration_status : "estimated_not_measured";
  merged.start_spl_db = clampNumber(Number(merged.start_spl_db), 30, 95, DEFAULT_LOUDNESS_POLICY.start_spl_db);
  merged.end_spl_db = clampNumber(Number(merged.end_spl_db), 30, 95, DEFAULT_LOUDNESS_POLICY.end_spl_db);
  if (merged.end_spl_db < merged.start_spl_db) {
    [merged.start_spl_db, merged.end_spl_db] = [merged.end_spl_db, merged.start_spl_db];
  }
  merged.instruction_offset_db = clampNumber(Number(merged.instruction_offset_db), -30, 0, DEFAULT_LOUDNESS_POLICY.instruction_offset_db);
  merged.estimated_full_scale_spl_db = clampNumber(Number(merged.estimated_full_scale_spl_db), 80, 130, DEFAULT_LOUDNESS_POLICY.estimated_full_scale_spl_db);
  merged.audio_peak_ceiling_dbfs = clampNumber(Number(merged.audio_peak_ceiling_dbfs), -12, 0, DEFAULT_LOUDNESS_POLICY.audio_peak_ceiling_dbfs);
  merged.movement_ramp_shape = "linear_db";
  merged.hold_level_policy = "constant_start_and_endpoint";
  merged.calibration_window = "final_500ms_active_movement_excluding_padding";
  merged.calibration_window_s = clampNumber(Number(merged.calibration_window_s), 0.05, 2, DEFAULT_LOUDNESS_POLICY.calibration_window_s);
  merged.pre_hold_s = Math.max(0, Number(resolvedControls.start_hold_s || 0));
  merged.movement_duration_s = Math.max(0.1, Number(resolvedControls.movement_duration_s || 3));
  merged.post_hold_s = Math.max(0, Number(resolvedControls.end_hold_s || 0));
  merged.total_duration_s = merged.pre_hold_s + merged.movement_duration_s + merged.post_hold_s;
  merged.start_target_rms_dbfs = merged.start_spl_db - merged.estimated_full_scale_spl_db;
  merged.end_target_rms_dbfs = merged.end_spl_db - merged.estimated_full_scale_spl_db;
  merged.instruction_target_spl_db = merged.end_spl_db + merged.instruction_offset_db;
  merged.instruction_target_rms_dbfs = merged.instruction_target_spl_db - merged.estimated_full_scale_spl_db;
  return merged;
}

function savedLoudnessPolicy() {
  const params = state?.design?.study_profile_reference_parameters || {};
  return normalizeLoudnessPolicy(params.loudness_policy || {});
}

function collectLoudnessPolicy(trajectoryControls = currentTrajectoryControls()) {
  const saved = savedLoudnessPolicy();
  const policy = normalizeLoudnessPolicy({
    ...saved,
    start_spl_db: Number($("loudness-start-spl")?.value || saved.start_spl_db),
    end_spl_db: Number($("loudness-end-spl")?.value || saved.end_spl_db),
    instruction_offset_db: Number($("instruction-offset-db")?.value || saved.instruction_offset_db),
    estimated_full_scale_spl_db: Number($("estimated-full-scale-spl")?.value || saved.estimated_full_scale_spl_db),
    audio_peak_ceiling_dbfs: Number($("audio-peak-ceiling-dbfs")?.value || saved.audio_peak_ceiling_dbfs)
  }, trajectoryControls);
  return policy;
}

function renderLoudnessControls() {
  const policy = savedLoudnessPolicy();
  const fields = {
    "loudness-start-spl": policy.start_spl_db,
    "loudness-end-spl": policy.end_spl_db,
    "instruction-offset-db": policy.instruction_offset_db,
    "estimated-full-scale-spl": policy.estimated_full_scale_spl_db,
    "audio-peak-ceiling-dbfs": policy.audio_peak_ceiling_dbfs
  };
  for (const [id, value] of Object.entries(fields)) {
    const input = $(id);
    if (input && document.activeElement !== input) input.value = Number(value).toFixed(id.includes("offset") || id.includes("ceiling") ? 1 : 0);
  }
  updateLoudnessDerived();
}

function updateLoudnessDerived() {
  const policy = collectLoudnessPolicy();
  const summary = $("loudness-derived");
  if (!summary) return;
  const instructionGain = dbToLinear(policy.instruction_offset_db);
  summary.innerHTML = `
    <span>${policy.start_spl_db.toFixed(0)}-&gt;${policy.end_spl_db.toFixed(0)} dB SPL linear-dB active ramp</span>
    <span>final ${policy.calibration_window_s.toFixed(1)} s active window calibrated</span>
    <span>RMS targets ${policy.start_target_rms_dbfs.toFixed(1)}-&gt;${policy.end_target_rms_dbfs.toFixed(1)} dBFS</span>
    <span>Instructions ${policy.instruction_offset_db.toFixed(1)} dB (${instructionGain.toFixed(3)}x)</span>
    <span>${policy.calibration_status === "measured" ? "measured" : "estimated, not measured"}</span>
  `;
}

function renderGeneratedNoiseSelect() {
  const select = $("generated-noise-select");
  const current = select.value;
  select.innerHTML = '<option value="">Choose noise type to bake...</option>';
  for (const item of PROCEDURAL_NOISE_TYPES) {
    const option = document.createElement("option");
    option.value = item.value;
    option.textContent = item.label;
    select.appendChild(option);
  }
  select.value = PROCEDURAL_NOISE_TYPES.some((item) => item.value === current) ? current : "";
}

function syncNoiseTypeButtons() {
  const activeType = pendingBakeRecipe && pendingBakeRecipe.kind === "generated_noise"
    ? pendingBakeRecipe.noise_type
    : "";
  const select = $("generated-noise-select");
  if (select) {
    select.value = PROCEDURAL_NOISE_TYPES.some((item) => item.value === activeType) ? activeType : "";
  }
  for (const button of document.querySelectorAll(".noise-type-button")) {
    const active = Boolean(activeType) && button.dataset.noiseType === activeType;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  }
}

function renderBakePanel() {
  const status = $("bake-status");
  const button = $("bake-stimulus");
  if (!status || !button) return;
  syncNoiseTypeButtons();
  const controlsLocked = isProfileReadonlyMode() || customViewModeLocked();
  const select = $("generated-noise-select");
  if (select) select.disabled = controlsLocked;
  for (const noiseButton of document.querySelectorAll(".noise-type-button")) {
    noiseButton.disabled = controlsLocked;
  }
  $("new-ingredient").disabled = controlsLocked;
  $("import-audio-spatialize").disabled = controlsLocked;
  $("import-audio-preserve").disabled = controlsLocked;
  if (!pendingBakeRecipe) {
    status.textContent = "Choose a stimulus type";
    status.className = "status-label required";
    button.disabled = true;
    button.textContent = "Create Stimulus";
    $("ingredient-editor-mode").textContent = "New ingredient";
    $("manual-audio-settings").hidden = true;
    $("trajectory-settings").hidden = false;
    $("fixed-clip-trajectory-note").hidden = true;
    const sourceModeControl = $("generated-source-mode-control");
    if (sourceModeControl) sourceModeControl.hidden = true;
    for (const card of document.querySelectorAll("[data-source-kind-card]")) card.classList.remove("active");
    return;
  }
  const label = pendingBakeRecipe.label || pendingBakeRecipe.audio?.label || noiseTypeLabel(pendingBakeRecipe.noise_type);
  if (document.activeElement !== $("bake-label") && !ingredientEditorDirty) $("bake-label").value = label || "";
  if ($("bake-gain")) $("bake-gain").value = 1;
  pendingBakeRecipe.loudness_policy = collectLoudnessPolicy();
  const isGenerated = pendingBakeRecipe.kind === "generated_noise";
  const isFixed = pendingBakeRecipe.kind === "fixed_audio" || pendingBakeRecipe.render_mode === "preserve";
  const isManual = !isGenerated;
  const sourceModeControl = $("generated-source-mode-control");
  if (sourceModeControl) {
    sourceModeControl.hidden = !isGenerated;
    if (isGenerated) {
      pendingBakeRecipe.source_profile = pendingBakeRecipe.source_profile || GOLD_STANDARD_SOURCE_PROFILE;
      setGeneratedSourceProfileControl(pendingBakeRecipe.source_profile);
    }
  }
  $("manual-audio-settings").hidden = !isManual;
  $("fixed-audio-duration-field").hidden = !isFixed;
  $("trajectory-settings").hidden = isFixed;
  $("fixed-clip-trajectory-note").hidden = !isFixed;
  $("choose-editor-audio").textContent = pendingBakeRecipe.audio?.source_input_path || pendingBakeRecipe.audio?.path ? "Replace Audio File" : "Choose Audio File";
  const sourcePath = pendingBakeRecipe.audio?.source_input_path || pendingBakeRecipe.audio?.path || "";
  $("selected-audio-file-status").textContent = sourcePath ? sourcePath.split(/[\\/]/).pop() : "No audio selected";
  const displayColor = normalizeDisplayColorHex(pendingBakeRecipe.display_color_hex || pendingBakeRecipe.audio?.display_color_hex) || pendingDisplayColorHex;
  pendingDisplayColorHex = normalizeDisplayColorHex(displayColor, "#1C7A86");
  $("manual-audio-color-value").textContent = pendingDisplayColorHex;
  $("manual-audio-color-swatch").style.background = pendingDisplayColorHex;
  const kind = isGenerated ? `${noiseTypeLabel(pendingBakeRecipe.noise_type)} noise` : (isFixed ? "Custom audio clip" : "Custom looming tone");
  const remake = pendingBakeRecipe.action === "remake";
  $("ingredient-editor-mode").textContent = remake ? `Selected · ${kind}` : `New · ${kind}`;
  button.textContent = remake ? (isFixed ? "Update Clip" : "Remake Stimulus") : (isFixed ? "Create Clip" : "Create Stimulus");
  const hasName = Boolean($("bake-label").value.trim());
  const hasAudio = !isManual || Boolean(sourcePath);
  status.textContent = !hasName ? "Stimulus name is required" : !hasAudio ? "Choose an audio file" : `${remake ? "Ready to remake" : "Ready to create"}: ${kind}`;
  status.className = `status-label ${hasName && hasAudio ? "ready" : "required"}`;
  button.disabled = controlsLocked || !hasName || !hasAudio;
  const activeSourceKind = pendingBakeRecipe.kind === "fixed_audio"
    ? "fixed_clip"
    : pendingBakeRecipe.kind === "imported_audio"
      ? "custom_looming"
      : pendingBakeRecipe.kind;
  for (const card of document.querySelectorAll("[data-source-kind-card]")) {
    card.classList.toggle("active", card.dataset.sourceKindCard === activeSourceKind);
  }
}

function renderNoiseTable() {
  const list = $("noise-list");
  list.innerHTML = "";
  for (const noise of state.design.noises || []) {
    const selectedNoise = String(noise.noise_type || "pink").toLowerCase();
    const sourceProfile = String(noise.source_profile || GOLD_STANDARD_SOURCE_PROFILE);
    const sourceProfileLabel = sourceProfile === CONTINUOUS_SOURCE_PROFILE ? "Smooth linear" : "Burst train";
    const wav = renderedWavForLabel(noise.label || `${noiseTypeLabel(selectedNoise)} noise`);
    const localPath = noise.prebaked_path || wav?.path || "";
    const card = document.createElement("div");
    card.className = `source-card stimulus-inventory-card noise-source-card${normalizeSourceKey(selectedIngredientLabel) === normalizeSourceKey(noise.label) ? " selected" : ""}`;
    card.tabIndex = 0;
    card.setAttribute("role", "button");
    card.setAttribute("aria-pressed", String(normalizeSourceKey(selectedIngredientLabel) === normalizeSourceKey(noise.label)));
    card.dataset.selectIngredient = noise.label || "";
    card.dataset.sourceLabel = sourceCardLabelFromSpec(noise, "Generated noise");
    applySourceCardColor(card, selectedNoise);
    card.innerHTML = `
      <div class="source-card-heading">
        <strong>${escapeHtml(noise.label || `${noiseTypeLabel(selectedNoise)} noise`)}</strong>
        <div class="source-card-actions">
          <button class="stimulus-card-audio-button" type="button" data-preview-source-label="${escapeAttr(noise.label || "")}" aria-label="Play or stop ${escapeAttr(noise.label || "stimulus")}">&#128266;</button>
          ${sourceFolderAction(localPath, noise.label || `${noiseTypeLabel(selectedNoise)} noise`)}
          <button type="button" data-remove-noise>Remove</button>
        </div>
      </div>
      ${stimulusTrajectoryHiddenFields(noise, selectedNoise, "generated_noise")}
      <input data-field="prebaked_path" type="hidden" value="${escapeAttr(localPath)}">
      <input data-field="gain" type="hidden" value="${Number(noise.gain || 1)}">
      <input data-field="motion_mode" type="hidden" value="${escapeAttr(noise.motion_mode || "looming")}">
      <input data-field="source_profile" type="hidden" value="${escapeAttr(sourceProfile)}">
      <input data-field="source_profile_parameters" type="hidden" value="${escapeAttr(JSON.stringify(noise.source_profile_parameters || {}))}">
      <div class="source-card-meta"><span class="source-mode-chip">${escapeHtml(noiseTypeLabel(selectedNoise))} noise</span><span class="source-mode-chip">${escapeHtml(sourceProfileLabel)}</span></div>
      <p class="stimulus-card-summary">${escapeHtml(compactTrajectorySummary(noise.trajectory_snapshot))}</p>
      <div class="stimulus-card-hidden-fields">
        <input data-field="label" value="${escapeAttr(noise.label || "")}">
        <input data-field="noise_type" value="${escapeAttr(selectedNoise)}">
        <input data-field="azimuth_deg" value="${Number(noise.azimuth_deg || 0)}">
        <input data-field="elevation_deg" value="${Number(noise.elevation_deg || 0)}">
      </div>
    `;
    list.appendChild(card);
  }
  for (const audio of state.design.custom_looming_files || []) {
    list.appendChild(renderAudioInventoryCard(audio, "spatialize"));
  }
}

function renderAudioTable() {
  const list = $("audio-list");
  const snippetList = $("snippet-list");
  list.innerHTML = "";
  snippetList.innerHTML = "";
  for (const audio of state.design.prestimulus_files || []) list.appendChild(renderAudioInventoryCard(audio, "prestimulus"));
}

function compactTrajectorySummary(snapshot = {}) {
  if (!snapshot || snapshot.start_distance_cm === undefined) return "Trajectory saved with source";
  const duration = Number(snapshot.movement_duration_s || 0);
  return `${Number(snapshot.start_distance_cm)} → ${Number(snapshot.end_distance_cm)} cm · ${Number(snapshot.start_rotation_deg || 0)}° → ${Number(snapshot.end_rotation_deg || 0)}° · ${duration.toFixed(1)} s`;
}

function renderAudioInventoryCard(audio, role) {
  const fixed = role === "prestimulus";
  const color = displayColorForSource(audio, fixed ? "prestimulus" : "custom_audio");
  const motionMode = fixed ? "stationary" : "looming";
  const label = sourceCardLabelFromSpec(audio, fixed ? "Custom audio clip" : "Custom looming tone");
  const phase = audio.phase || "";
  const card = document.createElement("div");
  card.className = `source-card stimulus-inventory-card audio-source-card${normalizeSourceKey(selectedIngredientLabel) === normalizeSourceKey(label) ? " selected" : ""}`;
  card.dataset.audioRole = role;
  card.dataset.sourceLabel = label;
  card.dataset.selectIngredient = label;
  card.tabIndex = 0;
  card.setAttribute("role", "button");
  card.setAttribute("aria-pressed", String(normalizeSourceKey(selectedIngredientLabel) === normalizeSourceKey(label)));
  applySourceCardColor(card, color);
  card.innerHTML = `
    <div class="source-card-heading">
      <strong>${escapeHtml(label)}</strong>
      <div class="source-card-actions">
        <button class="stimulus-card-audio-button" type="button" data-preview-source-label="${escapeAttr(label)}" aria-label="Play or stop ${escapeAttr(label)}">&#128266;</button>
        ${sourceFolderAction(audio.path, label)}
        <button type="button" data-remove-audio>Remove</button>
      </div>
    </div>
    <div class="source-card-meta"><span class="source-mode-chip">${fixed ? "Fixed clip" : "Custom looming tone"}</span><span class="source-mode-chip">${escapeHtml(color)}</span></div>
    <p class="stimulus-card-summary"><strong>${Number(audio.target_duration_s || audio.duration_s || 4).toFixed(1)} s</strong>${fixed ? " · Preserved audio clip — no trajectory" : ` · ${escapeHtml(compactTrajectorySummary(audio.trajectory_snapshot))}`}</p>
    ${stimulusTrajectoryHiddenFields(audio, color, fixed ? "fixed_audio" : "imported_audio")}
    <div class="stimulus-card-hidden-fields">
      <input data-field="path" value="${escapeAttr(audio.path || "")}">
      <input data-field="source_input_path" value="${escapeAttr(audio.source_input_path || "")}">
      <input data-field="display_color_hex" value="${escapeAttr(color)}">
      <input data-field="motion_mode" value="${escapeAttr(motionMode)}">
      <input data-field="audio_role" value="${fixed ? "prestimulus" : "spatialize"}">
      <input data-field="tone_type" value="${escapeAttr(audio.tone_type || "custom_audio")}">
      <input data-field="placement" value="before">
      <input data-field="target_source_label" value="">
      <input data-field="phase" value="${escapeAttr(phase)}">
      <input data-field="gap_s" value="0">
      <input data-field="gain" value="${Number(audio.gain || 1)}">
      <input data-field="label" value="${escapeAttr(label)}">
      <input data-field="target_duration_s" value="${Number(audio.target_duration_s || audio.duration_s || 4)}">
    </div>
  `;
  return card;
}

function stimulusTrajectoryHiddenFields(source, colorKey = "custom_audio", sourceKind = "") {
  const snapshot = trajectorySnapshotForSource(source, colorKey, sourceKind);
  const rawValue = escapeAttr(JSON.stringify(snapshot || {}));
  return `<input data-field="trajectory_snapshot" type="hidden" value="${rawValue}">`;
}

function stimulusTrajectoryTrace(source, colorKey = "custom_audio", sourceKind = "") {
  const snapshot = trajectorySnapshotForSource(source, colorKey, sourceKind);
  const rawValue = escapeAttr(JSON.stringify(snapshot || {}));
  if (!snapshot || sourceKind === "fixed_audio") return `<input data-field="trajectory_snapshot" type="hidden" value="${rawValue}">`;
  const colors = trajectoryColorSet(snapshot, colorKey);
  const color = colors[0] || sourceColor(colorKey);
  const gradient = trajectoryGradient(colors);
  const start = Number(snapshot.start_distance_cm || 0);
  const end = Number(snapshot.end_distance_cm || 0);
  const startRot = Number(snapshot.start_rotation_deg || 0);
  const endRot = Number(snapshot.end_rotation_deg || 0);
  const duration = Number(snapshot.movement_duration_s || 0);
  const holdStart = Number(snapshot.start_hold_s || 0);
  const holdEnd = Number(snapshot.end_hold_s || 0);
  const path = Number(snapshot.path_length_m || Math.abs(start - end) / 100);
  const title = `${start || "?"} -> ${end || "?"} cm`;
  const detail = `${startRot} -> ${endRot} deg, ${duration}s move`;
  const shared = colors.length > 1 ? `shared path, ${colors.length} tones` : "";
  return `
    <div class="stimulus-trajectory-trace" style="--trajectory-color: ${escapeAttr(color)}; --trajectory-gradient: ${escapeAttr(gradient)}">
      <input data-field="trajectory_snapshot" type="hidden" value="${rawValue}">
      <div class="stimulus-trajectory-line" aria-hidden="true">
        <span class="trajectory-dot start"></span>
        <span class="trajectory-dot end"></span>
      </div>
      <div class="stimulus-trajectory-text">
        <strong>${escapeHtml(title)}</strong>
        <span>${escapeHtml(detail)}</span>
        <span>${escapeHtml(`${path.toFixed(2)} m path, holds ${holdStart}s/${holdEnd}s`)}</span>
        ${shared ? `<span>${escapeHtml(shared)}</span>` : ""}
      </div>
    </div>
  `;
}

function trajectoryColorSet(snapshot, fallbackKey = "custom_audio") {
  const fallback = sourceColor(fallbackKey);
  const groupKey = trajectoryGroupKey(snapshot);
  if (!groupKey || !state?.design) return [fallback];
  const colors = [];
  for (const source of stimulusInventorySources()) {
    const sourceSnapshot = trajectorySnapshotForSource(source, source.color_key, source.source_kind);
    if (trajectoryGroupKey(sourceSnapshot) !== groupKey) continue;
    const color = sourceColor(source.color_key);
    if (!colors.includes(color)) colors.push(color);
  }
  if (!colors.includes(fallback)) colors.unshift(fallback);
  return colors.length ? colors : [fallback];
}

function stimulusInventorySources() {
  return [
    ...(state?.design?.noises || []).map((source) => ({
      ...source,
      source_kind: "generated_noise",
      color_key: source.noise_type || "pink"
    })),
    ...(state?.design?.custom_looming_files || []).map((source) => ({
      ...source,
      source_kind: "imported_audio",
      color_key: source.display_color_hex || source.tone_type || source.noise_type || "custom_audio"
    }))
  ];
}

function trajectoryGroupKey(snapshot = {}) {
  if (!snapshot || snapshot.start_distance_cm === undefined || snapshot.end_distance_cm === undefined) return "";
  const fields = [
    "start_distance_cm",
    "end_distance_cm",
    "start_rotation_deg",
    "end_rotation_deg",
    "movement_duration_s",
    "start_hold_s",
    "end_hold_s"
  ];
  const controlKey = fields.map((field) => roundedKeyPart(snapshot[field])).join("|");
  const start = snapshot.start || {};
  const end = snapshot.end || {};
  const coordinateKey = ["x_m", "y_m", "z_m"]
    .map((field) => `${roundedKeyPart(start[field])}:${roundedKeyPart(end[field])}`)
    .join("|");
  return `${controlKey}|${coordinateKey}`;
}

function roundedKeyPart(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(4) : "";
}

function sourceColor(key = "custom_audio") {
  const normalizedHex = normalizeDisplayColorHex(key);
  if (normalizedHex) return normalizedHex;
  return STIMULUS_TRAJECTORY_COLORS[String(key || "").toLowerCase()] || STIMULUS_TRAJECTORY_COLORS.custom_audio;
}

function normalizeDisplayColorHex(value, fallback = "") {
  const raw = String(value || "").trim();
  if (!/^#[0-9a-f]{6}$/i.test(raw)) return fallback;
  return raw.toUpperCase();
}

function displayColorForSource(source = {}, fallbackKey = "custom_audio") {
  return normalizeDisplayColorHex(source.display_color_hex) || sourceColor(source.tone_type || source.noise_type || fallbackKey);
}

function sourceColorOptions(selected = "custom_audio") {
  const selectedValue = String(selected || "custom_audio").toLowerCase();
  return SOURCE_COLOR_OPTIONS.map((item) =>
    `<option value="${escapeAttr(item.value)}" ${item.value === selectedValue ? "selected" : ""}>${escapeHtml(item.label)}</option>`
  ).join("");
}

function applySourceCardColor(card, colorKey = "custom_audio") {
  if (!card) return;
  const color = sourceColor(colorKey);
  card.style.setProperty("--source-card-color", color);
  card.style.setProperty("--source-card-color-soft", colorWithAlpha(color, 0.12));
}

function colorWithAlpha(hex, alpha = 0.12) {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ""));
  if (!match) return `rgba(36, 107, 85, ${alpha})`;
  const [, r, g, b] = match;
  return `rgba(${Number.parseInt(r, 16)}, ${Number.parseInt(g, 16)}, ${Number.parseInt(b, 16)}, ${Math.max(0, Math.min(1, alpha))})`;
}

function trajectoryGradient(colors = []) {
  const safeColors = (colors.length ? colors : [sourceColor("custom_audio")]).map((color) => color || sourceColor("custom_audio"));
  if (safeColors.length === 1) return safeColors[0];
  const stops = [];
  const width = 100 / safeColors.length;
  safeColors.forEach((color, index) => {
    const start = Math.round(index * width * 10) / 10;
    const end = Math.round((index + 1) * width * 10) / 10;
    stops.push(`${color} ${start}%`, `${color} ${end}%`);
  });
  return `linear-gradient(90deg, ${stops.join(", ")})`;
}

function trajectorySnapshotForSource(source = {}, colorKey = "custom_audio", sourceKind = "") {
  const snapshot = source.trajectory_snapshot && typeof source.trajectory_snapshot === "object"
    ? clone(source.trajectory_snapshot)
    : {};
  if (snapshot.start_distance_cm !== undefined && snapshot.end_distance_cm !== undefined) {
    return snapshot;
  }
  if (sourceKind === "fixed_audio") return {};
  if (String(source.motion_mode || "").toLowerCase() === "stationary") return {};
  const controls = state.trajectory_controls || currentTrajectoryControls();
  return {
    schema: "pps-stimulus-trajectory.v1",
    label: source.label || "",
    source_kind: sourceKind,
    noise_type: colorKey,
    start_distance_cm: controls.start_distance_cm,
    end_distance_cm: controls.end_distance_cm,
    start_rotation_deg: controls.start_rotation_deg,
    end_rotation_deg: controls.end_rotation_deg,
    movement_duration_s: controls.movement_duration_s,
    start_hold_s: controls.start_hold_s,
    end_hold_s: controls.end_hold_s,
    path_length_m: Math.max(0, Math.abs(Number(controls.start_distance_cm || 0) - Number(controls.end_distance_cm || 0)) / 100),
  };
}

function readJsonField(field) {
  if (!field) return {};
  try {
    const value = JSON.parse(field.value || "{}");
    return value && typeof value === "object" ? value : {};
  } catch (_error) {
    return {};
  }
}

function sourceFolderAction(path, label = "source") {
  if (!path) return "";
  if (staticModeActive) {
    return `<a class="source-folder-link" href="${escapeAttr(staticResourceUrl(path))}" target="_blank" rel="noopener noreferrer" aria-label="Open ${escapeAttr(label)} asset">Open Asset</a>`;
  }
  return `<button type="button" class="source-folder-link" data-open-folder="${escapeAttr(path)}" aria-label="Open ${escapeAttr(label)} folder">Open Folder</button>`;
}

function renderedWavForLabel(label) {
  const target = normalizeSourceKey(label);
  if (!target) return null;
  return (state.render?.wavs || []).find((wav) => {
    const keys = [
      wav.label,
      wav.path,
      String(wav.path || "").split(/[\\/]/).pop(),
      String(wav.path || "").split(/[\\/]/).pop()?.replace(/^looming_/i, "").replace(/\.[^.]+$/, ""),
    ];
    return keys.some((key) => normalizeSourceKey(key) === target);
  }) || null;
}

function normalizeSourceKey(value) {
  return String(value || "")
    .replace(/^looming[_\s-]*/i, "")
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
}

function renderSourceCounts() {
  const generated = $("noise-list").querySelectorAll(".noise-source-card").length;
  const audioCards = [
    ...$("noise-list").querySelectorAll(".audio-source-card"),
    ...$("audio-list").querySelectorAll(".audio-source-card"),
  ];
  const snippetCards = [...$("snippet-list").querySelectorAll(".audio-source-card")];
  const imported = audioCards.filter((card) => card.dataset.audioRole !== "prestimulus").length;
  const prestimulus = audioCards.filter((card) => card.dataset.audioRole === "prestimulus").length
    + snippetCards.filter((card) => card.dataset.audioRole === "prestimulus").length;
  const stimulusSources = generated + imported + prestimulus;
  const sourceLabel = `${stimulusSources} local source${stimulusSources === 1 ? "" : "s"}`;
  $("source-counts").textContent = sourceLabel;
  $("source-counts").className = `status-label ${stimulusSources ? "ready" : "required"}`;
  if ($("snippet-counts")) {
    $("snippet-counts").textContent = `${prestimulus} clip${prestimulus === 1 ? "" : "s"}`;
    $("snippet-counts").className = `status-label ${prestimulus ? "ready" : "required"}`;
  }
}

function renderSegmentRegistryOutputs() {
  updateSegmentFolderButton("0_profile", "open-profile-folder");
  updateSegmentFolderButton("1_core_audio_ingredients", "open-ingredient-folder");
  updateSegmentFolderButton("2_trial_sequence_designs", "open-trial-sequence-folder");

  const segment1 = projectSegment("1_core_audio_ingredients");
  const segment2 = projectSegment("2_trial_sequence_designs");
  const sequence = sequenceVariantEstimate();
  const sequenceButton = $("bake-trial-sequences");
  const sequenceStatus = $("trial-sequence-bake-status");
  const segment1Ready = segment1.status === "ready";
  const sequenceReadyToBake = segment1Ready && sequence.variantCount > 0;
  if (sequenceButton) sequenceButton.disabled = !sequenceReadyToBake;
  if (sequenceStatus) {
    sequenceStatus.textContent = segment2.status === "ready"
      ? `${segment2.variant_count || 0} variants`
      : (sequenceReadyToBake ? "ready to bake" : "waiting");
    sequenceStatus.className = `status-label ${segment2.status === "ready" || sequenceReadyToBake ? "ready" : "required"}`;
  }

  const segment3 = projectSegment("3_tactile_and_baseline_trials");
  const trialButton = $("bake-trial-files");
  const segment2Ready = segment2.status === "ready";
  const hasSoas = parseIntegerList($("block-soa-values")?.value || $("soa-values")?.value || "").length > 0;
  if (trialButton) trialButton.disabled = !(segment2Ready && hasSoas);
  updateSegmentFolderButton("3_tactile_and_baseline_trials", "open-trial-file-folder");
  const trialStatus = $("trial-file-bake-status");
  if (trialStatus) {
    trialStatus.textContent = segment3.status === "ready"
      ? `${segment3.total_count || 0} files`
      : (segment2Ready ? "ready to bake" : "waiting for Segment 2");
    trialStatus.className = `status-label ${segment3.status === "ready" || segment2Ready ? "ready" : "required"}`;
  }

  const segment4 = projectSegment("4_trial_repetition_pool");
  const poolButton = $("bake-trial-pool");
  const segment3Ready = segment3.status === "ready";
  if (poolButton) poolButton.disabled = !segment3Ready || trialPoolCompositionEstimate().totalTrials <= 0;
  const poolStatus = $("trial-pool-bake-status");
  if (poolStatus) {
    poolStatus.textContent = segment4.status === "ready"
      ? `${segment4.total_count || 0} rows`
      : (segment3Ready ? "ready to bake" : "waiting for Segment 3");
    poolStatus.className = `status-label ${segment4.status === "ready" || segment3Ready ? "ready" : "required"}`;
  }
  const poolOpenButton = $("open-trial-pool-folder");
  if (poolOpenButton) {
    poolOpenButton.disabled = !segment4.folder_path;
    poolOpenButton.dataset.folderPath = segment4.folder_path || "";
  }

  const segment5 = projectSegment("5_block_csv_preview");
  const blockButton = $("regenerate-block-csvs");
  const acceptButton = $("accept-block-csvs");
  const downloadButton = $("download-block-randomization");
  const segment4Ready = segment4.status === "ready";
  const blockAccepted = Boolean(segment5.accepted || state.block_csv_preview?.accepted);
  if (blockButton) {
    blockButton.disabled = !segment4Ready || blockAccepted;
    blockButton.textContent = (state.block_csv_preview?.blocks || []).length ? "Regenerate Blocks" : "Generate Blocks";
  }
  if (acceptButton) {
    acceptButton.disabled = !(segment5.status === "ready");
    const reviewingSchedule = Boolean(
      isCustomMode()
      && editModeActive
      && normalizedDesignerProgress().edit_step === "schedule"
    );
    acceptButton.textContent = reviewingSchedule || !blockAccepted
      ? "Accept Blocks & Continue"
      : "Edit Blocks";
  }
  if (downloadButton) downloadButton.disabled = !(state.block_csv_preview?.blocks || []).length;
  const blockStatus = $("block-csv-bake-status");
  if (blockStatus) {
    blockStatus.textContent = segment5.status === "ready"
      ? `${segment5.block_count || 0} CSVs${blockAccepted ? " accepted" : ""}`
      : (segment4Ready ? "ready to bake" : "waiting for Segment 4");
    blockStatus.className = `status-label ${segment5.status === "ready" || segment4Ready ? "ready" : "required"}`;
  }
  updateStageActionHierarchy();
}

function updateStageActionHierarchy() {
  const stages = [
    { step: "stimulus", artifact: "bake-stimulus", ready: projectSegment("1_core_audio_ingredients").status === "ready", create: "Add Ingredient", rebuild: "Add Another Ingredient" },
    { step: "trials", artifact: "bake-trial-sequences", ready: projectSegment("2_trial_sequence_designs").status === "ready", create: "Create Trial Sequences", rebuild: "Rebuild Trial Sequences" },
    { step: "baseline", artifact: "bake-trial-files", ready: projectSegment("3_tactile_and_baseline_trials").status === "ready", create: "Create Trial Files", rebuild: "Rebuild Trial Files" },
    { step: "block", artifact: "bake-trial-pool", ready: projectSegment("4_trial_repetition_pool").status === "ready", create: "Create Trial Pool", rebuild: "Rebuild Trial Pool" },
  ];
  for (const stage of stages) {
    const artifact = $(stage.artifact);
    const continueButton = document.querySelector(`[data-continue-step="${stage.step}"]`);
    if (artifact) {
      artifact.textContent = stage.ready ? stage.rebuild : stage.create;
      artifact.classList.toggle("primary", !stage.ready);
    }
    if (continueButton) continueButton.classList.toggle("primary", stage.ready);
  }
}

function projectSegment(folderName) {
  return state?.project_segments?.[folderName] || {};
}

function updateSegmentFolderButton(folderName, buttonId) {
  const segment = projectSegment(folderName);
  const button = $(buttonId);
  if (button) {
    button.disabled = !segment.folder_path;
    button.dataset.folderPath = segment.folder_path || "";
  }
}

function latestBlockCsvJob() {
  const jobs = state?.jobs || [];
  return jobs.find((job) => job.kind === "block_csv_preview" || job.result?.source_kind === "block_csv_preview") || null;
}

function setBlockProgress(current, total, label) {
  const safeTotal = Math.max(0, Number(total || 0));
  const safeCurrent = Math.max(0, Math.min(Number(current || 0), safeTotal || Number(current || 0)));
  const percent = safeTotal ? Math.round((safeCurrent / safeTotal) * 1000) / 10 : 0;
  const track = $("block-build-progress-track");
  if (track) {
    track.style.setProperty("--block-progress", String(percent));
    track.setAttribute("aria-valuenow", String(percent));
  }
  if ($("block-build-progress-label")) $("block-build-progress-label").textContent = label || "waiting";
  if ($("block-build-progress-count")) $("block-build-progress-count").textContent = `${safeCurrent} / ${safeTotal} blocks`;
  if ($("block-build-progress-percent")) $("block-build-progress-percent").textContent = `${percent}%`;
}

function renderBlockCsvPreview() {
  const blockInput = $("block-count");
  const seedInput = $("block-randomization-seed");
  const protocolBlocks = Math.max(1, Math.round(Number(state?.design?.protocol?.blocks || $("blocks")?.value || 1)));
  const protocolSeed = normalizedRandomizationSeed(state?.design?.protocol?.random_seed, 20250604);
  if (blockInput && document.activeElement !== blockInput) blockInput.value = protocolBlocks;
  if (seedInput && document.activeElement !== seedInput) seedInput.value = protocolSeed;
  const segment5 = projectSegment("5_block_csv_preview");
  const preview = state.block_csv_preview || {};
  renderBlockPolicySummary(preview);
  const blockAccepted = Boolean(segment5.accepted || preview.accepted);
  if (blockInput) blockInput.disabled = blockAccepted;
  if (seedInput) seedInput.disabled = blockAccepted;
  if ($("generate-block-randomization-seed")) $("generate-block-randomization-seed").disabled = blockAccepted;
  if ($("download-block-randomization")) $("download-block-randomization").disabled = !(preview.blocks || []).length;
  if ($("blocks")) $("blocks").value = blockInput?.value || protocolBlocks;
  const job = latestBlockCsvJob();
  if (job?.status === "running" || job?.status === "queued") {
    setBlockProgress(job.progress_current || 0, job.progress_total || protocolBlocks, job.progress_label || "creating block CSVs");
  } else if (segment5.status === "ready") {
    setBlockProgress(
      preview.block_count || segment5.block_count || 0,
      preview.block_count || segment5.block_count || 0,
      (preview.accepted || segment5.accepted) ? "accepted" : "ready"
    );
  } else {
    setBlockProgress(0, protocolBlocks, segment5.last_validation_message || "waiting");
  }

  const list = $("block-csv-preview-list");
  if (!list) return;
  const blocks = preview.blocks || [];
  if (!blocks.length) {
    list.innerHTML = `<div class="summary-text">No block CSVs yet.</div>`;
    return;
  }
  list.innerHTML = blocks.map((block, index) => renderBlockPreviewCard(block, index)).join("");
}

function normalizedRandomizationSeed(value, fallback = 20250604) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(2147483647, Math.round(parsed)));
}

function newRandomizationSeed() {
  const values = new Uint32Array(1);
  globalThis.crypto.getRandomValues(values);
  return values[0] & 0x7fffffff;
}

function renderBlockPolicySummary(preview = {}) {
  const container = $("block-order-policy-summary");
  if (!container) return;
  const strategy = preview.randomization_strategy || "seeded row-preserving randomization";
  const seed = preview.randomization_seed ?? state.design?.protocol?.random_seed;
  container.innerHTML = `
    <span><strong>Method</strong> ${escapeHtml(humanize(strategy))}</span>
    <span><strong>Seed</strong> ${seed === undefined || seed === null || seed === "" ? "assigned when blocks are generated" : escapeHtml(seed)}</span>
  `;
}

function renderBlockPreviewCard(block, index = 0) {
  const rows = block.preview_rows || [];
  const familyCounts = block.family_counts || {};
  const total = Number(block.trial_count || rows.length || 0);
  const distribution = ["audio_tactile", "baseline", "catch", "auditory_only"].map((family) => {
    const count = Number(familyCounts[family] || 0);
    return `
      <div class="block-distribution-segment" style="--segment-color:${escapeAttr(TRIAL_FAMILY_COLORS[family] || "#5e695f")}">
        ${escapeHtml(familyDisplayName(family))}: ${count}
      </div>
    `;
  }).join("");
  const meta = [
    ...blockFeatureChips(block.row_label_counts, "Row", rows, "row_label", "family_color_hex", 4),
    ...blockFeatureChips(block.soa_counts, "SOA", rows, "soa_ms", "soa_color_hex", 6),
    ...blockFeatureChips(block.noise_type_counts, "Noise", rows, "noise_type", "noise_color_hex", 5),
  ].join("");
  const bodyRows = rows.map(renderBlockPreviewRow).join("");
  const csvName = block.csv_file_name || "";
  const tableId = `block-preview-table-${index + 1}`;
  const mobileTableToggle = rows.length > 6
    ? `<button class="mobile-table-more" type="button" data-mobile-table-toggle="${tableId}" data-row-count="${rows.length}" aria-controls="${tableId}" aria-expanded="false">Show all ${rows.length} rows</button>`
    : "";
  return `
    <details class="block-preview-card" ${index === 0 ? "open" : ""}>
      <summary>
        <div>
          <strong>${escapeHtml(block.block_label || `Block ${block.block_index || ""}`)}</strong>
        </div>
        <div class="block-distribution">${distribution}</div>
        <span>${total} trials / ${escapeHtml(formatDurationMs(block.duration_ms || 0))}</span>
      </summary>
      <div class="block-preview-body">
        <details class="block-file-details"><summary>Source file</summary><code>${escapeHtml(csvName)}</code></details>
        <div class="block-preview-meta">${meta}</div>
        <div class="scroll-table compact">
          <table id="${tableId}" class="data-table compact block-preview-table mobile-card-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Family</th>
                <th>Row</th>
                <th>SOA</th>
                <th>Noise</th>
                <th>Sequence</th>
                <th>Duration</th>
                <th>WAV</th>
              </tr>
            </thead>
            <tbody>${bodyRows}</tbody>
          </table>
          ${mobileTableToggle}
        </div>
      </div>
    </details>
  `;
}

function blockFeatureChips(counts = {}, prefix, rows, valueKey, colorKey, limit) {
  const colorByValue = new Map();
  for (const row of rows || []) {
    const value = String(row[valueKey] || "");
    if (value && !colorByValue.has(value)) colorByValue.set(value, row[colorKey] || "#5e695f");
  }
  return Object.entries(counts)
    .filter(([value]) => value)
    .slice(0, limit)
    .map(([value, count]) => `
      <span class="feature-chip" style="--chip-color:${escapeAttr(colorByValue.get(value) || "#5e695f")}">
        ${escapeHtml(prefix)} ${escapeHtml(value)}: ${escapeHtml(count)}
      </span>
    `);
}

function renderBlockPreviewRow(row) {
  return `
    <tr>
      <td data-label="#">${escapeHtml(row.block_trial_index || "")}</td>
      ${coloredPreviewCell(row.family_label || familyDisplayName(row.family), row.family_color_hex, "Family")}
      ${coloredPreviewCell(row.row_label || "", row.row_color_hex, "Row")}
      ${coloredPreviewCell(row.soa_ms ? `${row.soa_ms} ms` : "", row.soa_color_hex, "SOA")}
      ${coloredPreviewCell(row.noise_type || "", row.noise_color_hex, "Noise")}
      <td data-label="Sequence">${escapeHtml(row.sequence_labels || row.sequence_variant_key || "")}</td>
      <td data-label="Duration">${escapeHtml(formatDurationMs(row.duration_ms || 0))}</td>
      <td data-label="WAV"><details class="block-file-details"><summary>Audio file</summary><code>${escapeHtml(row.source_file_name || "")}</code></details></td>
    </tr>
  `;
}

function coloredPreviewCell(value, color, label) {
  return `<td class="preview-color-cell" data-label="${escapeAttr(label)}" style="--cell-color:${escapeAttr(color || "transparent")}">${escapeHtml(value || "")}</td>`;
}

function stimulusTargetOptionsFromDesign(selected = "") {
  const options = stimulusSourceOptionsFromDesign();
  return renderOptionList(options, selected);
}

function stimulusSourceOptionsFromDesign() {
  const options = [{ value: "", label: "Every stimulus source" }];
  const seen = new Set([""]);
  for (const noise of state.design.noises || []) {
    addStimulusSourceOption(options, seen, noise.label || `${noiseTypeLabel(noise.noise_type)} noise`);
  }
  for (const audio of state.design.custom_looming_files || []) {
    addStimulusSourceOption(options, seen, audio.label || audioRoleTitle(audio.render_mode || "preserve"));
  }
  return options;
}

function stimulusSourceOptionsFromDom() {
  const options = [{ value: "", label: "Every stimulus source" }];
  const seen = new Set([""]);
  for (const card of $("noise-list").querySelectorAll(".noise-source-card")) {
    const label = card.querySelector('[data-field="label"]')?.value || "Generated noise";
    addStimulusSourceOption(options, seen, label);
  }
  for (const card of $("noise-list").querySelectorAll(".audio-source-card")) {
    const role = card.querySelector('[data-field="audio_role"]')?.value;
    if (role === "prestimulus") continue;
    const label = card.querySelector('[data-field="label"]')?.value || audioRoleTitle(role);
    addStimulusSourceOption(options, seen, label);
  }
  return options;
}

function addStimulusSourceOption(options, seen, label) {
  const value = String(label || "").trim();
  if (!value || seen.has(value)) return;
  seen.add(value);
  options.push({ value, label: value });
}

function stimulusPhaseOptions(selected = "") {
  const phases = state.design.protocol?.respiratory_phases || ["Inhale", "Exhale"];
  const options = [{ value: "", label: "Any phase" }];
  const seen = new Set([""]);
  for (const phase of phases) {
    const value = String(phase || "").trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: value });
  }
  return renderOptionList(options, selected);
}

function renderOptionList(options, selected = "") {
  const selectedValue = String(selected || "");
  return options
    .map((item) => `<option value="${escapeAttr(item.value)}" ${item.value === selectedValue ? "selected" : ""}>${escapeHtml(item.label)}</option>`)
    .join("");
}

function refreshAssemblyTargetOptions() {
  const options = stimulusSourceOptionsFromDom();
  for (const select of document.querySelectorAll('select[data-field="target_source_label"]')) {
    const selected = select.value || select.dataset.selectedTarget || "";
    select.innerHTML = renderOptionList(options, selected);
    select.value = options.some((item) => item.value === selected) ? selected : "";
    select.dataset.selectedTarget = select.value;
  }
}

function normalizeSnippetPlacement(value) {
  return value === "after" ? "after" : "before";
}

function renderTrials() {
  const protocol = state.design.protocol || {};
  syncTrialPoolDraftFromState();
  $("repetitions").value = formatRepetitionValue(trialPoolRepetitionDraft.defaultRepetitions ?? protocol.repetitions_per_condition ?? 1);
  const blockCount = Math.max(1, Math.round(Number(protocol.blocks || 1)));
  $("blocks").value = blockCount;
  if ($("block-count") && document.activeElement !== $("block-count")) $("block-count").value = blockCount;
  $("soa-values").value = formatList(protocol.soa_values_ms);
  $("block-soa-values").value = formatList(protocol.soa_values_ms);
  renderTrialStrips();
  renderProtocolSummary();
}

function formatDurationMs(ms) {
  const totalSeconds = Math.max(0, Math.round(Number(ms || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) return `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`;
  if (minutes) return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  return `${seconds}s`;
}

function formatMillis(ms) {
  return `${Math.round(Number(ms || 0)).toLocaleString()} ms`;
}

function formatPercent(value) {
  const rounded = Math.round(Number(value || 0) * 10) / 10;
  return `${rounded}%`;
}

function slugify(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "item";
}

function normalizeRepetitionValue(value, fallback = 0) {
  const parsed = Number(value);
  const base = Number.isFinite(parsed) ? parsed : Number(fallback || 0);
  return Math.max(0, Math.round(base * 2) / 2);
}

function formatRepetitionValue(value) {
  const normalized = normalizeRepetitionValue(value, 0);
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(1);
}

function trialPoolFamilyKey(value) {
  const key = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["audio", "target", "target_audio_tactile", "audio_tactile_trials"].includes(key)) return "audio_tactile";
  if (["baseline_trials", "tactile_baseline", "tactile_only"].includes(key)) return "baseline";
  if (["catch_trials", "audio_only"].includes(key)) return "catch";
  if (["auditory", "auditory_only", "auditory_only_trials", "audio_response", "audio_only_response"].includes(key)) return "auditory_only";
  return key;
}

function stableHashInt(text) {
  let hash = 2166136261;
  const input = String(text || "");
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function trialPoolSubgroupLabel(file) {
  const label = String(file?.row_label || file?.row_folder_name || "").trim();
  if (/inhale/i.test(label)) return "Inhale";
  if (/exhale/i.test(label)) return "Exhale";
  return label || "Row";
}

function trialPoolSourceLineage(file) {
  const labels = String(file?.source_labels || file?.sequence_labels || "").trim();
  if (labels) {
    const pieces = labels.split("|").map((piece) => piece.trim()).filter(Boolean);
    if (pieces.length) return slugify(pieces[pieces.length - 1]);
  }
  return slugify(file?.sequence_variant_key || file?.variant_key || file?.source_file_name || file?.file_key || "source");
}

function trialPoolFractionalStratum(file, fractionalRemainder) {
  const family = trialPoolFamilyKey(file?.family);
  const rowKey = slugify(trialPoolSubgroupLabel(file));
  const soaMs = Math.round(Number(file?.soa_ms || 0));
  const mode = slugify(file?.baseline_mode || "none");
  return `${family}|${rowKey}|soa${soaMs}|${mode}|frac${formatRepetitionValue(fractionalRemainder)}`;
}

function trialPoolFractionalParentStratum(file, fractionalRemainder) {
  const family = trialPoolFamilyKey(file?.family);
  const rowKey = slugify(trialPoolSubgroupLabel(file));
  const mode = slugify(file?.baseline_mode || "none");
  return `${family}|${rowKey}|${mode}|frac${formatRepetitionValue(fractionalRemainder)}`;
}

function trialPoolFilesFromState() {
  return Array.isArray(state?.trial_file_bake?.files) ? state.trial_file_bake.files : [];
}

function syncTrialPoolDraftFromState() {
  const sourceHash = state?.trial_file_bake?.manifest_sha256 || "";
  if (trialPoolDraftInitialized && trialPoolDraftSourceHash === sourceHash) return;
  const settings = state?.trial_pool_bake?.settings || {};
  const protocol = state?.design?.protocol || {};
  const protocolDefaults = protocol.trial_pool_repetition_defaults || {};
  const defaultRepetitions = normalizeRepetitionValue(
    settings.default_repetitions ?? protocolDefaults.default ?? protocol.repetitions_per_condition ?? 1,
    1
  );
  const familyRepetitions = {};
  for (const [key, value] of Object.entries(protocolDefaults)) {
    const family = trialPoolFamilyKey(key);
    if (["audio_tactile", "baseline", "catch", "auditory_only"].includes(family)) {
      familyRepetitions[family] = normalizeRepetitionValue(value, defaultRepetitions);
    }
  }
  for (const [key, value] of Object.entries(settings.family_repetitions || {})) {
    const family = trialPoolFamilyKey(key);
    if (["audio_tactile", "baseline", "catch", "auditory_only"].includes(family)) {
      familyRepetitions[family] = normalizeRepetitionValue(value, defaultRepetitions);
    }
  }
  trialPoolRepetitionDraft = {
    defaultRepetitions,
    familyRepetitions,
    folderRepetitions: Object.fromEntries(Object.entries(settings.folder_repetitions || {}).map(([key, value]) => [key, normalizeRepetitionValue(value, defaultRepetitions)])),
    fileRepetitionOverrides: Object.fromEntries(Object.entries(settings.file_repetition_overrides || {}).map(([key, value]) => [key, normalizeRepetitionValue(value, defaultRepetitions)])),
    fractionalSeed: normalizedRandomizationSeed(settings.fractional_seed ?? protocol.random_seed),
  };
  trialPoolDraftSourceHash = sourceHash;
  trialPoolDraftInitialized = true;
}

function trialPoolFolderGroups() {
  const groups = new Map();
  for (const file of trialPoolFilesFromState()) {
    const folderKey = file.folder_key || file.folder_name || "trial_files";
    if (!groups.has(folderKey)) {
      groups.set(folderKey, {
        folderKey,
        folderName: file.folder_name || folderKey,
        rowFolderName: file.row_folder_name || "",
        rowLabel: file.row_label || "",
        family: file.family || "",
        files: [],
      });
    }
    groups.get(folderKey).files.push(file);
  }
  return [...groups.values()].sort((a, b) => {
    const rowCompare = String(a.rowFolderName).localeCompare(String(b.rowFolderName));
    if (rowCompare) return rowCompare;
    return String(a.folderName).localeCompare(String(b.folderName));
  });
}

function repetitionForFolder(folderKey) {
  const group = trialPoolFolderGroups().find((item) => item.folderKey === folderKey);
  const family = trialPoolFamilyKey(group?.family);
  return normalizeRepetitionValue(
    trialPoolRepetitionDraft.folderRepetitions[folderKey]
      ?? trialPoolRepetitionDraft.familyRepetitions[family]
      ?? trialPoolRepetitionDraft.defaultRepetitions
      ?? 1,
    1
  );
}

function repetitionForFile(file) {
  const fileKey = file.file_key || "";
  if (fileKey && Object.prototype.hasOwnProperty.call(trialPoolRepetitionDraft.fileRepetitionOverrides, fileKey)) {
    return normalizeRepetitionValue(trialPoolRepetitionDraft.fileRepetitionOverrides[fileKey] || 0, 0);
  }
  return repetitionForFolder(file.folder_key || "");
}

function trialPoolCompositionEstimate() {
  const groups = trialPoolFolderGroups();
  const files = trialPoolFilesFromState();
  const records = files.map((file, index) => {
    const configured = repetitionForFile(file);
    const baseRepetitions = Math.floor(configured);
    const fractionalRemainder = normalizeRepetitionValue(configured - baseRepetitions, 0);
    return {
      file,
      index,
      configured,
      baseRepetitions,
      fractionalRemainder,
      fractionalExtra: false,
      balancingStratum: fractionalRemainder ? trialPoolFractionalStratum(file, fractionalRemainder) : "",
      balancingParentStratum: fractionalRemainder ? trialPoolFractionalParentStratum(file, fractionalRemainder) : "",
      sourceLineage: trialPoolSourceLineage(file),
    };
  });
  const warnings = [];
  const fractionalGroups = new Map();
  for (const record of records) {
    if (!record.fractionalRemainder) continue;
    if (!fractionalGroups.has(record.balancingStratum)) fractionalGroups.set(record.balancingStratum, []);
    fractionalGroups.get(record.balancingStratum).push(record);
  }
  const seed = normalizedRandomizationSeed(trialPoolRepetitionDraft.fractionalSeed ?? state?.design?.protocol?.random_seed);
  const strataByParent = new Map();
  for (const [stratum, group] of fractionalGroups.entries()) {
    const parent = group[0]?.balancingParentStratum || stratum;
    if (!strataByParent.has(parent)) strataByParent.set(parent, []);
    strataByParent.get(parent).push(stratum);
  }
  const stratumOffsets = new Map();
  for (const [parent, strata] of strataByParent.entries()) {
    const orderedStrata = [...strata].sort((left, right) => {
      const leftSoa = Number(fractionalGroups.get(left)?.[0]?.file?.soa_ms || 0);
      const rightSoa = Number(fractionalGroups.get(right)?.[0]?.file?.soa_ms || 0);
      return leftSoa - rightSoa || left.localeCompare(right);
    });
    let runningOffset = stableHashInt(`${seed}|${parent}`);
    for (const stratum of orderedStrata) {
      const group = fractionalGroups.get(stratum) || [];
      const expectedExtra = group.length * (group[0]?.fractionalRemainder || 0);
      const extraCount = Math.max(0, Math.min(group.length, Math.floor(expectedExtra + 0.5)));
      stratumOffsets.set(stratum, group.length ? runningOffset % group.length : 0);
      runningOffset += extraCount;
    }
  }
  for (const [stratum, group] of [...fractionalGroups.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    const expectedExtra = group.length * group[0].fractionalRemainder;
    const extraCount = Math.max(0, Math.min(group.length, Math.floor(expectedExtra + 0.5)));
    if (Math.abs(expectedExtra - Math.round(expectedExtra)) > 0.000001) {
      warnings.push(`Fractional split cannot be exact for ${group.length} files in ${stratum}; ${extraCount} extra rows selected.`);
    }
    const ordered = [...group].sort((a, b) => {
      const left = `${a.sourceLineage}|${a.file.sequence_variant_key || a.file.variant_key || ""}|${a.file.source_file_name || ""}|${a.file.file_key || ""}`;
      const right = `${b.sourceLineage}|${b.file.sequence_variant_key || b.file.variant_key || ""}|${b.file.source_file_name || ""}|${b.file.file_key || ""}`;
      return left.localeCompare(right);
    });
    if (!ordered.length || !extraCount) continue;
    const offset = (stratumOffsets.get(stratum) || 0) % ordered.length;
    const rotated = ordered.slice(offset).concat(ordered.slice(0, offset));
    for (const record of rotated.slice(0, extraCount)) {
      record.fractionalExtra = true;
    }
  }
  applyTrialPoolExactFamilyCounts(
    records,
    {
      exact_family_trial_counts: trialPoolExactFamilyCounts(state?.design?.protocol || {}),
      fractional_seed: seed,
    },
    warnings
  );
  const occurrenceByFileKey = new Map();
  for (const record of records) {
    const key = record.file.file_key || `source-${record.index}`;
    occurrenceByFileKey.set(key, record.baseRepetitions + (record.fractionalExtra ? 1 : 0));
  }
  const familyCounts = { audio_tactile: 0, baseline: 0, catch: 0, auditory_only: 0 };
  const familyDurationMs = { audio_tactile: 0, baseline: 0, catch: 0, auditory_only: 0 };
  const subgroupCounts = { audio_tactile: {}, baseline: {}, catch: {}, auditory_only: {} };
  const subgroupDurationMs = { audio_tactile: {}, baseline: {}, catch: {}, auditory_only: {} };
  const folderSummaries = [];
  let totalTrials = 0;
  let totalDurationMs = 0;
  for (const group of groups) {
    let folderTrials = 0;
    let folderDurationMs = 0;
    for (const file of group.files) {
      const reps = occurrenceByFileKey.get(file.file_key || "") ?? 0;
      const durationMs = Math.max(0, Number(file.duration_ms || 0));
      folderTrials += reps;
      folderDurationMs += reps * durationMs;
      if (Object.prototype.hasOwnProperty.call(familyCounts, file.family)) {
        familyCounts[file.family] += reps;
        familyDurationMs[file.family] += reps * durationMs;
        const subgroup = trialPoolSubgroupLabel(file);
        subgroupCounts[file.family][subgroup] = (subgroupCounts[file.family][subgroup] || 0) + reps;
        subgroupDurationMs[file.family][subgroup] = (subgroupDurationMs[file.family][subgroup] || 0) + reps * durationMs;
      }
    }
    totalTrials += folderTrials;
    totalDurationMs += folderDurationMs;
    folderSummaries.push({ ...group, trialCount: folderTrials, durationMs: folderDurationMs });
  }
  const percentages = {};
  for (const family of Object.keys(familyCounts)) {
    percentages[family] = totalTrials ? (100 * familyCounts[family] / totalTrials) : 0;
  }
  const longestFolder = folderSummaries.reduce((best, folder) => (
    !best || folder.durationMs > best.durationMs ? folder : best
  ), null);
  return {
    groups,
    folderSummaries,
    uniqueFiles: trialPoolFilesFromState().length,
    totalTrials,
    familyCounts,
    familyDurationMs,
    percentages,
    subgroupCounts,
    subgroupDurationMs,
    balanceWarnings: warnings,
    totalDurationMs,
    averageDurationMs: totalTrials ? totalDurationMs / totalTrials : 0,
    longestFolder,
  };
}

function renderProtocolSummary() {
  const summary = $("protocol-summary");
  summary.innerHTML = "";
  const composition = trialPoolCompositionEstimate();
  const rows = [
    ["Unique WAVs", composition.uniqueFiles, "total"],
    ["Total trials", composition.totalTrials, "total"],
    ["Audio-tactile", composition.familyCounts.audio_tactile, "audio"],
    ["Baseline", composition.familyCounts.baseline, "baseline"],
    ["Catch", composition.familyCounts.catch, "catch"],
    ["Auditory-only", composition.familyCounts.auditory_only, "auditory"],
  ];
  for (const [label, value, family] of rows) {
    const item = document.createElement("div");
    item.className = `summary-item count-chip ${family}`;
    item.innerHTML = `<span>${label}</span><strong>${value}</strong>`;
    summary.appendChild(item);
  }
  renderTrialPoolFolderCards(composition);
  renderCompositionTree(composition);
}

function updatePercentMixerDisplay(composition = blockCompositionEstimate()) {
  const catchPercent = Math.max(0, Math.min(90, numberValue("catch-percent", 0)));
  const baselinePercent = !baselineStrategyIsActive()
    ? 0
    : Math.max(0, Math.min(90, numberValue("baseline-percent", 0)));
  const audioPercent = Math.max(0, 100 - catchPercent - baselinePercent);
  const setText = (id, text) => {
    const element = $(id);
    if (element) element.textContent = text;
  };
  setText("audio-percent-value", `${audioPercent}%`);
  setText("baseline-percent-value", `${baselinePercent}%`);
  setText("catch-percent-value", `${catchPercent}%`);
  const audioBar = $("audio-percent-bar");
  if (audioBar) audioBar.style.width = `${audioPercent}%`;
  if ($("baseline-percent")) $("baseline-percent").value = String(baselinePercent);
  if ($("catch-percent")) $("catch-percent").value = String(catchPercent);

  const warning = audioPercent <= 0 || composition.totalPerBlock <= 0;
  document.querySelector(".percent-mixer")?.classList.toggle("mix-warning", warning);
}

function compositionTreeRow(kind, label, expression, count) {
  return `
    <div class="tree-branch ${kind}">
      <span class="tree-dot"></span>
      <div>
        <strong>${escapeHtml(label)}</strong>
        <span>${escapeHtml(expression)}</span>
      </div>
      <b>${count}</b>
    </div>
  `;
}

function compositionSliderRow(kind, label, family, composition) {
  const percent = Math.max(0, Math.min(100, Number(composition.percentages[family] || 0)));
  const count = composition.familyCounts[family] || 0;
  const duration = formatDurationMs(composition.familyDurationMs[family] || 0);
  const percentLabel = formatPercent(percent);
  const subgroupOrder = (label) => (/inhale/i.test(label) ? 0 : (/exhale/i.test(label) ? 1 : 2));
  const subRows = Object.entries(composition.subgroupCounts?.[family] || {})
    .sort((a, b) => subgroupOrder(a[0]) - subgroupOrder(b[0]) || a[0].localeCompare(b[0]))
    .map(([subgroup, subgroupCount]) => {
      const totalPercent = composition.totalTrials ? (100 * subgroupCount / composition.totalTrials) : 0;
      const familyPercent = count ? (100 * subgroupCount / count) : 0;
      return `
        <div class="pool-subslider" style="--subslider-value: ${Math.max(0, Math.min(100, totalPercent)).toFixed(3)};">
          <div class="pool-subslider-label">
            <strong>${escapeHtml(subgroup)}</strong>
            <span>${escapeHtml(subgroupCount)} trials</span>
          </div>
          <div class="pool-subslider-track" role="meter" aria-label="${escapeAttr(`${label} ${subgroup}`)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${escapeAttr(totalPercent.toFixed(1))}">
            <span></span>
          </div>
          <div class="pool-subslider-meta">
            <span>${escapeHtml(formatPercent(totalPercent))} total</span>
            <span>${escapeHtml(formatPercent(familyPercent))} family</span>
          </div>
        </div>
      `;
    }).join("");
  return `
    <article class="pool-slider ${kind}" style="--slider-value: ${percent.toFixed(3)};">
      <div class="pool-slider-header">
        <strong>${escapeHtml(label)}</strong>
        <span>${escapeHtml(percentLabel)}</span>
      </div>
      <div class="pool-slider-track"
        role="meter"
        aria-label="${escapeAttr(label)} percentage"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="${escapeAttr(percent.toFixed(1))}">
        <span class="pool-slider-fill"></span>
      </div>
      <div class="pool-slider-meta">
        <span>${escapeHtml(count)} trial${count === 1 ? "" : "s"}</span>
        <span>${escapeHtml(duration)}</span>
      </div>
      ${subRows ? `<div class="pool-subsliders">${subRows}</div>` : ""}
    </article>
  `;
}

function durationCalculusFamilyRow(kind, label, family, composition) {
  const count = composition.familyCounts[family] || 0;
  const totalMs = composition.familyDurationMs[family] || 0;
  const exactFormula = count
    ? `sum(duration_ms x reps) = ${formatMillis(totalMs)}`
    : `sum(duration_ms x reps) = 0 ms`;
  return `
    <div class="duration-calculus-row ${kind}">
      <span>${escapeHtml(label)}</span>
      <code>${escapeHtml(exactFormula)}</code>
      <strong>${escapeHtml(formatDurationMs(totalMs))}</strong>
    </div>
  `;
}

function renderTrialPoolDurationCalculus(composition) {
  const container = $("trial-pool-duration-calculus");
  if (!container) return;
  const total = formatDurationMs(composition.totalDurationMs);
  const average = composition.totalTrials ? formatDurationMs(composition.averageDurationMs) : "0s";
  container.innerHTML = `
    <div class="duration-calculus-heading">
      <strong>Duration Calculus</strong>
      <span>${escapeHtml(total)}</span>
    </div>
    ${durationCalculusFamilyRow("audio", "Audio-tactile", "audio_tactile", composition)}
    ${durationCalculusFamilyRow("baseline", "Baseline", "baseline", composition)}
    ${durationCalculusFamilyRow("catch", "Catch", "catch", composition)}
    ${durationCalculusFamilyRow("auditory", "Auditory-only", "auditory_only", composition)}
    <div class="duration-calculus-total">
      <span>Total</span>
      <code>${escapeHtml(formatDurationMs(composition.familyDurationMs.audio_tactile || 0))} + ${escapeHtml(formatDurationMs(composition.familyDurationMs.baseline || 0))} + ${escapeHtml(formatDurationMs(composition.familyDurationMs.catch || 0))} + ${escapeHtml(formatDurationMs(composition.familyDurationMs.auditory_only || 0))}</code>
      <strong>${escapeHtml(total)}</strong>
    </div>
    <div class="duration-calculus-total muted-total">
      <span>Average trial</span>
      <code>${escapeHtml(formatMillis(composition.totalDurationMs))} / ${composition.totalTrials || 0} trials</code>
      <strong>${escapeHtml(average)}</strong>
    </div>
  `;
}

function familyDisplayName(family) {
  return {
    audio_tactile: "Audio-tactile",
    baseline: "Baseline",
    catch: "Catch",
    auditory_only: "Auditory-only",
  }[family] || humanize(family || "trial");
}

function renderTrialPoolFolderCards(composition = trialPoolCompositionEstimate()) {
  const container = $("trial-pool-folder-list");
  if (!container) return;
  if (!composition.groups.length) {
    container.innerHTML = `<div class="summary-text">Bake Segment 3 trial files before assigning repetitions.</div>`;
    return;
  }
  container.innerHTML = composition.groups.map((group) => {
    const folderReps = repetitionForFolder(group.folderKey);
    const fileRows = group.files.map((file) => {
      const fileReps = repetitionForFile(file);
      const inherited = !Object.prototype.hasOwnProperty.call(trialPoolRepetitionDraft.fileRepetitionOverrides, file.file_key || "");
      return `
        <tr>
          <td data-label="WAV">${escapeHtml(file.source_file_name || "")}</td>
          <td data-label="Duration">${formatDurationMs(file.duration_ms || 0)}</td>
          <td data-label="SOA">${escapeHtml(file.soa_ms ?? "")}</td>
          <td data-label="Reps">
            <input data-trial-pool-file="${escapeAttr(file.file_key || "")}" type="number" min="0" max="1000" step="0.5" value="${escapeAttr(formatRepetitionValue(fileReps))}" aria-label="File repetitions">
            <span class="muted">${inherited ? "folder" : "custom"}</span>
          </td>
        </tr>
      `;
    }).join("");
    const rowLabel = group.rowLabel || group.rowFolderName || "Row";
    const folderSummary = composition.folderSummaries.find((item) => item.folderKey === group.folderKey) || {};
    return `
      <article class="trial-pool-folder-card ${escapeAttr(group.family || "")}">
        <div class="trial-pool-folder-header">
          <div>
            <strong>${escapeHtml(familyDisplayName(group.family))}</strong>
            <span>${escapeHtml(rowLabel)} / ${escapeHtml(group.folderName)}</span>
          </div>
          <label>
            <span>Reps</span>
            <input data-trial-pool-folder="${escapeAttr(group.folderKey)}" type="number" min="0" max="1000" step="0.5" value="${escapeAttr(formatRepetitionValue(folderReps))}">
          </label>
        </div>
        <div class="trial-pool-folder-metrics">
          <span>${group.files.length} WAVs</span>
          <span>${folderSummary.trialCount || 0} trials</span>
          <span>${formatDurationMs(folderSummary.durationMs || 0)}</span>
        </div>
        <details class="trial-pool-file-details">
          <summary>Advanced file-level overrides</summary>
          <table class="data-table compact mobile-card-table">
            <thead><tr><th>WAV</th><th>Duration</th><th>SOA</th><th>Reps</th></tr></thead>
            <tbody>${fileRows}</tbody>
          </table>
        </details>
      </article>
    `;
  }).join("");
}

function currentSoaValues() {
  return parseIntegerList($("block-soa-values")?.value || $("soa-values")?.value || "");
}

function renderCompositionTree(composition, context = {}) {
  const tree = $("composition-tree");
  if (!tree) return;
  const balanceMessage = composition.balanceWarnings?.length
    ? composition.balanceWarnings[0]
    : "Half-step repetitions use deterministic row/SOA/source-lineage balancing.";
  const balanceClass = composition.balanceWarnings?.length ? "warning" : "ready";
  tree.innerHTML = `
    ${compositionSliderRow("audio", "Audio-tactile", "audio_tactile", composition)}
    ${compositionSliderRow("baseline", "Baseline", "baseline", composition)}
    ${compositionSliderRow("catch", "Catch", "catch", composition)}
    ${compositionSliderRow("auditory", "Auditory-only", "auditory_only", composition)}
    <div class="balance-note ${balanceClass}">${escapeHtml(balanceMessage)}</div>
  `;
  renderTrialPoolDurationCalculus(composition);
}

function sequenceVariantEstimate() {
  const rows = [...$("filmstrip-list").querySelectorAll(".filmstrip-row")];
  if (!rows.length) return { rowCount: 0, variantCount: 0, rowVariants: [] };
  const rowVariants = rows.map((row, index) => {
    const label = row.querySelector('[data-strip-field="label"]')?.value.trim() || `Trial type ${index + 1}`;
    return { label, count: rowSourceCount(row) };
  });
  return {
    rowCount: rows.length,
    variantCount: rowVariants.reduce((total, row) => total + row.count, 0),
    rowVariants,
  };
}

function baselineAnchorSpecsFromInputs() {
  const strategy = currentBaselineStrategy();
  const soaValues = currentSoaValues();
  if (!baselineStrategyIsActive(strategy)) return [];
  if (strategy === "min_anchor") {
    return soaValues.length ? [{ label: "minimum", soa_ms: soaValues[0], mode: "audio_tactile" }] : [];
  }
  if (strategy === "max_anchor") {
    return soaValues.length ? [{ label: "maximum", soa_ms: soaValues[soaValues.length - 1], mode: "audio_tactile" }] : [];
  }
  if (strategy === "min_max") {
    return Array.from(new Set([soaValues[0], soaValues[soaValues.length - 1]].filter((value) => Number.isFinite(value))))
      .map((soa, index) => ({ label: index === 0 ? "minimum" : "maximum", soa_ms: soa, mode: "audio_tactile" }));
  }
  if (strategy === "tactile_only") {
    const customSoas = parseIntegerList($("baseline-soa-values").value);
    const values = customSoas.length ? customSoas : soaValues;
    const label = customSoas.length ? "custom tactile-only" : "full SOA tactile-only";
    return values.map((soa) => ({ label, soa_ms: soa, mode: "tactile_only" }));
  }
  if (strategy === "stationary_burst") {
    const customSoas = parseIntegerList($("baseline-soa-values").value);
    const values = customSoas.length ? customSoas : soaValues;
    const label = customSoas.length ? "custom stationary burst" : "full SOA stationary burst";
    return values.map((soa) => ({ label, soa_ms: soa, mode: "stationary_burst" }));
  }
  if (strategy === "custom") {
    const mode = $("baseline-custom-audio-tactile")?.checked ? "audio_tactile" : "tactile_only";
    return parseIntegerList($("baseline-soa-values").value).map((soa) => ({ label: "custom", soa_ms: soa, mode }));
  }
  return derivedBaselineTimingsMs(strategy).map((soa) => ({ label: strategy, soa_ms: soa, mode: "audio_tactile" }));
}

function renderBaselineTactileSummary() {
  const strip = $("baseline-count-strip");
  const tree = $("baseline-factor-tree");
  if (!strip || !tree) return;

  const sequence = sequenceVariantEstimate();
  const soaValues = currentSoaValues();
  const anchors = baselineAnchorSpecsFromInputs();
  const includeCatch = $("include-catch-trials")?.checked || false;
  const includeAuditoryOnly = $("include-auditory-only-trials")?.checked || false;
  const audioFiles = sequence.variantCount * soaValues.length;
  const baselineFiles = sequence.variantCount * anchors.length;
  const catchFiles = includeCatch ? sequence.variantCount : 0;
  const auditoryOnlyFiles = includeAuditoryOnly ? sequence.variantCount : 0;
  const folderCount = sequence.rowCount * (audioFiles ? 1 : 0) + sequence.rowCount * (baselineFiles ? 1 : 0) + sequence.rowCount * (catchFiles ? 1 : 0) + sequence.rowCount * (auditoryOnlyFiles ? 1 : 0);

  const rows = [
    ["Row variants", sequence.variantCount, "total"],
    ["SOAs", soaValues.length, "audio"],
    ["Audio-tactile files", audioFiles, "audio"],
    ["Baseline anchors", anchors.length, "baseline"],
    ["Baseline files", baselineFiles, "baseline"],
    ["Catch files", catchFiles, "catch"],
    ["Auditory-only files", auditoryOnlyFiles, "auditory"],
    ["Output folders", folderCount, "total"],
  ];
  strip.innerHTML = rows.map(([label, value, family]) => `
    <div class="summary-item count-chip ${family}">
      <span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong>
    </div>
  `).join("");

  const strategy = currentBaselineStrategy();
  const strategyNote = BASELINE_STRATEGY_NOTES[strategy] || BASELINE_STRATEGY_NOTES[""];
  const rowLabel = sequence.rowVariants.length
    ? sequence.rowVariants.map((row) => `${row.label}: ${row.count}`).join("; ")
    : "No trial-sequence rows yet";
  const audioExpression = `${sequence.variantCount} row variant${sequence.variantCount === 1 ? "" : "s"} x ${soaValues.length} SOA${soaValues.length === 1 ? "" : "s"}`;
  const baselineExpression = baselineStrategyIsActive(strategy)
    ? `${anchors.length} anchor${anchors.length === 1 ? "" : "s"}; ${strategyNote.note}`
    : (strategy === "none" ? "No baseline files will be created" : "Choose baseline handling");
  const catchExpression = includeCatch
    ? `${sequence.variantCount} row variant${sequence.variantCount === 1 ? "" : "s"} copied from Segment 2 audio`
    : "Catch file generation disabled";
  const auditoryOnlyExpression = includeAuditoryOnly
    ? `${sequence.variantCount} response-required audio-only variant${sequence.variantCount === 1 ? "" : "s"}`
    : "Auditory-only file generation disabled";
  tree.innerHTML = `
    <div class="tree-root">
      <strong>Segment 3 trial files</strong>
      <span>${escapeHtml(rowLabel)}</span>
    </div>
    ${compositionTreeRow("audio", "Audio-tactile files", audioExpression, audioFiles)}
    ${compositionTreeRow("baseline", "Baseline files", baselineExpression, baselineFiles)}
    ${compositionTreeRow("catch", "Catch files", catchExpression, catchFiles)}
    ${compositionTreeRow("auditory", "Auditory-only files", auditoryOnlyExpression, auditoryOnlyFiles)}
  `;

  const bake = state.trial_file_bake || {};
  const ready = Boolean(bake.manifest_exists);
  const status = $("trial-file-bake-status");
  if (status) {
    const total = Number(bake.total_count || 0);
    status.textContent = ready ? `${total} files` : "waiting";
    status.className = `status-label ${ready ? "ready" : ""}`;
  }
  const openButton = $("open-trial-file-folder");
  if (openButton) {
    openButton.disabled = !bake.root;
    openButton.dataset.folderPath = bake.root || "";
  }
}

function renderRowMixOverrides(composition = blockCompositionEstimate()) {
  const container = $("row-mix-overrides");
  if (!container) return;
  if (container.contains(document.activeElement) && document.activeElement?.matches?.("[data-row-mix-index][data-row-mix-field]")) {
    return;
  }
  const rows = [...$("filmstrip-list").querySelectorAll(".filmstrip-row")];
  if (!rows.length) {
    container.innerHTML = `<div class="summary-text">Add a trial-sequence row to override its block composition.</div>`;
    return;
  }
  const body = rows.map((row, index) => {
    const label = row.querySelector('[data-strip-field="label"]')?.value.trim() || `Trial type ${index + 1}`;
    const counts = composition.rows[index] || rowCompositionCounts(row);
    const mix = counts.mix;
    return `
      <tr>
        <td data-label="Row">${escapeHtml(label)}</td>
        <td data-label="Audio %"><input aria-label="${escapeAttr(label)} audio percentage" data-row-mix-index="${index}" data-row-mix-field="audio_tactile_percentage" type="number" min="0" max="100" step="1" value="${escapeAttr(mix.audioTactile)}"></td>
        <td data-label="Baseline %"><input aria-label="${escapeAttr(label)} baseline percentage" data-row-mix-index="${index}" data-row-mix-field="baseline_percentage" type="number" min="0" max="100" step="1" value="${escapeAttr(mix.baseline)}"></td>
        <td data-label="Catch %"><input aria-label="${escapeAttr(label)} catch percentage" data-row-mix-index="${index}" data-row-mix-field="catch_percentage" type="number" min="0" max="100" step="1" value="${escapeAttr(mix.catch)}"></td>
        <td data-label="Trials / block">${counts.totalPerBlock}</td>
      </tr>
    `;
  }).join("");
  container.innerHTML = `
    <table class="data-table compact mobile-card-table">
      <thead>
        <tr>
          <th>Row</th>
          <th>Audio %</th>
          <th>Baseline %</th>
          <th>Catch %</th>
          <th>Trials / block</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
  `;
}

function renderBaseline() {
  const protocol = state.design.protocol || {};
  const savedStrategy = protocol.baseline_strategy === ""
    ? ""
    : (protocol.include_baseline_trials === false ? "none" : (protocol.baseline_strategy || "tactile_only"));
  $("catch-percent").value = protocol.catch_trial_percentage ?? 0;
  if ($("include-catch-trials")) $("include-catch-trials").checked = Boolean(protocol.include_catch_trials);
  if ($("include-auditory-only-trials")) {
    $("include-auditory-only-trials").checked = Boolean(protocol.include_auditory_only_trials);
  }
  $("baseline-percent").value = !savedStrategy || savedStrategy === "none" ? 0 : (protocol.baseline_trial_percentage ?? 0);
  $("baseline-soa-values").value = formatList(protocol.baseline_soa_values_ms || []);
  $("baseline-custom-audio-tactile").checked = protocol.baseline_custom_trial_mode === "audio_tactile";
  setBaselineStrategy(savedStrategy);
  updateBaselineDecision();
  renderBaselineTactileSummary();
}

function currentBaselineStrategy() {
  return $("baseline-strategy").value || "";
}

function baselineStrategyIsActive(strategy = currentBaselineStrategy()) {
  return Boolean(strategy && strategy !== "none");
}

function baselineOptionInputs() {
  return [...document.querySelectorAll('input[name="baseline-option"]')];
}

function setBaselineStrategy(strategy) {
  const nextStrategy = strategy || "";
  $("baseline-strategy").value = nextStrategy;
  if ($("baseline-strategy").value !== nextStrategy) $("baseline-strategy").value = "none";
  $("baseline-enabled").checked = Boolean($("baseline-strategy").value && $("baseline-strategy").value !== "none");
  syncBaselineStrategyControls();
}

function syncBaselineStrategyControls() {
  const strategy = $("baseline-strategy").value || "";
  $("baseline-strategy").value = strategy;
  $("baseline-enabled").checked = Boolean(strategy && strategy !== "none");
  for (const input of baselineOptionInputs()) {
    input.disabled = false;
    input.checked = baselineOptionCheckedForStrategy(input.value, strategy);
    input.closest(".baseline-option-card")?.classList.toggle("active", input.checked);
    input.closest(".baseline-option-card")?.classList.remove("disabled");
  }
  if ($("baseline-custom-controls")) $("baseline-custom-controls").hidden = strategy !== "custom";
}

function baselineOptionCheckedForStrategy(value, strategy) {
  return Boolean(strategy) && value === strategy;
}

function strategyFromBaselineOptions(changedInput = null) {
  if (changedInput?.checked) return changedInput.value || "";
  return baselineOptionInputs().find((input) => input.checked)?.value || "";
}

function derivedBaselineTimingsMs(strategy = currentBaselineStrategy()) {
  if (strategy === "none" || !strategy) return [];
  const soaValues = currentSoaValues();
  if (strategy === "min_anchor") {
    return soaValues.length ? [soaValues[0]] : [];
  }
  if (strategy === "max_anchor") {
    return soaValues.length ? [soaValues[soaValues.length - 1]] : [];
  }
  if (strategy === "min_max") {
    if (!soaValues.length) return [];
    return Array.from(new Set([soaValues[0], soaValues[soaValues.length - 1]]));
  }
  if (strategy === "soa_zero") return [0];
  if (strategy === "sound_offset") {
    const controls = currentTrajectoryControls();
    return [Math.round((controls.start_hold_s + controls.movement_duration_s + controls.end_hold_s) * 1000)];
  }
  const custom = parseIntegerList($("baseline-soa-values").value);
  if (custom.length) return custom;
  if (strategy === "custom") return [];
  return soaValues;
}

function enforceGlobalPercentBounds(changedId = "") {
  const maxExtra = 99;
  let catchPercent = Math.max(0, Math.min(90, numberValue("catch-percent", 0)));
  let baselinePercent = !baselineStrategyIsActive()
    ? 0
    : Math.max(0, Math.min(90, numberValue("baseline-percent", 0)));
  if (catchPercent + baselinePercent > maxExtra) {
    if (changedId === "baseline-percent") {
      catchPercent = Math.max(0, maxExtra - baselinePercent);
    } else {
      baselinePercent = Math.max(0, maxExtra - catchPercent);
    }
  }
  $("catch-percent").value = String(catchPercent);
  $("baseline-percent").value = String(baselinePercent);
}

function eventSequenceAudioCountPerBlock() {
  return blockCompositionEstimate().audioPerBlock;
}

function tactileSiteCount() {
  return Math.max(1, (state.design.protocol?.tactile_sites || ["hand"]).length);
}

function rowSourceCount(row) {
  let count = 1;
  for (const card of row.querySelectorAll(".filmstrip-element")) {
    if (card.dataset.elementKind === "jitter") {
      count *= Math.max(0, parseIntegerList(card.querySelector('[data-element-field="jitter_values_ms"]')?.value).length);
    } else {
      const hidden = card.querySelectorAll('input[data-element-field="source_labels"][type="hidden"]');
      const checked = card.querySelectorAll('input[data-element-field="source_labels"]:checked');
      const selectedOptions = card.querySelectorAll('select[data-element-field="source_labels"] option:checked');
      count *= Math.max(0, hidden.length || checked.length || selectedOptions.length);
    }
  }
  return count;
}

function rowAudioCountPerBlock(row) {
  const soaCount = currentSoaValues().length;
  const repetitions = Math.max(1, Math.round(numberValue("repetitions", 1)));
  return rowSourceCount(row) * Math.max(soaCount, 0) * repetitions * tactileSiteCount();
}

function defaultRowMix() {
  const catchPercent = Math.max(0, Math.min(99, numberValue("catch-percent", 0)));
  const baselinePercent = !baselineStrategyIsActive()
    ? 0
    : Math.max(0, Math.min(99, numberValue("baseline-percent", 0)));
  return {
    audioTactile: Math.max(1, 100 - catchPercent - baselinePercent),
    catch: catchPercent,
    baseline: baselinePercent,
  };
}

function rowMixFromRow(row) {
  const defaults = defaultRowMix();
  const field = (name) => row.querySelector(`[data-strip-field="${name}"]`);
  const catchValue = field("catch_percentage")?.value;
  const baselineValue = !baselineStrategyIsActive() ? 0 : field("baseline_percentage")?.value;
  const audioValue = field("audio_tactile_percentage")?.value;
  const catchPercent = catchValue === undefined || catchValue === ""
    ? defaults.catch
    : Math.max(0, Math.min(100, Number(catchValue || 0)));
  const baselinePercent = baselineValue === undefined || baselineValue === ""
    ? defaults.baseline
    : Math.max(0, Math.min(100, Number(baselineValue || 0)));
  const audioTactilePercent = audioValue === undefined || audioValue === ""
    ? Math.max(0, 100 - catchPercent - baselinePercent)
    : Math.max(0, Math.min(100, Number(audioValue || 0)));
  return {
    audioTactile: audioTactilePercent,
    catch: catchPercent,
    baseline: baselinePercent,
    total: audioTactilePercent + catchPercent + baselinePercent,
  };
}

function applyBaselineDefaultToTrialRows() {
  const catchPercent = Math.max(0, Math.min(99, numberValue("catch-percent", 0)));
  const baselinePercent = !baselineStrategyIsActive()
    ? 0
    : Math.max(0, Math.min(99, numberValue("baseline-percent", 0)));
  for (const row of $("filmstrip-list").querySelectorAll(".filmstrip-row")) {
    const catchField = row.querySelector('[data-strip-field="catch_percentage"]');
    const baselineField = row.querySelector('[data-strip-field="baseline_percentage"]');
    const audioField = row.querySelector('[data-strip-field="audio_tactile_percentage"]');
    if (!baselineField || !audioField) continue;
    const rowCatch = catchField?.dataset.rowMixOverride === "true"
      ? Math.max(0, Math.min(99, Number(catchField.value || 0)))
      : catchPercent;
    const rowBaseline = baselineField.dataset.rowMixOverride === "true"
      ? Math.max(0, Math.min(99, Number(baselineField.value || 0)))
      : baselinePercent;
    if (catchField && catchField.dataset.rowMixOverride !== "true") catchField.value = String(rowCatch);
    if (baselineField.dataset.rowMixOverride !== "true") baselineField.value = String(rowBaseline);
    if (audioField.dataset.rowMixOverride !== "true") audioField.value = String(Math.max(1, 100 - rowCatch - rowBaseline));
  }
}

function syncCompositionSoaInputs(sourceInput) {
  const value = sourceInput?.value ?? $("soa-values").value;
  $("soa-values").value = value;
  if ($("block-soa-values") && $("block-soa-values") !== sourceInput) {
    $("block-soa-values").value = value;
  }
}

function rowExtraCount(audioCount, extraPercent, audioPercent) {
  if (audioCount <= 0 || extraPercent <= 0) return 0;
  return Math.ceil(audioCount * extraPercent / Math.max(0.1, audioPercent));
}

function rowCompositionCounts(row) {
  const audioPerBlock = rowAudioCountPerBlock(row);
  const mix = rowMixFromRow(row);
  const catchPerBlock = rowExtraCount(audioPerBlock, mix.catch, mix.audioTactile);
  const baselinePerBlock = !baselineStrategyIsActive()
    ? 0
    : rowExtraCount(audioPerBlock, mix.baseline, mix.audioTactile);
  return {
    mix,
    audioPerBlock,
    catchPerBlock,
    baselinePerBlock,
    totalPerBlock: audioPerBlock + catchPerBlock + baselinePerBlock,
  };
}

function blockCompositionEstimate() {
  const rows = [...$("filmstrip-list").querySelectorAll(".filmstrip-row")];
  if (!rows.length) {
    const soaCount = parseIntegerList($("soa-values").value).length;
    const repetitions = Math.max(1, Math.round(numberValue("repetitions", 1)));
    const audioPerBlock = Math.max(1, stimulusSourceDetailsFromDom().length) * Math.max(soaCount, 0) * repetitions * tactileSiteCount();
    const mix = defaultRowMix();
    const catchPerBlock = rowExtraCount(audioPerBlock, mix.catch, mix.audioTactile);
    const baselinePerBlock = !baselineStrategyIsActive()
      ? 0
      : rowExtraCount(audioPerBlock, mix.baseline, mix.audioTactile);
    return {
      audioPerBlock,
      catchPerBlock,
      baselinePerBlock,
      totalPerBlock: audioPerBlock + catchPerBlock + baselinePerBlock,
      rows: [],
    };
  }
  const composition = {
    audioPerBlock: 0,
    catchPerBlock: 0,
    baselinePerBlock: 0,
    totalPerBlock: 0,
    rows: [],
  };
  for (const row of rows) {
    const rowCounts = rowCompositionCounts(row);
    composition.audioPerBlock += rowCounts.audioPerBlock;
    composition.catchPerBlock += rowCounts.catchPerBlock;
    composition.baselinePerBlock += rowCounts.baselinePerBlock;
    composition.totalPerBlock += rowCounts.totalPerBlock;
    composition.rows.push(rowCounts);
  }
  return composition;
}

function baselineCountEstimate() {
  const strategy = currentBaselineStrategy();
  const blocks = Math.max(1, Math.round(numberValue("blocks", 1)));
  const composition = blockCompositionEstimate();
  if (!strategy || strategy === "none") {
    return { strategy, timings: [], perBlock: 0, total: 0, actualPercent: 0, audioPerBlock: composition.audioPerBlock, composition };
  }
  const timings = derivedBaselineTimingsMs(strategy);
  const perBlock = composition.baselinePerBlock;
  const denominator = Math.max(1, composition.totalPerBlock);
  return {
    strategy,
    timings,
    perBlock,
    total: perBlock * blocks,
    actualPercent: Math.round((1000 * perBlock / denominator)) / 10,
    audioPerBlock: composition.audioPerBlock,
    composition,
  };
}

function estimatedTrialSeconds() {
  const controls = currentTrajectoryControls();
  const soundWindow = controls.start_hold_s + controls.movement_duration_s + controls.end_hold_s;
  const firstStrip = state.design.protocol?.trial_strips?.[0];
  const fixedLabels = (firstStrip?.elements || [])
    .filter((element) => element.kind === "fixed_audio")
    .map((element) => element.source_label || element.label)
    .filter(Boolean);
  const fixedSeconds = fixedLabels.reduce((total, label) => {
    const clip = (state.design.prestimulus_files || []).find((item) => item.label === label);
    return total + Number(clip?.target_duration_s || 0);
  }, 0);
  return Math.max(0.1, fixedSeconds + soundWindow);
}

function updateBaselineDecision() {
  syncBaselineStrategyControls();
  const strategy = currentBaselineStrategy();
  const status = $("baseline-status");
  const soaValues = currentSoaValues();
  const customValues = parseIntegerList($("baseline-soa-values").value);
  const valid = Boolean(strategy) && soaValues.length > 0 && (strategy !== "custom" || customValues.length > 0);
  if (status) {
    status.textContent = valid ? "ready" : "required";
    status.className = `status-label ${valid ? "ready" : "required"}`;
  }
  if (strategy === "none") $("baseline-percent").value = 0;
  const timingSummary = $("tactile-timing-summary");
  if (timingSummary) {
    const timingText = soaValues.length
      ? `${soaValues.length} tactile timing${soaValues.length === 1 ? "" : "s"}: ${soaValues.join(", ")} ms`
      : "Enter at least one tactile timing.";
    const familyNotes = [
      $("include-catch-trials")?.checked ? "catch included" : "no catch trials",
      $("include-auditory-only-trials")?.checked ? "auditory-only included" : "no auditory-only trials",
    ];
    timingSummary.innerHTML = `<span><strong>Tactile channel 3</strong> ${escapeHtml(timingText)}</span><span>${escapeHtml(familyNotes.join(" · "))}</span>`;
    timingSummary.classList.toggle("required", !soaValues.length);
  }
  enforceGlobalPercentBounds("baseline-percent");
  updatePercentMixerDisplay();
  renderBaselineTactileSummary();
}

function renderTrialStrips() {
  const list = $("filmstrip-list");
  if (!list) return;
  const strips = state.design.protocol?.trial_strips || [];
  list.innerHTML = "";
  if (!strips.length) {
    list.appendChild(renderAddRowControl(-1, true));
    updateFilmstripCounts();
    return;
  }
  strips.forEach((strip, index) => {
    list.appendChild(renderTrialStripRow(strip, index));
    list.appendChild(renderAddRowControl(index, false));
  });
  updateFilmstripCounts();
}

function stripMixValues(strip = {}) {
  const protocol = state.design.protocol || {};
  const catchPercent = strip.catch_percentage ?? protocol.catch_trial_percentage ?? 0;
  const baselineFallback = protocol.include_baseline_trials === false
    ? 0
    : (protocol.baseline_trial_percentage ?? 0);
  const baselinePercent = strip.baseline_percentage ?? baselineFallback;
  const audioTactilePercent = strip.audio_tactile_percentage ?? Math.max(0, 100 - catchPercent - baselinePercent);
  return {
    audioTactile: Math.round(Number(audioTactilePercent || 0) * 10) / 10,
    catch: Math.round(Number(catchPercent || 0) * 10) / 10,
    baseline: Math.round(Number(baselinePercent || 0) * 10) / 10,
  };
}

function renderTrialStripRow(strip, index, options = {}) {
  const placeholder = Boolean(options.placeholder);
  const elements = strip.elements || [];
  const row = document.createElement("div");
  const mix = stripMixValues(strip);
  row.className = `filmstrip-row ${placeholder ? "empty-sequence-row" : ""}`;
  row.dataset.stripIndex = String(index);
  row.innerHTML = `
    ${placeholder ? "" : `
    <div class="filmstrip-row-header">
      <button type="button" class="filmstrip-preview-button" data-preview-strip="${index}" title="Prelisten trial type" aria-label="Prelisten trial type">&#9658;</button>
      <div class="filmstrip-row-actions">
        <button type="button" class="icon-action" data-strip-move="up" title="Move row up" aria-label="Move row up">^</button>
        <button type="button" class="icon-action" data-strip-move="down" title="Move row down" aria-label="Move row down">v</button>
        <button type="button" class="icon-action danger" data-remove-strip title="Remove trial sequence row" aria-label="Remove trial sequence row">x</button>
      </div>
    </div>`}
    <div class="filmstrip-row-body">
      <div class="field-row trial-condition-field">
        <label>Condition label</label>
        <input data-strip-field="label" value="${escapeAttr(strip.label || `Trial type ${index + 1}`)}">
      </div>
      <div class="filmstrip-row-summary" aria-live="polite">
        <span class="filmstrip-row-expression">${escapeHtml(trialStripExpression(strip))}</span>
        <span class="status-label optional filmstrip-row-variant-count">${stripPreviewVariants(strip).length} ${stripPreviewVariants(strip).length === 1 ? "variant" : "variants"}</span>
      </div>
      <div class="filmstrip-sequence"></div>
    </div>
    <div class="filmstrip-row-mix state-only" aria-label="Trial type row composition">
      <div class="field-row">
        <label>Audio-tactile %</label>
        <input data-strip-field="audio_tactile_percentage" type="number" min="0" max="100" step="1" value="${escapeAttr(mix.audioTactile)}">
      </div>
      <div class="field-row">
        <label>Catch %</label>
        <input data-strip-field="catch_percentage" type="number" min="0" max="100" step="1" value="${escapeAttr(mix.catch)}">
      </div>
      <div class="field-row">
        <label>Baseline %</label>
        <input data-strip-field="baseline_percentage" type="number" min="0" max="100" step="1" value="${escapeAttr(mix.baseline)}">
      </div>
    </div>
  `;
  const sequence = row.querySelector(".filmstrip-sequence");
  for (const [elementIndex, element] of elements.entries()) {
    sequence.appendChild(renderTrialStripElement(element, elementIndex));
    sequence.appendChild(renderAddEventControl(index, elementIndex + 1, strip));
  }
  if (!elements.length) {
    sequence.appendChild(renderAddEventControl(index, 0, strip, true));
  }
  return row;
}

function renderTrialStripElement(element, index) {
  const sourceType = element.kind === "fixed_audio" || element.kind === "jitter"
    ? element.kind
    : "looming_stimulus";
  const card = document.createElement("div");
  card.className = `filmstrip-element sequence-event ${sourceType.replace("_", "-")} ${filmstripNoiseClass(element)}`;
  card.dataset.elementIndex = String(index);
  card.dataset.elementKind = sourceType;
  if (sourceType === "fixed_audio") {
    card.innerHTML = renderFixedBlock(element);
  } else if (sourceType === "jitter") {
    card.innerHTML = renderJitterBlock(element);
  } else {
    card.innerHTML = renderAudioPoolBlock(element);
  }
  return card;
}

function renderAddEventControl(rowIndex, insertAfter, strip = {}, isEmpty = false) {
  const wrapper = document.createElement("div");
  wrapper.className = `sequence-event-add ${isEmpty ? "empty-row-add" : ""}`;
  wrapper.innerHTML = `
    <button type="button" class="sequence-event-add-symbol with-label" data-add-strip-element="fixed_audio" data-insert-after="${insertAfter}" title="Add sequence box" aria-label="Add sequence box"><span aria-hidden="true">+</span> ${isEmpty ? "Add first box" : "Add box"}</button>
  `;
  return wrapper;
}

function renderAddRowControl(insertAfter = -1, isEmpty = false) {
  const wrapper = document.createElement("div");
  wrapper.className = `trial-row-add ${isEmpty ? "trial-row-empty" : "between-row-add"}`;
  wrapper.innerHTML = `
    <button type="button" class="sequence-event-add-symbol with-label" data-add-strip-row data-insert-row-after="${insertAfter}" title="Add trial family" aria-label="Add trial family"><span aria-hidden="true">+</span> ${isEmpty ? "Add first trial family" : "Add trial family"}</button>
  `;
  return wrapper;
}

function renderFixedBlock(element) {
  return renderAudioPoolBlock(element);
}

function audioBoxLabels(element = {}) {
  const labels = (element.source_labels || []).filter(Boolean);
  if (!labels.length && element.source_label) labels.push(element.source_label);
  const fallback = String(element.label || "").trim();
  if (!labels.length && fallback && !genericAudioBoxLabel(fallback)) labels.push(fallback);
  return Array.from(new Set(labels));
}

function trialStripExpression(strip = {}) {
  const expressions = (strip.elements || []).map((element) => {
    if (element.kind === "jitter") {
      const values = (element.jitter_values_ms || []).filter((value) => Number.isFinite(Number(value)));
      return values.length ? `Jitter (${values.join(" | ")} ms)` : "Jitter (add a duration)";
    }
    const labels = audioBoxLabels(element);
    if (!labels.length) return "Empty audio box";
    return labels.length === 1 ? labels[0] : `(${labels.join(" | ")})`;
  });
  return expressions.length ? expressions.join(" → ") : "Add an audio or jitter box to define this trial family.";
}

function genericAudioBoxLabel(label = "") {
  return new Set(["Audio box", "Looming Stimulus", "Fixed audio clip", "Audio pool"]).has(String(label || "").trim());
}

function renderAudioPoolBlock(element) {
  const labels = audioBoxLabels(element);
  const title = "Audio box";
  return `
    <div class="filmstrip-element-heading">
      <span>${title}</span>
      <button type="button" class="icon-action danger" data-remove-strip-element title="Remove event" aria-label="Remove event">x</button>
    </div>
    <label class="box-mode-toggle">
      <input data-element-field="is_jitter" type="checkbox">
      Jitter / ITI box
    </label>
    <div class="sequence-label-list" data-sequence-label-list>
      ${labels.map((label) => `
        <span class="sequence-label-chip">
          <span class="sequence-label-chip-text">${escapeHtml(label)}</span>
          <span class="sequence-label-chip-actions">
            <button type="button" class="sequence-label-preview" data-preview-source-label="${escapeAttr(label)}" title="Play ${escapeAttr(label)}" aria-label="Play ${escapeAttr(label)}">&#128266;</button>
            <button type="button" class="sequence-label-remove" data-remove-box-label="${escapeAttr(label)}" title="Remove label" aria-label="Remove ${escapeAttr(label)}">x</button>
          </span>
          <input data-element-field="source_labels" type="hidden" value="${escapeAttr(label)}">
        </span>
      `).join("") || `<span class="sequence-label-empty">No labels selected</span>`}
    </div>
    <div class="box-add-label-row">
      <select data-element-field="label_picker">${sequenceAudioOptions("")}</select>
      <button type="button" data-add-box-label>Add</button>
    </div>
    <input data-element-field="label" type="hidden" value="${escapeAttr(element.label || title)}">
    <input data-element-field="randomized" type="hidden" value="true">
  `;
}

function renderJitterBlock(element) {
  const title = "Jitter / ITI event";
  const values = formatList(element.jitter_values_ms || []);
  return `
    <div class="filmstrip-element-heading">
      <span>${title}</span>
      <button type="button" class="icon-action danger" data-remove-strip-element title="Remove event" aria-label="Remove event">x</button>
    </div>
    <label class="box-mode-toggle">
      <input data-element-field="is_jitter" type="checkbox" checked>
      Jitter / ITI box
    </label>
    <div class="field-row">
      <label>Jitter values ms</label>
      <textarea data-element-field="jitter_values_ms" rows="5" autocomplete="off" placeholder="500&#10;700&#10;1100&#10;2700">${escapeHtml((element.jitter_values_ms || []).join("\n"))}</textarea>
    </div>
    <input data-element-field="label" type="hidden" value="${escapeAttr(element.label || title)}">
    <input data-element-field="randomized" type="hidden" value="true">
  `;
}

function filmstripNoiseClass(element) {
  if (element.kind === "jitter") return "jitter";
  const labels = element.source_labels || [];
  const firstLabel = labels[0] || element.source_label || "";
  const source = stimulusSourceDetailsFromDom().find((item) => item.label === firstLabel);
  return source?.noise_type ? `noise-${String(source.noise_type).toLowerCase()}` : "";
}

function fixedAudioOptions(selected = "") {
  const options = [{ value: "", label: "Select clip" }];
  for (const item of fixedAudioSourceOptions()) options.push(item);
  return renderOptionList(options, selected);
}

function sequenceAudioOptions(selected = "") {
  const options = [{ value: "", label: "Add label..." }];
  const seen = new Set();
  for (const item of fixedAudioSourceOptions()) {
    if (seen.has(item.value)) continue;
    options.push(item);
    seen.add(item.value);
  }
  for (const source of stimulusSourceDetailsFromDom()) {
    if (!source.label || seen.has(source.label)) continue;
    options.push({ value: source.label, label: source.label });
    seen.add(source.label);
  }
  return renderOptionList(options, selected);
}

function sourcePoolOptions(selected = []) {
  const selectedSet = new Set(selected || []);
  return stimulusSourceDetailsFromDom()
    .map((item) => `<option value="${escapeAttr(item.label)}" ${selectedSet.has(item.label) ? "selected" : ""}>${escapeHtml(item.label)}</option>`)
    .join("");
}

function sourceCardLabelFromSpec(spec = {}, fallback = "Audio source") {
  return String(spec.label || fallback || "Audio source").trim();
}

function sourceCardCurrentLabel(card) {
  if (!card) return "";
  const fallback = card.classList.contains("audio-source-card")
    ? audioRoleTitle(card.dataset.audioRole || "preserve")
    : "Generated noise";
  return String(card.querySelector('[data-field="label"]')?.value || fallback).trim();
}

function sequenceLabelSetFromDom() {
  const labels = new Set();
  for (const item of fixedAudioSourceOptions()) {
    if (item.value) labels.add(item.value);
  }
  for (const source of stimulusSourceDetailsFromDom()) {
    if (source.label) labels.add(source.label);
  }
  return labels;
}

function reconcileTrialStripLabelsWithSourcePool(labelMap = {}) {
  const validLabels = sequenceLabelSetFromDom();
  const strips = collectTrialStrips();
  let changed = false;
  for (const strip of strips) {
    for (const element of strip.elements || []) {
      if (!["fixed_audio", "looming_stimulus"].includes(element.kind)) continue;
      const originalLabels = audioBoxLabels(element);
      const nextLabels = [];
      for (const label of originalLabels) {
        const mapped = labelMap[label] || label;
        if (mapped && validLabels.has(mapped) && !nextLabels.includes(mapped)) {
          nextLabels.push(mapped);
        }
      }
      if (nextLabels.length !== originalLabels.length || nextLabels.some((label, index) => label !== originalLabels[index])) {
        changed = true;
        element.source_labels = nextLabels;
        element.source_label = nextLabels[0] || "";
        if (element.kind === "fixed_audio") {
          element.label = nextLabels.length > 1 ? "Audio pool" : (nextLabels[0] || "Audio box");
        }
      }
    }
  }
  if (changed) {
    setTrialStrips(strips);
  } else {
    syncFilmstripSourceOptions();
  }
  return changed;
}

function reconcileSourceCardLabel(card) {
  const previous = String(card?.dataset.sourceLabel || "").trim();
  const next = sourceCardCurrentLabel(card);
  if (card) card.dataset.sourceLabel = next;
  if (previous && next && previous !== next) {
    reconcileTrialStripLabelsWithSourcePool({ [previous]: next });
  } else {
    syncFilmstripSourceOptions();
  }
}

function fixedAudioSourceOptions() {
  const options = [];
  for (const card of [
    ...$("audio-list").querySelectorAll(".audio-source-card"),
    ...$("snippet-list").querySelectorAll(".audio-source-card"),
  ]) {
    const role = card.querySelector('[data-field="audio_role"]')?.value;
    if (role !== "prestimulus") continue;
    const label = card.querySelector('[data-field="label"]')?.value.trim();
    if (label) options.push({ value: label, label });
  }
  return options;
}

function stimulusSourceDetailsFromDom() {
  const sources = [];
  for (const card of $("noise-list").querySelectorAll(".noise-source-card")) {
    const label = card.querySelector('[data-field="label"]')?.value.trim() || "Generated noise";
    sources.push({
      label,
      noise_type: card.querySelector('[data-field="noise_type"]')?.value || "pink",
    });
  }
  for (const card of [
    ...$("noise-list").querySelectorAll(".audio-source-card"),
    ...$("audio-list").querySelectorAll(".audio-source-card"),
  ]) {
    const role = card.querySelector('[data-field="audio_role"]')?.value;
    if (role === "prestimulus") continue;
    const label = card.querySelector('[data-field="label"]')?.value.trim() || audioRoleTitle(role);
    sources.push({ label, noise_type: card.querySelector('[data-field="tone_type"]')?.value || "custom_audio" });
  }
  return sources;
}

function collectTrialStrips() {
  const strips = [];
  for (const [stripIndex, row] of [...$("filmstrip-list").querySelectorAll(".filmstrip-row")].entries()) {
    const label = row.querySelector('[data-strip-field="label"]')?.value.trim() || `Trial type ${stripIndex + 1}`;
    const elements = [];
    for (const [elementIndex, card] of [...row.querySelectorAll(".filmstrip-element")].entries()) {
      const kind = card.dataset.elementKind === "fixed_audio" || card.dataset.elementKind === "jitter"
        ? card.dataset.elementKind
        : "looming_stimulus";
      const item = {
        element_id: `row-${stripIndex + 1}-element-${elementIndex + 1}`,
        kind,
        label: card.querySelector('[data-element-field="label"]')?.value.trim() || (kind === "fixed_audio" ? "Fixed audio clip" : kind === "jitter" ? "Jitter / ITI event" : "Looming Stimulus"),
        source_label: "",
        source_labels: [],
        jitter_values_ms: [],
        randomized: Boolean(card.querySelector('[data-element-field="randomized"]')?.checked),
      };
      if (kind === "fixed_audio") {
        item.source_labels = [...card.querySelectorAll('input[data-element-field="source_labels"]')].map((input) => input.value).filter(Boolean);
        item.source_label = item.source_labels[0] || "";
        item.label = item.source_labels.length > 1 ? "Audio pool" : (item.source_label || item.label);
      } else if (kind === "jitter") {
        item.jitter_values_ms = parseIntegerList(card.querySelector('[data-element-field="jitter_values_ms"]')?.value).filter((value) => value >= 0);
        item.randomized = true;
      } else {
        const checked = [...card.querySelectorAll('input[data-element-field="source_labels"]:checked')].map((input) => input.value);
        const hidden = [...card.querySelectorAll('input[data-element-field="source_labels"][type="hidden"]')].map((input) => input.value);
        const selected = [...card.querySelectorAll('select[data-element-field="source_labels"] option:checked')].map((option) => option.value);
        item.source_labels = (hidden.length ? hidden : (checked.length ? checked : selected)).filter(Boolean);
        item.source_label = item.source_labels[0] || "";
        item.randomized = true;
      }
      elements.push(item);
    }
    strips.push({
      strip_id: `strip-${stripIndex + 1}`,
      label,
      audio_tactile_percentage: Number(row.querySelector('[data-strip-field="audio_tactile_percentage"]')?.value || 0),
      catch_percentage: Number(row.querySelector('[data-strip-field="catch_percentage"]')?.value || 0),
      baseline_percentage: Number(row.querySelector('[data-strip-field="baseline_percentage"]')?.value || 0),
      elements,
    });
  }
  return strips;
}

function updateFilmstripCounts() {
  const strips = collectTrialStrips();
  for (const [index, row] of [...$("filmstrip-list").querySelectorAll(".filmstrip-row")].entries()) {
    const rowCounts = rowCompositionCounts(row);
    const mixValid = Math.abs(rowCounts.mix.total - 100) <= 0.5 && rowCounts.mix.audioTactile > 0;
    row.classList.toggle("mix-warning", !mixValid);
    const strip = strips[index] || {};
    const variants = stripPreviewVariants(strip).length;
    const expression = row.querySelector(".filmstrip-row-expression");
    const count = row.querySelector(".filmstrip-row-variant-count");
    if (expression) expression.textContent = trialStripExpression(strip);
    if (count) count.textContent = `${variants} ${variants === 1 ? "variant" : "variants"}`;
  }
  renderProtocolSummary();
  renderLiveTrialPreviewTables();
  updateBaselineDecision();
  renderBakePanel();
}

async function previewFilmstripRow(button) {
  const row = button.closest(".filmstrip-row");
  if (!row) return;
  state.design.protocol = state.design.protocol || {};
  state.design.protocol.trial_strips = collectTrialStrips();
  const payload = collectPayload();
  payload.strip_index = Number(row.dataset.stripIndex || 0);
  button.disabled = true;
  button.classList.add("playing");
  let started = false;
  try {
    const preview = await designerApi.previewTrialRow(payload);
    if (activeTrialRowPreviewAudio) {
      activeTrialRowPreviewAudio.pause();
      activeTrialRowPreviewAudio = null;
    }
    const audio = new Audio(apiUrl(`${preview.url}?v=${Date.now()}`));
    activeTrialRowPreviewAudio = audio;
    audio.addEventListener("ended", () => button.classList.remove("playing"), { once: true });
    audio.addEventListener("pause", () => button.classList.remove("playing"), { once: true });
    await audio.play();
    started = true;
    showToast(`Preview: ${preview.sequence.join(" | ")}`);
  } catch (error) {
    button.classList.remove("playing");
    throw error;
  } finally {
    if (!started) button.classList.remove("playing");
    button.disabled = false;
  }
}

async function previewSourceLabel(button) {
  const label = button.dataset.previewSourceLabel || "";
  if (!label) return;
  if (activeSourcePreviewButton === button) {
    stopActiveSourcePreview();
    return;
  }
  state.design.protocol = state.design.protocol || {};
  state.design.protocol.trial_strips = collectTrialStrips();
  const staticAsset = staticModeActive ? staticPreviewAssetForLabel(label) : null;
  const payload = collectPayload();
  payload.label = label;
  button.disabled = true;
  button.classList.add("playing");
  const audioContext = getSourcePreviewAudioContext();
  const contextReady = audioContext?.resume().catch(() => {});
  let started = false;
  try {
    if (staticAsset?.url) {
      button.disabled = false;
      await playSourcePreviewAudio(
        staticAsset.url,
        button,
        audioContext,
        contextReady,
        Number(staticAsset.duration_s || 0)
      );
      started = true;
      showToast(`Soundcheck: ${staticAsset.label || label}`);
      return;
    }
    const preview = await designerApi.previewAudioSource(payload);
    button.disabled = false;
    await playSourcePreviewAudio(
      apiUrl(`${preview.url}?v=${Date.now()}`),
      button,
      audioContext,
      contextReady,
      Number(preview.duration_s) || 0
    );
    started = true;
    showToast(`Soundcheck: ${preview.label || label}`);
  } catch (error) {
    button.classList.remove("playing");
    throw error;
  } finally {
    if (!started) button.classList.remove("playing");
    button.disabled = false;
  }
}

function staticPreviewAssetForLabel(label) {
  const target = normalizeSourceKey(label);
  if (!target) return null;
  const inventoryAssets = state?.preload_inventory?.assets || [];
  for (const asset of inventoryAssets) {
    if (normalizeSourceKey(asset.label) === target) {
      return {
        label: asset.label,
        url: asset.url || staticResourceUrl(asset.path),
        duration_s: asset.duration_s
      };
    }
  }
  for (const noise of state?.design?.noises || []) {
    if (normalizeSourceKey(noise.label) === target && noise.prebaked_path) {
      return {
        label: noise.label,
        url: staticResourceUrl(noise.prebaked_path),
        duration_s: noise.duration_s || 0
      };
    }
  }
  for (const source of [...(state?.design?.custom_looming_files || []), ...(state?.design?.prestimulus_files || [])]) {
    if (normalizeSourceKey(source.label) === target && source.path) {
      return {
        label: source.label,
        url: staticResourceUrl(source.path),
        duration_s: source.target_duration_s || source.duration_s || 0
      };
    }
  }
  return null;
}

function getSourcePreviewAudioContext() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!sourcePreviewAudioContext) {
    sourcePreviewAudioContext = new AudioContextClass();
  }
  return sourcePreviewAudioContext;
}

function stopActiveSourcePreview() {
  const previousButton = activeSourcePreviewButton;
  if (activeSourcePreviewNode) {
    try {
      activeSourcePreviewNode.stop();
    } catch (_error) {
      // Already stopped.
    }
    activeSourcePreviewNode.disconnect();
    activeSourcePreviewNode = null;
  }
  if (activeSourcePreviewAudio) {
    activeSourcePreviewAudio.pause();
    activeSourcePreviewAudio = null;
  }
  finishSourcePreview(previousButton);
}

function finishSourcePreview(button = activeSourcePreviewButton) {
  if (activeSourcePreviewClearTimer) {
    clearTimeout(activeSourcePreviewClearTimer);
    activeSourcePreviewClearTimer = null;
  }
  if (button) button.classList.remove("playing");
  if (!button || activeSourcePreviewButton === button) {
    activeSourcePreviewButton = null;
  }
}

function armSourcePreviewCleanup(button, durationS) {
  const durationMs = Number.isFinite(durationS) && durationS > 0
    ? Math.min(Math.max(durationS * 1000 + 750, 1250), 60000)
    : 15000;
  activeSourcePreviewButton = button;
  activeSourcePreviewClearTimer = window.setTimeout(() => {
    if (activeSourcePreviewButton !== button) return;
    if (activeSourcePreviewNode) {
      try {
        activeSourcePreviewNode.stop();
      } catch (_error) {
        // Already stopped.
      }
      activeSourcePreviewNode.disconnect();
      activeSourcePreviewNode = null;
    }
    if (activeSourcePreviewAudio) {
      activeSourcePreviewAudio.pause();
      activeSourcePreviewAudio = null;
    }
    finishSourcePreview(button);
  }, durationMs);
}

async function playSourcePreviewAudio(url, button, audioContext, contextReady, durationS = 0) {
  stopActiveSourcePreview();
  armSourcePreviewCleanup(button, durationS);
  if (audioContext) {
    if (contextReady) await Promise.race([contextReady, delay(250)]);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Preview audio unavailable: ${response.status}`);
    const buffer = await audioContext.decodeAudioData(await response.arrayBuffer());
    const node = audioContext.createBufferSource();
    node.buffer = buffer;
    node.connect(audioContext.destination);
    activeSourcePreviewNode = node;
    node.addEventListener("ended", () => {
      if (activeSourcePreviewNode === node) {
        activeSourcePreviewNode = null;
      }
      node.disconnect();
      finishSourcePreview(button);
    }, { once: true });
    node.start();
    return;
  }
  const audio = new Audio(url);
  activeSourcePreviewAudio = audio;
  audio.addEventListener("ended", () => {
    if (activeSourcePreviewAudio === audio) {
      activeSourcePreviewAudio = null;
    }
    finishSourcePreview(button);
  }, { once: true });
  audio.addEventListener("pause", () => {
    if (activeSourcePreviewAudio === audio) {
      activeSourcePreviewAudio = null;
    }
    finishSourcePreview(button);
  }, { once: true });
  audio.play().catch((error) => {
    finishSourcePreview(button);
    showToast(error.message || String(error));
  });
}

function setTrialStrips(strips) {
  state.design.protocol = state.design.protocol || {};
  state.design.protocol.trial_strips = strips;
  markDesignerDirty();
  renderTrialStrips();
}

function defaultTrialStrip(rowNumber, kind = null) {
  const mix = defaultRowMix();
  return {
    strip_id: `strip-${rowNumber}`,
    label: `Trial type ${rowNumber}`,
    audio_tactile_percentage: mix.audioTactile,
    catch_percentage: mix.catch,
    baseline_percentage: mix.baseline,
    elements: kind ? [defaultFilmstripElement(kind)] : [],
  };
}

function addFilmstripRow(kind = null, insertAfter = "end") {
  const strips = collectTrialStrips();
  const rawInsertAfter = insertAfter === undefined || insertAfter === null ? "end" : insertAfter;
  const targetIndex = rawInsertAfter === "end"
    ? strips.length
    : Math.max(0, Math.min(strips.length, Number(rawInsertAfter) + 1));
  strips.splice(targetIndex, 0, defaultTrialStrip(targetIndex + 1, kind || "fixed_audio"));
  setTrialStrips(strips);
}

function defaultFilmstripElement(kind) {
  if (kind === "fixed_audio") {
    return {
      element_id: "",
      kind: "fixed_audio",
      label: "Audio box",
      source_label: "",
      source_labels: [],
      randomized: true,
    };
  }
  if (kind === "jitter") {
    return {
      element_id: "",
      kind: "jitter",
      label: "Jitter / ITI event",
      source_label: "",
      source_labels: [],
      jitter_values_ms: [],
      randomized: true,
    };
  }
  return {
    element_id: "",
    kind: "looming_stimulus",
    label: "Audio box",
    source_label: "",
    source_labels: [],
    randomized: true,
  };
}

function addFilmstripElement(button, kind) {
  const row = button.closest(".filmstrip-row");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const strips = collectTrialStrips();
  if (!strips[stripIndex]) return;
  const insertAfter = Number(button.dataset.insertAfter ?? strips[stripIndex].elements.length);
  const index = Math.max(0, Math.min(strips[stripIndex].elements.length, insertAfter));
  strips[stripIndex].elements.splice(index, 0, defaultFilmstripElement(kind));
  setTrialStrips(strips);
}

function removeFilmstripRow(button) {
  const row = button.closest(".filmstrip-row");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const strips = collectTrialStrips();
  if (stripIndex < 0) return;
  strips.splice(stripIndex, 1);
  setTrialStrips(strips);
}

function moveFilmstripRow(button, direction) {
  const row = button.closest(".filmstrip-row");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const targetIndex = direction === "up" ? stripIndex - 1 : stripIndex + 1;
  const strips = collectTrialStrips();
  if (!strips[stripIndex] || !strips[targetIndex]) return;
  const [item] = strips.splice(stripIndex, 1);
  strips.splice(targetIndex, 0, item);
  setTrialStrips(strips);
}

function removeFilmstripElement(button) {
  const row = button.closest(".filmstrip-row");
  const card = button.closest(".filmstrip-element");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const elementIndex = Number(card?.dataset.elementIndex || -1);
  const strips = collectTrialStrips();
  if (!strips[stripIndex] || elementIndex < 0) return;
  strips[stripIndex].elements.splice(elementIndex, 1);
  setTrialStrips(strips);
}

function addBoxLabel(button) {
  const row = button.closest(".filmstrip-row");
  const card = button.closest(".filmstrip-element");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const elementIndex = Number(card?.dataset.elementIndex || -1);
  const label = card?.querySelector('[data-element-field="label_picker"]')?.value || "";
  if (!label) return;
  const strips = collectTrialStrips();
  const element = strips[stripIndex]?.elements?.[elementIndex];
  if (!element) return;
  const labels = audioBoxLabels(element);
  if (!labels.includes(label)) labels.push(label);
  element.kind = "fixed_audio";
  element.source_label = labels[0] || "";
  element.source_labels = labels;
  element.label = labels.length > 1 ? "Audio pool" : (labels[0] || "Audio box");
  setTrialStrips(strips);
}

function removeBoxLabel(button) {
  const row = button.closest(".filmstrip-row");
  const card = button.closest(".filmstrip-element");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const elementIndex = Number(card?.dataset.elementIndex || -1);
  const label = button.dataset.removeBoxLabel || "";
  const strips = collectTrialStrips();
  const element = strips[stripIndex]?.elements?.[elementIndex];
  if (!element) return;
  const labels = audioBoxLabels(element).filter((item) => item !== label);
  element.kind = "fixed_audio";
  element.source_label = labels[0] || "";
  element.source_labels = labels;
  element.label = labels.length > 1 ? "Audio pool" : (labels[0] || "Audio box");
  setTrialStrips(strips);
}

function setFilmstripElementMode(input) {
  const row = input.closest(".filmstrip-row");
  const card = input.closest(".filmstrip-element");
  const stripIndex = Number(row?.dataset.stripIndex || -1);
  const elementIndex = Number(card?.dataset.elementIndex || -1);
  const strips = collectTrialStrips();
  const element = strips[stripIndex]?.elements?.[elementIndex];
  if (!element) return;
  if (input.checked) {
    strips[stripIndex].elements[elementIndex] = {
      element_id: element.element_id || "",
      kind: "jitter",
      label: "Jitter / ITI event",
      source_label: "",
      source_labels: [],
      jitter_values_ms: element.jitter_values_ms || [],
      randomized: true,
    };
  } else {
    strips[stripIndex].elements[elementIndex] = {
      element_id: element.element_id || "",
      kind: "fixed_audio",
      label: "Audio box",
      source_label: "",
      source_labels: audioBoxLabels(element),
      jitter_values_ms: [],
      randomized: true,
    };
  }
  setTrialStrips(strips);
}

function syncFilmstripSourceOptions() {
  for (const select of document.querySelectorAll('[data-element-field="source_label"]')) {
    const selected = select.value;
    select.innerHTML = fixedAudioOptions(selected);
    select.value = [...select.options].some((option) => option.value === selected) ? selected : "";
  }
  for (const select of document.querySelectorAll('[data-element-field="label_picker"]')) {
    const selected = select.value;
    select.innerHTML = sequenceAudioOptions(selected);
    select.value = [...select.options].some((option) => option.value === selected) ? selected : "";
  }
  for (const select of document.querySelectorAll('[data-element-field="source_labels"]')) {
    const selected = Array.from(select.selectedOptions || []).map((option) => option.value);
    select.innerHTML = sourcePoolOptions(selected);
  }
  updateFilmstripCounts();
}

function renderRun() {
  const setup = state.run_sequence_setup || {};
  const profileValid = Boolean(setup.ready || getWorkflowStep("run")?.complete);
  const finalized = isProfileFinalized();
  if ($("participants") && document.activeElement !== $("participants")) {
    $("participants").value = state.design.protocol?.participants ?? setup.participant_count ?? 1;
  }
  for (const input of document.querySelectorAll('input[name="experiment-structure"]')) {
    if (document.activeElement !== input) input.checked = input.value === (setup.experiment_structure || "single");
    input.disabled = finalized;
  }
  if ($("participants")) $("participants").disabled = finalized;
  const pill = $("run-sequence-status");
  if (pill) {
    pill.textContent = finalized ? "profile locked" : profileValid ? "ready to lock" : "review required";
    pill.className = `status-label ${finalized || profileValid ? "ready" : "required"}`;
  }
  const regenerateButton = $("regenerate-run-sequence");
  if (regenerateButton) regenerateButton.disabled = finalized || !setup.ready;
  const prepareButton = $("prepare-experiment");
  if (prepareButton) {
    prepareButton.disabled = true;
    prepareButton.hidden = true;
  }
  const saveProfileButton = $("save-study-profile");
  if (saveProfileButton) {
    saveProfileButton.hidden = finalized;
    saveProfileButton.disabled = !profileValid;
    saveProfileButton.title = profileValid
      ? (staticModeActive ? "Lock this browser-local profile for portable export." : "Register and lock this validated profile in the researcher workspace.")
      : "Complete profile validation before finalizing.";
  }
  const bundleButton = $("export-profile-bundle");
  if (bundleButton) {
    bundleButton.hidden = !finalized;
    bundleButton.disabled = !finalized;
    bundleButton.title = finalized ? "Export this locked profile as a portable bundle." : "Lock the validated profile before exporting it.";
  }
  const summary = $("run-sequence-summary");
  const jsonButton = $("export-experiment-json");
  if (jsonButton) {
    jsonButton.hidden = !finalized;
    jsonButton.disabled = !finalized || staticModeActive;
    jsonButton.title = staticModeActive ? "Use the installed Designer to verify local audio files and export an experiment JSON."
      : "Export the approved schedule and verified local ingredients for the Runner.";
  }
  if (summary) {
    const exampleCount = Number(setup.participant_count || state.design.protocol?.participants || 0);
    const visibleCount = Number(setup.rows?.length || 0);
    const partCount = Number(setup.parts_per_participant || (currentExperimentStructure() === "pre_post" ? 2 : 1));
    const orderSummary = visibleCount < exampleCount
      ? `${visibleCount} of ${exampleCount} example orders shown`
      : `${exampleCount} example order${exampleCount === 1 ? "" : "s"}`;
    summary.textContent = setup.ready
      ? `${orderSummary} · ${partCount} ${partCount === 1 ? "part" : "parts"} · deterministic preview`
      : (setup.message || "waiting for accepted blocks");
    summary.className = `status-label ${setup.ready ? "ready" : "required"}`;
  }
  renderProfileValidationChecklist(profileValid, finalized);
  renderRunInstructions(setup.instruction_profile || state.design?.study_profile_reference_parameters?.dashboard_run_setup?.instruction_profile || {});
  renderRunSequenceTable(setup.rows || []);
  renderProtocolSummary();
}

function renderProfileValidationChecklist(profileValid = false, finalized = false) {
  const container = $("profile-validation-checklist");
  const summary = $("profile-validation-summary");
  if (!container) return;
  const workflow = state.custom_workflow || {};
  const stepMap = new Map((workflow.steps || []).map((step) => [step.id, step]));
  const items = WORKFLOW_STEPS.slice(0, -1).map((stepId) => {
    const step = workflow.is_custom ? (stepMap.get(stepId) || profileStepStatus(stepId)) : profileStepStatus(stepId);
    return { id: stepId, label: step.label || stepLabel(stepId), complete: Boolean(step.complete) };
  });
  const completeCount = items.filter((item) => item.complete).length;
  container.innerHTML = items.map((item) => `
    <div class="profile-validation-item ${item.complete ? "complete" : "required"}">
      <span aria-hidden="true">${item.complete ? "✓" : "!"}</span>
      <span>${escapeHtml(item.label)}</span>
      <strong>${item.complete ? "validated" : "review required"}</strong>
    </div>
  `).join("");
  if (summary) {
    summary.textContent = finalized ? "locked" : profileValid ? "all checks passed" : `${completeCount} of ${items.length} passed`;
    summary.className = `status-label ${finalized || profileValid ? "ready" : "required"}`;
  }
}

function folderDisplayName(path) {
  const text = String(path || "").replace(/[\\/]+$/, "");
  if (!text) return "runner default";
  const parts = text.split(/[\\/]/).filter(Boolean);
  const tail = parts.slice(-2).join(" / ");
  return tail || text;
}

function normalizedRunInstructionSlots(profile = {}) {
  const rawSlots = Array.isArray(profile.slots) ? profile.slots : [];
  const bySlot = new Map(rawSlots.map((item) => [item.slot, item]));
  return RUN_INSTRUCTION_SLOTS.map((definition) => {
    const item = bySlot.get(definition.slot) || {};
    const mode = RUN_INSTRUCTION_CONTINUE_MODES.some((entry) => entry.value === item.continue_mode)
      ? item.continue_mode
      : "click";
    return {
      slot: definition.slot,
      label: item.label || definition.label,
      enabled: Boolean(item.enabled),
      required: false,
      path: item.path || "",
      duration_s: Number(item.duration_s || 0),
      sample_rate: Number(item.sample_rate || 0),
      channels: Number(item.channels || 0),
      sha256: item.sha256 || "",
      continue_mode: mode,
      delay_s: Math.max(0, Number(item.delay_s || 0)),
      button_label: item.button_label || "Continue",
      source: item.source || ""
    };
  });
}

function renderRunInstructions(profile = {}) {
  const container = $("run-instruction-slots");
  if (!container) return;
  const prepared = isProfileFinalized();
  const slots = normalizedRunInstructionSlots(profile);
  const enabledCount = slots.filter((slot) => slot.enabled && slot.path).length;
  const summary = $("run-instructions-summary");
  if (summary) {
    summary.textContent = enabledCount ? `${enabledCount} of ${slots.length} optional clips enabled` : "optional";
    summary.className = `status-label ${enabledCount ? "ready" : "optional"}`;
  }
  container.innerHTML = slots.map((slot) => {
    const duration = slot.duration_s ? `${slot.duration_s.toFixed(slot.duration_s >= 10 ? 1 : 2)}s` : "no clip";
    const modeOptions = RUN_INSTRUCTION_CONTINUE_MODES.map((mode) => (
      `<option value="${mode.value}" ${mode.value === slot.continue_mode ? "selected" : ""}>${mode.label}</option>`
    )).join("");
    return `
      <article class="run-instruction-slot" data-run-instruction-slot="${escapeHtml(slot.slot)}">
        <label class="inline-toggle">
          <input type="checkbox" data-instruction-field="enabled" ${slot.enabled ? "checked" : ""} ${prepared ? "disabled" : ""}>
          <span>${escapeHtml(RUN_INSTRUCTION_SLOTS.find((item) => item.slot === slot.slot)?.label || slot.slot)}</span>
        </label>
        <input class="instruction-label-input" data-instruction-field="label" type="text" value="${escapeHtml(slot.label)}" ${prepared ? "disabled" : ""}>
        <button type="button" class="instruction-import-button" data-instruction-import="${escapeHtml(slot.slot)}" ${prepared ? "disabled" : ""}>Load Audio</button>
        <span class="status-label ${slot.path ? "ready" : "optional"}">${escapeHtml(duration)}</span>
        <select data-instruction-field="continue_mode" ${prepared ? "disabled" : ""}>${modeOptions}</select>
        <input data-instruction-field="delay_s" type="number" min="0" step="0.1" value="${slot.delay_s}" ${prepared ? "disabled" : ""} title="Delay seconds">
        <input data-instruction-field="button_label" type="text" value="${escapeHtml(slot.button_label)}" ${prepared ? "disabled" : ""} title="Runner button label">
        <input data-instruction-field="path" type="hidden" value="${escapeHtml(slot.path)}">
        <input data-instruction-field="duration_s" type="hidden" value="${slot.duration_s}">
        <input data-instruction-field="sample_rate" type="hidden" value="${slot.sample_rate}">
        <input data-instruction-field="channels" type="hidden" value="${slot.channels}">
        <input data-instruction-field="sha256" type="hidden" value="${escapeHtml(slot.sha256)}">
        <input data-instruction-field="source" type="hidden" value="${escapeHtml(slot.source)}">
      </article>
    `;
  }).join("");
}

function collectRunInstructionProfile() {
  const slots = [];
  for (const definition of RUN_INSTRUCTION_SLOTS) {
    const card = document.querySelector(`[data-run-instruction-slot="${definition.slot}"]`);
    const field = (name) => card?.querySelector(`[data-instruction-field="${name}"]`);
    slots.push({
      slot: definition.slot,
      label: field("label")?.value.trim() || definition.label,
      enabled: field("enabled")?.checked === true,
      required: false,
      path: field("path")?.value.trim() || "",
      duration_s: Number(field("duration_s")?.value || 0),
      sample_rate: Number(field("sample_rate")?.value || 0),
      channels: Number(field("channels")?.value || 0),
      sha256: field("sha256")?.value.trim() || "",
      continue_mode: field("continue_mode")?.value || "click",
      delay_s: Math.max(0, Number(field("delay_s")?.value || 0)),
      button_label: field("button_label")?.value.trim() || "Continue",
      source: field("source")?.value.trim() || ""
    });
  }
  return { schema: "pps-run-instructions.v1", slots };
}

function renderWorkflow() {
  const workflow = state.custom_workflow || { is_custom: false, steps: [] };
  const customMode = Boolean(workflow.is_custom);
  const profileReadonly = isProfileReadonlyMode();
  const viewLocked = customViewModeLocked();
  const sequentialEdit = Boolean(customMode && editModeActive && !profileReadonly);
  const progress = normalizedDesignerProgress();
  const activeStep = sequentialEdit ? progress.edit_step : (workflow.current_step || "run");
  const activeIndex = WORKFLOW_STEPS.indexOf(activeStep);
  const confirmedSteps = new Set(progress.confirmed_steps);
  const needsReviewSteps = new Set(progress.needs_review_steps);
  document.body.classList.toggle("custom-mode", customMode);
  document.body.classList.toggle("profile-readonly-mode", profileReadonly);
  document.body.classList.toggle("sequential-edit-mode", sequentialEdit);
  const pill = $("workflow-pill");

  if (pill) {
    pill.textContent = sequentialEdit
      ? `${stepLabel(activeStep)} editing`
      : customMode
        ? workflow.ready_to_prepare ? "custom ready" : `${stepLabel(workflow.current_step || "run")} required`
      : "profile loaded";
    pill.className = `status-label ${sequentialEdit || (customMode && !workflow.ready_to_prepare) ? "required" : "ready"}`;
  }

  const stepMap = new Map((workflow.steps || []).map((step) => [step.id, step]));
  for (const stepId of WORKFLOW_STEPS) {
    const index = WORKFLOW_STEPS.indexOf(stepId);
    const step = customMode
      ? (stepMap.get(stepId) || { id: stepId, complete: false, missing: [] })
      : profileStepStatus(stepId);
    const current = Boolean(sequentialEdit && stepId === activeStep);
    const confirmed = Boolean(sequentialEdit && confirmedSteps.has(stepId));
    const needsReview = Boolean(sequentialEdit && needsReviewSteps.has(stepId));
    const downstream = Boolean(sequentialEdit && index > activeIndex);
    const locked = Boolean(sequentialEdit && !current);
    const complete = Boolean(step.complete);
    const link = document.querySelector(`[data-step-link="${stepId}"]`);
    const stateLabel = document.querySelector(`[data-step-state="${stepId}"]`);
    const badges = document.querySelectorAll(`[data-step-badge="${stepId}"]`);
    const segment = $(STEP_TARGETS[stepId]);
    const reopenButton = document.querySelector(`[data-reopen-step="${stepId}"]`);
    const continueButton = document.querySelector(`[data-continue-step="${stepId}"]`);

    if (link) {
      link.classList.toggle("locked", locked);
      link.classList.toggle("downstream", downstream);
      link.classList.toggle("current", current);
      link.classList.toggle("complete", complete);
      link.classList.toggle("not-ready", !complete);
      if (current) link.setAttribute("aria-current", "step");
      else link.removeAttribute("aria-current");
      link.removeAttribute("aria-disabled");
      link.title = downstream
        ? `Read-only until ${stepLabel(activeStep)} is saved.`
        : confirmed
          ? "Saved and read-only. Use Reopen segment to change it."
          : "";
    }
    if (segment) {
      segment.classList.toggle("workflow-current", current);
      segment.classList.toggle("workflow-confirmed", confirmed);
      segment.classList.toggle("workflow-downstream", downstream);
      segment.classList.toggle("workflow-needs-review", needsReview);
      segment.dataset.workflowState = current
        ? "editing"
        : confirmed
          ? "confirmed"
          : needsReview
            ? "needs-review"
            : sequentialEdit
              ? "locked"
              : "overview";
    }
    if (reopenButton) {
      reopenButton.hidden = !(confirmed && index > 0 && index < activeIndex);
      reopenButton.disabled = !sequentialEdit || workflowSaveInFlight;
    }
    if (continueButton) continueButton.hidden = !(sequentialEdit && current);
    if (stepId === "schedule" && $("accept-block-csvs")) {
      $("accept-block-csvs").hidden = !(sequentialEdit && current);
    }
    if (stepId === "run" && $("save-study-profile")) {
      $("save-study-profile").hidden = isProfileFinalized() || !(sequentialEdit && current);
    }
    if (stateLabel) {
      const label = step.label || humanize(stepId);
      const missing = Array.isArray(step.missing) && step.missing.length
        ? `: ${step.missing.join(", ")}`
        : "";
      const status = complete ? "ready" : "not ready";
      stateLabel.textContent = complete ? "\u2713" : "\u2715";
      stateLabel.className = `decision-state ${complete ? "ready" : "not-ready"}`;
      stateLabel.title = `${label} ${status}${missing}`;
      stateLabel.setAttribute("aria-label", `${label} ${status}`);
    }
    const missingSummary = !complete && Array.isArray(step.missing) && step.missing.length
      ? step.missing.join(", ")
      : "";
    for (const badge of badges) {
      badge.hidden = profileReadonly;
      if (profileReadonly) {
        badge.textContent = "";
        badge.removeAttribute("title");
        badge.className = "step-badge";
        continue;
      }
      const baseText = sequentialEdit
        ? current
          ? "editing"
          : confirmed
            ? "saved"
            : needsReview
              ? "needs review"
              : "locked"
        : complete
          ? "validated"
          : "review required";
      badge.textContent = (baseText === "editing" || baseText === "review required") && missingSummary
        ? `${baseText} · ${missingSummary}`
        : baseText;
      badge.title = missingSummary ? `Needs: ${missingSummary}` : "";
      badge.className = `step-badge ${current ? "current" : confirmed ? "complete" : needsReview ? "needs-review" : locked ? "locked" : complete ? "complete" : ""}`;
    }
  }

  for (const panel of document.querySelectorAll("[data-step-panel]")) {
    const stepId = panel.dataset.stepPanel;
    const stepIndex = WORKFLOW_STEPS.indexOf(stepId);
    const editLocked = Boolean(sequentialEdit && stepId !== activeStep);
    const downstream = Boolean(editLocked && stepIndex > activeIndex);
    const confirmed = Boolean(editLocked && confirmedSteps.has(stepId));
    const readonly = Boolean(profileReadonly && stepId !== "study");
    const viewReadonly = Boolean(viewLocked && stepId !== "study");
    panel.classList.toggle("locked", downstream);
    panel.classList.toggle("workflow-confirmed", confirmed);
    panel.classList.toggle("profile-readonly", readonly);
    panel.classList.toggle("view-readonly", viewReadonly);
    if (editLocked) {
      panel.title = downstream
        ? `Read-only until ${stepLabel(activeStep)} is saved.`
        : "This segment is saved. Use Reopen segment before changing it.";
    } else if (readonly || viewReadonly) {
      panel.title = readonly
        ? "Create a custom study before editing this loaded profile."
        : "Switch to Edit mode before changing this custom study.";
    } else if (
      panel.title === "Create a custom study before editing this loaded profile."
      || panel.title === "Switch to Edit mode before changing this custom study."
      || panel.title === "This segment is saved. Use Reopen segment before changing it."
      || panel.title.startsWith("Read-only until ")
    ) {
      panel.title = "";
    }
    for (const control of panel.querySelectorAll("input, select, textarea, button")) {
      // Preview view-controls (2D/3D, zoom, Fit Radius, Reset Camera) only change
      // the camera/view, never the trajectory data, so they stay usable even when
      // the step is read-only or locked. People can always look at the preview;
      // editing the trajectory itself remains gated to edit mode.
      const previewViewControl = Boolean(control.matches?.("[data-preview-view-control]"));
      // One hidden picker is shared by Segment 1 source import and Segment 6
      // instruction import even though its DOM home is inside Segment 2.
      const sharedFilePicker = control.id === "audio-file-input";
      const readonlyLocked = readonly && !previewViewControl && !profileReadonlyControlAllowed(control);
      const viewModeLocked = viewReadonly && !previewViewControl && !viewModeControlAllowed(control);
      if (readonlyLocked) {
        if (!control.title) {
          control.dataset.profileReadonlyTitle = "true";
          control.title = "Create a custom study before editing this loaded profile.";
        }
      } else if (control.dataset.profileReadonlyTitle === "true") {
        control.title = "";
        delete control.dataset.profileReadonlyTitle;
      }
      if (viewModeLocked) {
        if (!control.title) {
          control.dataset.viewReadonlyTitle = "true";
          control.title = "Switch to Edit mode before changing this custom study.";
        }
      } else if (control.dataset.viewReadonlyTitle === "true") {
        control.title = "";
        delete control.dataset.viewReadonlyTitle;
      }
      setWorkflowDisabled(
        control,
        previewViewControl ? false : ((editLocked && !sharedFilePicker) || readonlyLocked || viewModeLocked)
      );
    }
  }

  for (const footer of document.querySelectorAll(".step-footer")) {
    footer.hidden = ![...footer.children].some((child) => (
      !child.hidden
      && (!sequentialEdit || child.matches("[data-continue-step], #accept-block-csvs, #save-study-profile"))
    ));
  }

  setWorkflowDisabled($("design-name"), profileReadonly || viewLocked || sequentialEdit);
  setWorkflowDisabled($("apply-profile-project"), profileReadonly || viewLocked || sequentialEdit);
  setWorkflowDisabled($("apply-design"), profileReadonly || viewLocked || sequentialEdit);
  updateActiveNav();
}

function renderPreviewTables() {
  renderTrialPreviewTable(state.trial_preview || []);
}

function renderRunSequenceTable(rows) {
  const exampleIndex = new Map();
  const examples = rows.map((row) => {
    const key = String(row.participant || row.participant_index || `row-${exampleIndex.size + 1}`);
    if (!exampleIndex.has(key)) exampleIndex.set(key, exampleIndex.size + 1);
    return { ...row, participant: `Example ${exampleIndex.get(key)}` };
  });
  fillTable("run-sequence-table", examples, ["participant", "part", "block_count", "block_order"]);
}

function renderTrialPreviewTable(trialRows, totalRows = trialRows.length) {
  const count = $("trial-count");
  const table = $("trial-table");
  if (!count || !table) return;
  const visibleCount = trialRows.length;
  count.textContent = totalRows > visibleCount
    ? `${visibleCount} of ${totalRows} shown`
    : `${visibleCount} shown`;
  fillTable("trial-table", trialRows, ["index", "family", "row", "folder", "file", "repetition", "duration", "soa_ms", "sequence"]);
}

function cartesianProduct(factors) {
  return factors.reduce((acc, factor) => acc.flatMap((prefix) => factor.map((choice) => [...prefix, choice])), [[]]);
}

function stripPreviewVariants(strip) {
  const factors = [];
  for (const element of strip.elements || []) {
    if (element.kind === "jitter") {
      const values = (element.jitter_values_ms || []).filter((value) => Number.isFinite(Number(value)));
      factors.push(values.map((value) => ({
        kind: "jitter",
        label: `${element.label || "Jitter"} (${value} ms)`,
        value,
      })));
    } else {
      const labels = audioBoxLabels(element);
      factors.push(labels.map((label) => ({ kind: "audio", label })));
    }
  }
  if (!factors.length || factors.some((factor) => !factor.length)) return [];
  return cartesianProduct(factors);
}

function previewVariantLabel(variant, family = "Audio-Tactile") {
  const labels = variant.map((choice) => family === "Baseline" && choice.kind === "audio" ? "Baseline tactile" : choice.label);
  if (family === "Baseline" && !labels.includes("Baseline tactile")) labels.push("Baseline tactile");
  return labels.join(" | ");
}

function previewVariantKey(variant) {
  return variant.map((choice) => choice.kind === "jitter" ? `jitter_${choice.value}ms` : choice.label).join(" + ");
}

function renderLiveTrialPreviewTables() {
  if (!state?.design?.protocol || !$("trial-table")) return;
  const pool = trialPoolCompositionEstimate();
  const rows = [];
  const pushRow = (row) => {
    if (rows.length < TRIAL_PREVIEW_LIMIT) rows.push(row);
  };
  let index = 1;
  for (const group of pool.groups) {
    for (const file of group.files) {
      const reps = repetitionForFile(file);
      for (let repetition = 1; repetition <= reps; repetition += 1) {
        pushRow({
          index,
          family: familyDisplayName(file.family),
          row: file.row_label || file.row_folder_name || "",
          folder: file.folder_name || group.folderName,
          file: file.source_file_name || "",
          repetition,
          duration: formatDurationMs(file.duration_ms || 0),
          soa_ms: file.soa_ms ?? "",
          sequence: file.sequence_labels || file.sequence_variant_key || "",
        });
        index += 1;
      }
    }
  }
  if (!pool.groups.length) {
    renderTrialPreviewTable([], 0);
    return;
  }
  renderTrialPreviewTable(rows, pool.totalTrials);
}

function fillTable(id, rows, keys) {
  const table = $(id);
  const body = table?.querySelector("tbody");
  if (!body) return;
  const labels = [...table.querySelectorAll("thead th")].map((header) => header.textContent.trim());
  body.innerHTML = "";
  for (const row of rows) {
    const tr = body.insertRow();
    const family = String(row.type || row.trial_type || "").toLowerCase();
    if (family.includes("baseline")) tr.classList.add("preview-baseline");
    else if (family.includes("catch")) tr.classList.add("preview-catch");
    else if (family.includes("audio")) tr.classList.add("preview-audio");
    for (const [index, key] of keys.entries()) {
      const cell = tr.insertCell();
      cell.dataset.label = labels[index] || humanize(key);
      cell.textContent = row[key] ?? "";
    }
  }
}

function renderJob(job) {
  const detail = job.result ? JSON.stringify(job.result, null, 2) : (job.error || job.message || "");
  return `
    <div class="job-item">
      <strong><span>${humanize(job.kind)}</span><span>${job.status}</span></strong>
      <div class="muted">${job.message || ""}</div>
      ${detail ? `<pre>${escapeHtml(detail)}</pre>` : ""}
    </div>
  `;
}

function collectPayload({ includeIngredientDraft = false } = {}) {
  const design = clone(state.design);
  const trajectoryControls = includeIngredientDraft
    ? currentTrajectoryControls()
    : { ...(state.trajectory_controls || currentTrajectoryControls()) };
  design.name = String(state.design?.name || "Untitled PPS design").trim() || "Untitled PPS design";
  design.noises = collectNoises();
  const audio = collectAudioFiles();
  design.custom_looming_files = audio.looming;
  design.prestimulus_files = audio.prestimulus;
  design.study_profile_reference_parameters = {
    ...(design.study_profile_reference_parameters || {}),
    loudness_policy: collectLoudnessPolicy(trajectoryControls)
  };
  const trialStrips = collectTrialStrips();
  const legacySpatial = design.protocol?.spatial_values_cm?.length
    ? design.protocol.spatial_values_cm
    : [trajectoryControls.end_distance_cm];
  const baselineStrategy = currentBaselineStrategy();
  const baselineTimings = baselineStrategyIsActive(baselineStrategy)
    ? parseIntegerList($("baseline-soa-values").value)
    : [];
  design.protocol = {
    ...(design.protocol || {}),
    repetitions_per_condition: Math.max(1, Math.round(numberValue("repetitions", 1))),
    soa_values_ms: parseIntegerList($("block-soa-values").value || $("soa-values").value),
    spatial_values_cm: legacySpatial,
    pair_spatial_values_with_soas: false,
    include_catch_trials: $("include-catch-trials")?.checked || false,
    catch_crosses_sequence_variants: design.protocol?.catch_crosses_sequence_variants !== false,
    catch_trial_percentage: numberValue("catch-percent", 0),
    include_auditory_only_trials: Boolean($("include-auditory-only-trials")?.checked),
    auditory_only_crosses_sequence_variants: design.protocol?.auditory_only_crosses_sequence_variants !== false,
    auditory_only_trial_percentage: Number(design.protocol?.auditory_only_trial_percentage || 0),
    auditory_only_trials_exact: design.protocol?.auditory_only_trials_exact ?? null,
    include_baseline_trials: baselineStrategyIsActive(baselineStrategy),
    baseline_strategy: baselineStrategy,
    baseline_trials_exact: baselineStrategyIsActive(baselineStrategy) ? (design.protocol?.baseline_trials_exact ?? null) : null,
    baseline_trial_percentage: baselineStrategyIsActive(baselineStrategy) ? numberValue("baseline-percent", 0) : 0,
    baseline_soa_values_ms: baselineTimings,
    baseline_custom_trial_mode: $("baseline-custom-audio-tactile")?.checked ? "audio_tactile" : "tactile_only",
    baseline_crosses_sequence_variants: design.protocol?.baseline_crosses_sequence_variants !== false,
    distribute_trial_pool_across_blocks: Boolean(design.protocol?.distribute_trial_pool_across_blocks),
    blocks: Math.max(1, Math.round(numberValue("block-count", numberValue("blocks", 1)))),
    random_seed: normalizedRandomizationSeed($("block-randomization-seed")?.value, normalizedRandomizationSeed(design.protocol?.random_seed)),
    participants: Math.max(1, Math.round(numberValue("participants", 1))),
    trial_strips: trialStrips
  };
  if (design.protocol.participant_order_policy?.algorithm === "seeded_factoradic_cycle.v1") {
    design.protocol.participant_order_policy.preview_count = design.protocol.participants;
    design.protocol.participant_order_policy.seed = design.protocol.random_seed;
  }
  return {
    participant_id: state.participant_id || (state.custom_workflow?.is_custom ? "" : "P001"),
    design,
    trajectory_controls: trajectoryControls,
    run_setup: {
      experiment_structure: currentExperimentStructure(),
      seed: design.protocol.random_seed,
      instruction_profile: collectRunInstructionProfile(),
    }
  };
}

function collectProfileRunPayload() {
  return {
    participant_id: state.participant_id || "P001"
  };
}

function currentExperimentStructure() {
  return document.querySelector('input[name="experiment-structure"]:checked')?.value || state.run_sequence_setup?.experiment_structure || "single";
}

function collectNoises() {
  return [...$("noise-list").querySelectorAll(".noise-source-card")].map((card) => {
    const field = (name) => card.querySelector(`[data-field="${name}"]`);
    return {
      label: field("label").value.trim() || "Noise",
      noise_type: field("noise_type").value,
      azimuth_deg: Number(field("azimuth_deg").value || 0),
      elevation_deg: Number(field("elevation_deg").value || 0),
      gain: Number(field("gain").value || 1),
      prebaked_path: field("prebaked_path")?.value.trim() || "",
      source_profile: field("source_profile")?.value.trim() || GOLD_STANDARD_SOURCE_PROFILE,
      source_profile_parameters: readJsonField(field("source_profile_parameters")) || {},
      motion_mode: field("motion_mode")?.value.trim() || "looming",
      trajectory_snapshot: readJsonField(field("trajectory_snapshot"))
    };
  });
}

function collectAudioFiles() {
  const result = { looming: [], prestimulus: [] };
  const cards = [
    ...$("noise-list").querySelectorAll(".audio-source-card"),
    ...$("audio-list").querySelectorAll(".audio-source-card"),
    ...$("snippet-list").querySelectorAll(".audio-source-card"),
  ];
  for (const card of cards) {
    const field = (name) => card.querySelector(`[data-field="${name}"]`);
    const role = field("audio_role").value;
    const item = {
      label: field("label").value.trim() || "Audio file",
      path: field("path").value.trim(),
      target_duration_s: Number(field("target_duration_s").value || 4),
      render_mode: role === "spatialize" ? "spatialize" : "preserve",
      tone_type: field("tone_type")?.value.trim() || "",
      gain: Number(field("gain").value || 1),
      placement: normalizeSnippetPlacement(field("placement")?.value),
      target_source_label: field("target_source_label")?.value.trim() || "",
      phase: field("phase")?.value.trim() || "",
      gap_s: Math.max(0, Number(field("gap_s")?.value || 0)),
      motion_mode: field("motion_mode")?.value || (role === "prestimulus" ? "stationary" : "looming"),
      display_color_hex: normalizeDisplayColorHex(field("display_color_hex")?.value) || sourceColor(field("tone_type")?.value || "custom_audio"),
      source_input_path: field("source_input_path")?.value.trim() || "",
      trajectory_snapshot: readJsonField(field("trajectory_snapshot"))
    };
    if (role === "prestimulus") {
      result.prestimulus.push(item);
    } else {
      result.looming.push(item);
    }
  }
  return result;
}

async function applyDesign() {
  if (!ensureEditableProject()) return;
  if (staticModeActive) {
    state.design = collectPayload().design;
    await window.PPSDesigner?.drafts?.save(state);
  } else {
    state = await designerApi.saveDesign(collectPayload());
  }
  markDesignerSaved();
  renderAll();
  updateViewer();
  const staleSegments = Object.values(state.project_segments || {}).filter((segment) => segment.status === "stale");
  showToast(staticModeActive ? "Browser draft saved locally" : staleSegments.length ? "Design saved; downstream segments need rebake" : "Design saved");
}

function revealWorkflowStep(stepId) {
  const segment = $(STEP_TARGETS[stepId] || stepId);
  if (segment?.classList.contains("collapsed")) {
    segment.querySelector(":scope > .segment-heading .segment-collapse-button")?.click();
  }
  scrollToStep(stepId);
}

async function continueWorkflowStep(stepId) {
  if (!ensureEditableProject()) return;
  if (workflowSaveInFlight) return;
  if (stepId === "stimulus" && ingredientEditorDirty) {
    if (!confirmDiscardIngredientDraft("continue to Trial Sequence Design")) return;
    discardIngredientEditorDraft();
    renderBakePanel();
  }
  const progress = normalizedDesignerProgress();
  if (stepId !== progress.edit_step) {
    showToast(`Save ${stepLabel(progress.edit_step)} first.`);
    revealWorkflowStep(progress.edit_step);
    return;
  }
  if (designerDirty && !confirmDownstreamInvalidation(`Saving ${stepLabel(stepId)}`, stepId)) return;
  workflowSaveInFlight = true;
  const button = document.querySelector(`[data-continue-step="${stepId}"]`);
  if (button) button.disabled = true;
  let actionResult = null;
  const payload = collectPayload();
  payload.workflow_action = {
    type: "save_and_continue",
    step_id: stepId,
    expected_revision: progress.revision,
  };
  try {
    if (staticModeActive) {
      state.design = payload.design;
      refreshHostedWorkflowSteps();
      const step = getWorkflowStep(stepId) || profileStepStatus(stepId);
      if (step?.complete) {
        const next = NEXT_STEP[stepId] || "run";
        setHostedDesignerProgress({
          ...progress,
          edit_step: next,
          confirmed_steps: WORKFLOW_STEPS.filter((id) => new Set([...progress.confirmed_steps, stepId]).has(id)),
          needs_review_steps: progress.needs_review_steps.filter((id) => id !== stepId),
          revision: progress.revision + 1,
        });
        actionResult = { advanced: true, unlocked_step: next, missing: [] };
      } else {
        actionResult = { advanced: false, missing: [...(step?.missing || ["Complete this segment before continuing."])] };
      }
      await window.PPSDesigner?.drafts?.save(state);
    } else {
      state = await designerApi.saveDesign(payload);
      actionResult = state.workflow_action_result || null;
    }
    markDesignerSaved();
    renderAll();
    updateViewer();
    if (actionResult?.advanced) {
      const next = actionResult.unlocked_step || NEXT_STEP[stepId];
      showToast(`${stepLabel(stepId)} saved. ${stepLabel(next)} is ready to edit.`);
      if (next) revealWorkflowStep(next);
      return;
    }
    const missing = actionResult?.missing || getWorkflowStep(stepId)?.missing || [];
    showToast(missing.join(" ") || `${stepLabel(stepId)} saved; complete its required output to continue.`);
    revealWorkflowStep(stepId);
  } finally {
    workflowSaveInFlight = false;
    if (button) button.disabled = false;
    renderWorkflow();
  }
}

async function reopenWorkflowStep(stepId) {
  if (!ensureEditableProject() || workflowSaveInFlight) return;
  const progress = normalizedDesignerProgress();
  if (!progress.confirmed_steps.includes(stepId)) return;
  if (!confirmDiscardUnsaved(`reopen ${stepLabel(stepId)}`)) return;
  const later = progress.confirmed_steps.filter(
    (id) => WORKFLOW_STEPS.indexOf(id) > WORKFLOW_STEPS.indexOf(stepId)
  );
  const consequence = later.length
    ? ` Later saved segments (${later.map(stepLabel).join(", ")}) will need review.`
    : "";
  if (!window.confirm(`Reopen ${stepLabel(stepId)} for editing?${consequence}`)) return;
  workflowSaveInFlight = true;
  try {
    if (staticModeActive) {
      const stored = await window.PPSDesigner?.drafts?.load?.().catch(() => null);
      if (stored?.design && stored?.project?.project_id === state?.project?.project_id) state = clone(stored);
      const refreshed = normalizedDesignerProgress();
      const targetIndex = WORKFLOW_STEPS.indexOf(stepId);
      setHostedDesignerProgress({
        ...refreshed,
        edit_step: stepId,
        confirmed_steps: refreshed.confirmed_steps.filter((id) => WORKFLOW_STEPS.indexOf(id) < targetIndex),
        needs_review_steps: WORKFLOW_STEPS.filter((id) => (
          refreshed.needs_review_steps.includes(id)
          || (refreshed.confirmed_steps.includes(id) && WORKFLOW_STEPS.indexOf(id) > targetIndex)
        )),
        revision: refreshed.revision + 1,
      });
      await window.PPSDesigner?.drafts?.save(state);
    } else {
      state = await designerApi.saveDesign({
        workflow_action: {
          type: "reopen",
          step_id: stepId,
          expected_revision: progress.revision,
        },
      });
    }
    markDesignerSaved();
    renderAll();
    updateViewer();
    revealWorkflowStep(stepId);
    showToast(`${stepLabel(stepId)} reopened. Later segments are read-only until reviewed again.`);
  } finally {
    workflowSaveInFlight = false;
    renderWorkflow();
  }
}

async function loadTemplate() {
  if (templateLoadInFlight) return;
  const select = $("template-select");
  if (!confirmDiscardUnsaved("load another profile")) {
    renderStudy();
    return;
  }
  if (designerDirty) markDesignerSaved();
  const choice = selectedProfileChoice(select.value);
  const id = choice.id;
  if (!id) return;
  if (choice.kind === "custom") {
    await loadCustomProject(id);
    return;
  }
  if (staticModeActive) {
    if (choice.kind === "manual") {
      renderStudy();
      showToast("Start the local companion backend before creating custom studies.");
      return;
    }
    state = await staticStateForTemplate(id);
    staticModeActive = true;
    resetEditMode();
    setConnectionStatus(false, "static profile");
    renderAll();
    updateViewer();
    showToast("Static profile loaded from committed GitHub assets");
    return;
  }
  templateLoadInFlight = true;
  select.disabled = true;
  try {
    state = await designerApi.loadTemplate(id);
    await applyStaticProfileInspectionPreview();
    resetEditMode();
    renderAll();
    updateViewer();
    showToast(choice.kind === "manual" ? "Custom design started" : "Profile loaded");
  } finally {
    templateLoadInFlight = false;
    $("template-select").disabled = false;
  }
}

async function loadCustomProject(projectIdOverride = "") {
  const select = $("existing-custom-project");
  const projectId = projectIdOverride || select?.value || "";
  if (!projectId) {
    showToast("Choose a custom study to open");
    return;
  }
  state = await designerApi.loadProject(projectId);
  resetEditMode();
  renderAll();
  updateViewer();
  showToast("Custom study opened");
  scrollToStep(state.custom_workflow?.current_step || "study");
}

async function exportDataAcquisitionFolder() {
  const payload = isProfileReadonlyMode() ? collectProfileRunPayload() : collectPayload();
  state = await designerApi.exportDataAcquisition(payload);
  renderAll();
  updateViewer();
  showToast("Data acquisition folder linked");
}

function activeModalBackdrop() {
  return [...document.querySelectorAll(".modal-backdrop")].find((modal) => !modal.hidden) || null;
}

function syncModalEnvironment() {
  const open = Boolean(activeModalBackdrop());
  const shell = document.querySelector(".app-shell");
  const skipLink = document.querySelector(".skip-link");
  if (shell) shell.inert = open;
  if (skipLink) skipLink.inert = open;
  document.body.classList.toggle("modal-open", open);
}

function trapModalFocus(event) {
  if (event.key !== "Tab") return false;
  const modal = activeModalBackdrop();
  const dialog = modal?.querySelector('[role="dialog"]');
  if (!dialog) return false;
  const focusable = [...dialog.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => !element.hidden && element.getClientRects().length > 0);
  if (!focusable.length) {
    event.preventDefault();
    dialog.focus();
    return true;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
    event.preventDefault();
    first.focus();
  }
  return true;
}

function renderAudioColorPreview(value) {
  const normalized = normalizeDisplayColorHex(value);
  const error = $("audio-color-error");
  if (!normalized) {
    if (error) {
      error.textContent = "Enter a colour as #RRGGBB using six hexadecimal digits.";
      error.hidden = false;
    }
    return false;
  }
  if (error) error.hidden = true;
  $("audio-color-preview").style.background = normalized;
  $("audio-color-preview-value").textContent = normalized;
  return true;
}

function openAudioColorModal() {
  if (!pendingBakeRecipe || pendingBakeRecipe.kind === "generated_noise") return;
  audioColorModalReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const color = normalizeDisplayColorHex(pendingDisplayColorHex, "#1C7A86");
  $("audio-color-native").value = color.toLowerCase();
  $("audio-color-hex").value = color;
  renderAudioColorPreview(color);
  $("audio-color-modal").hidden = false;
  syncModalEnvironment();
  window.requestAnimationFrame(() => $("audio-color-native").focus());
}

function closeAudioColorModal() {
  $("audio-color-modal").hidden = true;
  $("audio-color-error").hidden = true;
  syncModalEnvironment();
  if (audioColorModalReturnFocus && document.contains(audioColorModalReturnFocus)) audioColorModalReturnFocus.focus();
  audioColorModalReturnFocus = null;
}

function applyAudioColorModal() {
  const normalized = normalizeDisplayColorHex($("audio-color-hex").value);
  if (!normalized || !renderAudioColorPreview(normalized)) return false;
  pendingDisplayColorHex = normalized;
  pendingBakeRecipe.display_color_hex = normalized;
  if (pendingBakeRecipe.audio) pendingBakeRecipe.audio.display_color_hex = normalized;
  ingredientEditorDirty = true;
  renderBakePanel();
  updateViewer();
  closeAudioColorModal();
  return true;
}

function openSegmentInfoModal(stepId, trigger = null) {
  const info = SEGMENT_INFO[stepId];
  if (!info) return;
  const showNoiseGuide = stepId === "trials";
  segmentInfoModalReturnFocus = trigger instanceof HTMLElement
    ? trigger
    : (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  $("segment-info-kicker").textContent = info.kicker;
  $("segment-info-modal-title").textContent = info.title;
  $("segment-info-purpose").textContent = info.purpose;
  $("segment-info-inputs").textContent = info.inputs;
  const backend = $("segment-info-backend");
  backend.replaceChildren();
  if (info.backendLinkLabel) {
    backend.append(document.createTextNode(info.backendBeforeLink || ""));
    const link = document.createElement("a");
    link.textContent = info.backendLinkLabel;
    configureTemplateDirectoryLink(link);
    backend.append(link, document.createTextNode(info.backendAfterLink || ""));
  } else {
    backend.textContent = info.backend || "";
  }
  $("segment-info-next").textContent = info.output || "";
  const noteCard = $("segment-info-note-card");
  const note = $("segment-info-note");
  noteCard.hidden = !info.note;
  note.textContent = info.note || "";
  $("segment-info-noise-guide").hidden = !showNoiseGuide;
  document.querySelector(".segment-info-card")?.classList.toggle("noise-guide-active", showNoiseGuide);
  $("segment-info-modal").hidden = false;
  syncModalEnvironment();
  window.setTimeout(() => $("segment-info-modal-close")?.focus(), 0);
}

function configureTemplateDirectoryLink(link) {
  const directory = state?.template_directory || {};
  const localPath = !staticModeActive ? String(directory.path || "") : "";
  if (localPath) {
    link.href = "#";
    link.title = localPath;
    link.removeAttribute("target");
    link.removeAttribute("rel");
    link.onclick = (event) => {
      event.preventDefault();
      openLocalFolder(localPath).catch(reportError);
    };
    return;
  }
  link.href = String(directory.url || HOSTED_TEMPLATE_DIRECTORY_URL);
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.title = "Open the template directory on GitHub";
  link.onclick = null;
}

function closeSegmentInfoModal() {
  const modal = $("segment-info-modal");
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  syncModalEnvironment();
  if (segmentInfoModalReturnFocus && document.contains(segmentInfoModalReturnFocus)) {
    segmentInfoModalReturnFocus.focus();
  }
  segmentInfoModalReturnFocus = null;
}

function defaultCustomStudyName() {
  const sourceId = state?.design?.study_profile_id
    || state?.project?.project_id
    || state?.design?.study_profile_reference_parameters?.customized_from_profile_id
    || "source";
  return `${state?.design?.name || state?.design?.study_profile_title || "PPS design"} custom [${sourceId}]`;
}

function openCustomizeModal() {
  if (!state || !isProfileReadonlyMode()) return;
  customizeModalMode = "copy";
  const modal = $("customize-modal");
  const input = $("customize-study-name");
  const source = $("customize-source-label");
  const error = $("customize-error");
  $("customize-modal-kicker").textContent = "Custom Profile Copy";
  $("customize-modal-title").textContent = "Name This Custom Copy";
  $("customize-submit").textContent = "Create Custom Copy";
  customizeModalReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  input.value = defaultCustomStudyName();
  source.textContent = state?.design?.study_profile_title || state?.design?.name || "";
  source.hidden = false;
  error.hidden = true;
  error.textContent = "";
  modal.hidden = false;
  syncModalEnvironment();
  window.setTimeout(() => {
    input.focus();
    input.select();
  }, 0);
}

function openNewCustomDesignModal() {
  if (!state) {
    showToast("Wait for the profile catalogue to load.");
    return;
  }
  customizeModalMode = "blank";
  const modal = $("customize-modal");
  const input = $("customize-study-name");
  const source = $("customize-source-label");
  const error = $("customize-error");
  customizeModalReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  $("customize-modal-kicker").textContent = "New Custom Design";
  $("customize-modal-title").textContent = "Start With a Clean Slate";
  $("customize-submit").textContent = "Create Design";
  input.value = "";
  source.textContent = "";
  source.hidden = true;
  error.hidden = true;
  error.textContent = "";
  modal.hidden = false;
  syncModalEnvironment();
  window.setTimeout(() => input.focus(), 0);
}

function closeCustomizeModal() {
  const modal = $("customize-modal");
  if (modal.hidden) return;
  modal.hidden = true;
  syncModalEnvironment();
  if (customizeModalReturnFocus && document.contains(customizeModalReturnFocus)) {
    customizeModalReturnFocus.focus();
  }
  customizeModalReturnFocus = null;
}

function showCustomizeError(message) {
  const error = $("customize-error");
  error.textContent = message;
  error.hidden = false;
}

async function submitCustomizeModal() {
  const input = $("customize-study-name");
  const name = input.value.trim();
  if (!name) {
    showCustomizeError("Enter a study name.");
    input.focus();
    return;
  }
  $("customize-submit").disabled = true;
  try {
    if (customizeModalMode === "blank") {
      await createBlankCustomProject(name);
    } else {
      await customizeAsNewProject(name);
    }
    closeCustomizeModal();
  } catch (error) {
    showCustomizeError(error.message || String(error));
  } finally {
    $("customize-submit").disabled = false;
  }
}

function hostedBlankDesign(name) {
  const seed = 20250604;
  const design = clone(state?.design || {});
  design.name = name;
  design.study_profile_id = "";
  design.study_profile_title = "";
  design.study_profile_notes = "";
  design.study_profile_reference_parameters = {
    dashboard_mode: "custom",
    profile_status: "draft",
    created_from: "blank_design",
    capability_provenance: "hosted_compose",
  };
  design.noises = [];
  design.custom_looming_files = [];
  design.prestimulus_files = [];
  design.trajectory = {
    start_radius_m: 1.1,
    end_radius_m: 0.1,
    path_direction: "approach",
    coordinate_mode: "polar",
    start_x_m: null,
    start_y_m: null,
    start_z_m: null,
    end_x_m: null,
    end_y_m: null,
    end_z_m: null,
    path_length_m: 1,
    propagation_speed_mps: 1 / 3,
    azimuth_start_deg: 0,
    azimuth_end_deg: 0,
    elevation_deg: 0,
    padding_pre_s: 0.5,
    padding_post_s: 0.5,
    sample_rate: 44100,
    use_inverse_square: true,
  };
  design.protocol = {
    ...(design.protocol || {}),
    repetitions_per_condition: 1,
    trial_pool_repetition_defaults: {},
    soa_values_ms: [],
    spatial_values_cm: [],
    pair_spatial_values_with_soas: false,
    auditory_motion_directions: ["looming"],
    tactile_sites: ["hand"],
    include_catch_trials: false,
    catch_trial_percentage: 0,
    catch_trials_exact: null,
    include_auditory_only_trials: false,
    auditory_only_trial_percentage: 0,
    auditory_only_trials_exact: null,
    include_baseline_trials: false,
    baseline_strategy: "",
    baseline_trials_exact: null,
    baseline_trial_percentage: 0,
    baseline_soa_values_ms: [],
    baseline_custom_trial_mode: "tactile_only",
    blocks: 1,
    block_specs: [],
    distribute_trial_pool_across_blocks: false,
    trial_strips: [],
    participants: 1,
    random_seed: seed,
    participant_order_policy: {
      schema: "pps-participant-order-policy.v1",
      algorithm: "seeded_factoradic_cycle.v1",
      seed,
      preview_count: 12,
    },
  };
  return design;
}

function browserDraftId(name) {
  const slug = String(name || "profile").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || "profile";
  return `browser_${slug}_${Date.now().toString(36)}`;
}

async function createBlankCustomProject(name) {
  const cleanName = String(name || "").trim();
  if (!cleanName) {
    openNewCustomDesignModal();
    return;
  }
  if (staticModeActive) {
    const profileId = browserDraftId(cleanName);
    state = clone(state);
    state.design = hostedBlankDesign(cleanName);
    state.selected_template = CUSTOM_TEMPLATE_ID;
    state.project = {
      project_id: profileId,
      project_kind: "browser_draft",
      project_label: cleanName,
      source_template_id: "",
      source_profile_id: "",
      created_at: new Date().toISOString(),
      project_dir: "",
      profile_dir: "",
      segment_folders: {},
    };
    const labels = {
      study: "Study Profile",
      stimulus: "Stimulus Design",
      trials: "Trial Sequence Design",
      baseline: "Baseline and Tactile Trial Design",
      block: "Trial Composition",
      schedule: "Block CSV Preview",
      run: "Profile Validation and Save",
    };
    state.custom_workflow = {
      is_custom: true,
      is_finalized: false,
      current_step: "stimulus",
      ready_to_render: false,
      ready_to_prepare: false,
      missing: ["Define and bake at least one stimulus ingredient."],
      steps: WORKFLOW_STEPS.map((id) => ({
        id,
        label: labels[id],
        complete: id === "study",
        missing: id === "study" ? [] : [id === "stimulus" ? "Define and bake at least one stimulus ingredient." : "Complete the preceding segment."],
      })),
    };
    initializeHostedDesignerProgress("stimulus");
    state.trajectory_controls = {
      start_distance_cm: 110,
      end_distance_cm: 10,
      start_rotation_deg: 0,
      end_rotation_deg: 0,
      movement_duration_s: 3,
      start_hold_s: 0.5,
      end_hold_s: 0.5,
    };
    state.viewer_payload = staticViewerPayload(state.design);
    state.project_segments = Object.fromEntries(Object.entries(STEP_SEGMENT_FOLDERS).map(([id, folderName]) => [folderName, {
      folder_name: folderName,
      folder_path: "",
      status: id === "study" ? "ready" : "missing",
      manifest_exists: id === "study",
      last_validation_message: id === "study" ? "Browser draft created." : "Complete the preceding segment.",
    }]));
    state.trial_sequence_bake = {};
    state.trial_file_bake = {};
    state.trial_pool_bake = {};
    state.block_csv_preview = {};
    state.run_sequence_setup = {};
    state.preload_inventory = {};
    await window.PPSDesigner?.drafts?.save(state);
  } else {
    state = await designerApi.createCustomProject({ name: cleanName });
  }
  editModeActive = true;
  markDesignerSaved();
  renderAll();
  updateViewer();
  showToast(staticModeActive ? "Clean browser draft created; nothing was uploaded" : "Clean custom design created");
  scrollToStep("stimulus");
}

async function customizeAsNewProject(name) {
  const cleanName = String(name || "").trim();
  if (!cleanName) {
    openCustomizeModal();
    return;
  }
  if (staticModeActive) {
    const sourceId = state.design?.study_profile_id || state.selected_template || state.project?.project_id || "source";
    const derivedName = cleanName.toLowerCase().includes(sourceId.toLowerCase()) ? cleanName : `${cleanName} [${sourceId}]`;
    const profileId = browserDraftId(derivedName);
    state = clone(state);
    state.design.name = derivedName;
    state.design.study_profile_id = "";
    state.design.study_profile_title = "";
    state.design.study_profile_reference_parameters = {
      ...(state.design.study_profile_reference_parameters || {}),
      dashboard_mode: "custom",
      customized_from_profile_id: sourceId,
      capability_provenance: "hosted_compose",
      profile_status: "draft"
    };
    state.design.protocol.participant_order_policy = state.design.protocol.participant_order_policy?.algorithm
      ? state.design.protocol.participant_order_policy
      : {
          schema: "pps-participant-order-policy.v1",
          algorithm: "seeded_factoradic_cycle.v1",
          seed: normalizedRandomizationSeed(state.design.protocol.random_seed),
          preview_count: Math.max(1, Math.min(100, Number(state.design.protocol.participants || 12))),
        };
    state.selected_template = CUSTOM_TEMPLATE_ID;
    state.custom_workflow = { ...(state.custom_workflow || {}), is_custom: true, is_finalized: false };
    initializeHostedDesignerProgress("stimulus");
    state.project = {
      ...(state.project || {}),
      project_id: profileId,
      project_kind: "browser_draft",
      project_label: derivedName,
      source_template_id: sourceId,
      source_profile_id: sourceId,
      created_at: new Date().toISOString(),
      project_dir: "",
      profile_dir: "",
    };
    await window.PPSDesigner?.drafts?.save(state);
  } else {
    state = await designerApi.customizeProject({ name: cleanName });
  }
  editModeActive = true;
  markDesignerSaved();
  renderAll();
  updateViewer();
  showToast(staticModeActive ? "Browser draft created; nothing was uploaded" : "Custom project created");
  scrollToStep(state.custom_workflow?.edit_step || "stimulus");
}

async function startBakeStimulus() {
  if (!ensureEditableProject()) return;
  const recipe = collectBakeRecipe();
  if (!recipe) {
    showToast("Choose a source and enter a stimulus name");
    return;
  }
  if (!confirmDownstreamInvalidation(recipe.action === "remake" ? "Remaking a Segment 1 ingredient" : "Creating a Segment 1 ingredient", "stimulus")) return;
  if (staticModeActive) {
    if (recipe.kind !== "fixed_audio" || recipe.action === "remake") {
      showToast(STATIC_COMPANION_REQUIRED_MESSAGE);
      return;
    }
    state.design.prestimulus_files = state.design.prestimulus_files || [];
    state.design.prestimulus_files.push({
      ...recipe.audio,
      label: recipe.label,
      render_mode: "preserve",
      motion_mode: "stationary",
      display_color_hex: recipe.display_color_hex,
      trajectory_snapshot: {},
      placement: "before",
      target_source_label: "",
      phase: "",
      gap_s: 0,
    });
    await window.PPSDesigner?.drafts?.save(state);
    selectedIngredientLabel = recipe.label;
    ingredientEditorDirty = false;
    ingredientEditorMode = "inspect";
    pendingBakeRecipe = null;
    markDesignerSaved();
    renderAll();
    updateViewer();
    showToast("Fixed clip created in this browser draft");
    return;
  }
  const payload = collectPayload({ includeIngredientDraft: true });
  payload.bake_recipe = recipe;
  const job = await designerApi.bakeStimulus(payload);
  ingredientEditorDirty = false;
  markDesignerSaved();
  showToast(recipe.action === "remake" ? "Atomic ingredient remake started" : "Ingredient creation started");
  pollJob(job.job_id);
  await loadState();
}

async function startBakeTrialSequences() {
  if (!ensureEditableProject()) return;
  if (!confirmDownstreamInvalidation("Baking Segment 2 trial sequences", "trials")) return;
  const segment1 = projectSegment("1_core_audio_ingredients");
  const sequence = sequenceVariantEstimate();
  if (segment1.status !== "ready") {
    showToast("Bake or import Segment 1 ingredients first");
    return;
  }
  if (sequence.variantCount <= 0) {
    showToast("Add at least one complete trial sequence row");
    return;
  }
  const payload = collectPayload();
  payload.bake_recipe = { kind: "trial_sequence_batch", label: "2_trial_sequence_designs" };
  const job = await designerApi.bakeStimulus(payload);
  markDesignerSaved();
  showToast("Trial sequence bake started");
  pollJob(job.job_id);
  await loadState();
}

async function startBakeTrialFiles() {
  if (!ensureEditableProject()) return;
  if (!confirmDownstreamInvalidation("Baking Segment 3 baseline/tactile trial files", "baseline")) return;
  if (projectSegment("2_trial_sequence_designs").status !== "ready") {
    showToast("Bake Segment 2 trial sequences first");
    return;
  }
  const payload = collectPayload();
  payload.bake_recipe = { kind: "audiotactile_trial_batch", label: "3_tactile_and_baseline_trials" };
  const job = await designerApi.bakeStimulus(payload);
  markDesignerSaved();
  showToast("Baseline/tactile trial bake started");
  pollJob(job.job_id);
  await loadState();
}

function trialPoolBakeRecipe() {
  const exactFamilyCounts = trialPoolExactFamilyCounts(state?.design?.protocol || {});
  const recipe = {
    kind: "trial_repetition_pool",
    label: "4_trial_repetition_pool",
    default_repetitions: normalizeRepetitionValue(trialPoolRepetitionDraft.defaultRepetitions || 0, 0),
    family_repetitions: { ...trialPoolRepetitionDraft.familyRepetitions },
    folder_repetitions: { ...trialPoolRepetitionDraft.folderRepetitions },
    file_repetition_overrides: { ...trialPoolRepetitionDraft.fileRepetitionOverrides },
    fractional_seed: normalizedRandomizationSeed(trialPoolRepetitionDraft.fractionalSeed ?? state?.design?.protocol?.random_seed),
  };
  if (Object.keys(exactFamilyCounts).length) recipe.exact_family_trial_counts = exactFamilyCounts;
  return recipe;
}

async function startBakeTrialPool() {
  if (!ensureEditableProject()) return;
  if (!confirmDownstreamInvalidation("Baking Segment 4 trial pool CSV", "block")) return;
  if (projectSegment("3_tactile_and_baseline_trials").status !== "ready") {
    showToast("Bake Segment 3 trial files first");
    return;
  }
  if (trialPoolCompositionEstimate().totalTrials <= 0) {
    showToast("Set at least one repetition before baking the trial pool");
    return;
  }
  const payload = collectPayload();
  payload.bake_recipe = trialPoolBakeRecipe();
  const job = await designerApi.bakeStimulus(payload);
  markDesignerSaved();
  showToast("Trial pool CSV bake started");
  pollJob(job.job_id);
  await loadState();
}

async function startBakeBlockCsvs() {
  if (!ensureEditableProject()) return;
  if (!confirmDownstreamInvalidation("Regenerating Segment 5 block CSVs", "schedule")) return;
  if (projectSegment("4_trial_repetition_pool").status !== "ready") {
    showToast("Bake Segment 4 trial pool first");
    return;
  }
  if (projectSegment("5_block_csv_preview").accepted || state.block_csv_preview?.accepted) {
    if (!window.confirm("Reopen the accepted Segment 5 blocks and generate a new set?")) return;
    if (staticModeActive) {
      showToast("Block generation needs the installed PPS Designer. Export this browser draft to continue locally.");
      return;
    }
    state = await designerApi.editBlockCsv();
  }
  const blockCount = Math.max(1, Math.round(numberValue("block-count", numberValue("blocks", 1))));
  const seed = normalizedRandomizationSeed($("block-randomization-seed")?.value, normalizedRandomizationSeed(state?.design?.protocol?.random_seed));
  if ($("blocks")) $("blocks").value = blockCount;
  if ($("block-randomization-seed")) $("block-randomization-seed").value = seed;
  const blockButton = $("regenerate-block-csvs");
  if (blockButton) blockButton.disabled = true;
  setBlockProgress(0, blockCount, "regenerating block CSVs");
  try {
    const payload = collectPayload();
    payload.bake_recipe = {
      kind: "block_csv_preview",
      label: "5_block_csv_preview",
      block_count: blockCount,
      seed,
    };
    const job = await designerApi.bakeStimulus(payload);
    markDesignerSaved();
    setBlockProgress(job.progress_current || 0, job.progress_total || blockCount, job.progress_label || "queued");
    showToast("Block regeneration started");
    pollJob(job.job_id);
    await loadState();
  } catch (error) {
    setBlockProgress(0, blockCount, error.message || "block CSV bake failed");
    throw error;
  } finally {
    if (blockButton) blockButton.disabled = projectSegment("4_trial_repetition_pool").status !== "ready" || Boolean(projectSegment("5_block_csv_preview").accepted || state.block_csv_preview?.accepted);
  }
}

async function acceptBlockCsvs() {
  if (!ensureEditableProject()) return;
  const segment5 = projectSegment("5_block_csv_preview");
  if (segment5.status !== "ready") {
    showToast("Regenerate blocks first");
    return;
  }
  const acceptButton = $("accept-block-csvs");
  if (acceptButton) acceptButton.disabled = true;
  const progress = normalizedDesignerProgress();
  if (progress.edit_step !== "schedule") {
    showToast(`Save ${stepLabel(progress.edit_step)} first.`);
    if (acceptButton) acceptButton.disabled = false;
    return;
  }
  if (staticModeActive) {
    state.design = collectPayload().design;
    state.block_csv_preview = { ...(state.block_csv_preview || {}), accepted: true };
    const segment = state.project_segments?.[STEP_SEGMENT_FOLDERS.schedule];
    if (segment) segment.accepted = true;
    refreshHostedWorkflowSteps();
    const schedule = getWorkflowStep("schedule");
    if (!schedule?.complete) {
      showToast((schedule?.missing || ["Complete Segment 5 before continuing."]).join(" "));
      if (acceptButton) acceptButton.disabled = false;
      renderAll();
      return;
    }
    setHostedDesignerProgress({
      ...progress,
      edit_step: "run",
      confirmed_steps: WORKFLOW_STEPS.filter((id) => new Set([...progress.confirmed_steps, "schedule"]).has(id)),
      needs_review_steps: progress.needs_review_steps.filter((id) => id !== "schedule"),
      revision: progress.revision + 1,
    });
    await window.PPSDesigner?.drafts?.save(state);
    state.workflow_action_result = { type: "accept_and_continue", advanced: true, unlocked_step: "run" };
  } else {
    const payload = collectPayload();
    payload.workflow_action = {
      type: "accept_and_continue",
      step_id: "schedule",
      expected_revision: progress.revision,
    };
    state = await designerApi.acceptBlockCsv(payload);
  }
  markDesignerSaved();
  renderAll();
  const advanced = state.workflow_action_result?.advanced === true;
  showToast(advanced
    ? "Blocks accepted and saved. Profile validation is ready."
    : (state.workflow_action_result?.missing || ["Blocks were accepted, but Segment 5 still needs review."]).join(" "));
  revealWorkflowStep(advanced ? "run" : "schedule");
}

function csvEscape(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function rowsToCsv(rows, columns) {
  return [
    columns.map(csvEscape).join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column] ?? "")).join(",")),
  ].join("\r\n") + "\r\n";
}

function downloadTextFile(fileName, text, mimeType) {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function blockRandomizationDownloadStem(preview) {
  const profile = slugify(state?.design?.study_profile_id || state?.design?.name || "pps_profile") || "pps_profile";
  const seed = String(preview?.randomization_seed || state?.design?.protocol?.random_seed || "seed");
  return `${profile}_segment5_seed${seed}`;
}

function blockRandomizationRows(preview) {
  return (preview?.blocks || []).flatMap((block) => block.preview_rows || []);
}

function blockRandomizationManifestForDownload(preview, columns) {
  return {
    schema: preview.schema || "pps-block-csv-preview.v1",
    download_kind: "segment5_block_randomization",
    downloaded_at: new Date().toISOString(),
    profile_id: state?.design?.study_profile_id || "",
    profile_title: state?.design?.study_profile_title || state?.design?.name || "",
    status: preview.status || projectSegment("5_block_csv_preview").status || "",
    accepted: Boolean(preview.accepted || projectSegment("5_block_csv_preview").accepted),
    block_count: preview.block_count || (preview.blocks || []).length,
    csv_count: preview.csv_count || (preview.blocks || []).length,
    total_trials: preview.total_trials || preview.total_count || blockRandomizationRows(preview).length,
    randomization_seed: preview.randomization_seed || state?.design?.protocol?.random_seed || "",
    randomization_strategy: preview.randomization_strategy || BLOCK_RANDOMIZATION_STRATEGY,
    max_consecutive_same_feature: preview.max_consecutive_same_feature || BLOCK_RANDOMIZATION_MAX_CONSECUTIVE_FEATURE,
    balancing_strategy: preview.balancing_strategy || "",
    row_sequence_strategy: preview.row_sequence_strategy || "cycle_preserved_segment_row_order_within_each_block",
    row_order: preview.row_order || [],
    csv_columns: columns,
    blocks: (preview.blocks || []).map((block) => ({
      block_index: block.block_index,
      block_label: block.block_label,
      csv_file_name: block.csv_file_name,
      trial_count: block.trial_count,
      duration_ms: block.duration_ms,
      family_counts: block.family_counts || {},
      row_label_counts: block.row_label_counts || {},
      soa_counts: block.soa_counts || {},
      noise_type_counts: block.noise_type_counts || {},
    })),
  };
}

async function downloadBlockRandomization() {
  const preview = state?.block_csv_preview || {};
  const blocks = preview.blocks || [];
  if (!blocks.length) {
    showToast("Generate blocks first");
    return;
  }
  const truncatedBlock = blocks.find((block) => {
    const previewCount = (block.preview_rows || []).length;
    const trialCount = Number(block.trial_count || previewCount);
    return previewCount < trialCount;
  });
  if (truncatedBlock) {
    showToast("This preview is truncated; use the local Segment 5 CSV files for the full randomization");
    return;
  }
  const sourceColumns = Array.isArray(preview.csv_columns) && preview.csv_columns.length
    ? preview.csv_columns
    : BLOCK_CSV_DOWNLOAD_COLUMNS;
  const filteredColumns = sourceColumns
    .filter((column) => BLOCK_CSV_DOWNLOAD_COLUMNS.includes(column));
  const columns = filteredColumns.length ? filteredColumns : BLOCK_CSV_DOWNLOAD_COLUMNS;
  const rows = blockRandomizationRows(preview);
  if (!rows.length) {
    showToast("Generate blocks first");
    return;
  }
  const stem = blockRandomizationDownloadStem(preview);
  downloadTextFile(`${stem}.csv`, rowsToCsv(rows, columns), "text/csv;charset=utf-8");
  window.setTimeout(() => {
    const manifest = blockRandomizationManifestForDownload(preview, columns);
    downloadTextFile(`${stem}_manifest.json`, JSON.stringify(manifest, null, 2) + "\n", "application/json;charset=utf-8");
  }, 120);
  showToast("Segment 5 randomization downloaded");
}

function scheduleRunSequencePreview() {
  window.clearTimeout(runSequencePreviewTimer);
  renderRun();
  runSequencePreviewTimer = window.setTimeout(() => {
    previewRunSequence().catch(reportError);
  }, 300);
}

async function previewRunSequence() {
  if (!ensureEditableProject()) return;
  if (staticModeActive) {
    state.design = collectPayload().design;
    refreshHostedWorkflowSteps();
    state.run_sequence_setup = staticRunSetupPreview(state.design, {
      finished_profile: Boolean(getWorkflowStep("run")?.complete),
      message: "Browser-local order preview",
    });
    renderAll();
    return;
  }
  state = await designerApi.previewRunSequence(collectPayload());
  renderAll();
}

async function regenerateRunSequence() {
  if (!ensureEditableProject()) return;
  await previewRunSequence();
  showToast("Order preview refreshed. Save by locking the profile when review is complete.");
}

async function ensureLocalBackendState() {
  if (!state) {
    await loadState();
  }
  if (!state || staticModeActive) {
    showToast(STATIC_COMPANION_REQUIRED_MESSAGE);
    return false;
  }
  return true;
}

async function savePreparedStudyProfile() {
  if (staticModeActive) {
    state.design = collectPayload().design;
    refreshHostedWorkflowSteps();
    const progress = normalizedDesignerProgress();
    const required = WORKFLOW_STEPS.slice(0, -1);
    if (
      progress.edit_step !== "run"
      || required.some((stepId) => !progress.confirmed_steps.includes(stepId))
      || progress.needs_review_steps.length
    ) {
      showToast("Save each segment in order and resolve reopened reviews before locking this profile.");
      return;
    }
    if (!getWorkflowStep("run")?.complete) {
      showToast("Complete every required segment before finalizing this profile.");
      return;
    }
    state.design.study_profile_reference_parameters.profile_status = "finalized";
    state.design.study_profile_reference_parameters.finalized_at = new Date().toISOString();
    state.custom_workflow.is_finalized = true;
    editModeActive = false;
    await window.PPSDesigner?.drafts?.save(state);
    markDesignerSaved();
    renderAll();
    showToast("Profile finalized and locked. Use Edit to create another named custom copy.");
    return;
  }
  if (!(await ensureLocalBackendState())) return;
  openSaveProfileModal();
}

function openSaveProfileModal() {
  const modal = $("save-profile-modal");
  const input = $("save-profile-name");
  const error = $("save-profile-error");
  if (!modal || !input || !error) return;
  saveProfileModalReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  input.value = state.design?.name || state.design?.study_profile_title || "New study profile";
  error.hidden = true;
  error.textContent = "";
  modal.hidden = false;
  syncModalEnvironment();
  window.setTimeout(() => {
    input.focus();
    input.select();
  }, 0);
}

function closeSaveProfileModal() {
  const modal = $("save-profile-modal");
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  syncModalEnvironment();
  if (saveProfileModalReturnFocus && document.contains(saveProfileModalReturnFocus)) {
    saveProfileModalReturnFocus.focus();
  }
  saveProfileModalReturnFocus = null;
}

function showSaveProfileError(message) {
  const error = $("save-profile-error");
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

async function submitSaveProfileModal() {
  const input = $("save-profile-name");
  const cleanName = String(input?.value || "").trim();
  if (!cleanName) {
    showSaveProfileError("Enter a profile name.");
    input?.focus();
    return;
  }
  $("save-profile-submit").disabled = true;
  try {
    state = await designerApi.saveDesign(collectPayload());
    state = await designerApi.savePreparedProfile({
      name: cleanName,
      expected_revision: normalizedDesignerProgress().revision,
    });
    editModeActive = false;
    markDesignerSaved();
    closeSaveProfileModal();
    renderAll();
    updateViewer();
    showToast("Profile finalized and locked");
  } catch (error) {
    showSaveProfileError(error.message || String(error));
  } finally {
    $("save-profile-submit").disabled = false;
  }
}

async function exportOutputFolder() {
  if (!(await ensureLocalBackendState())) return;
  const prepared = Boolean(state.run_sequence_setup?.prepared || projectSegment("6_experiment_run_setup").status === "ready");
  if (!prepared) {
    showToast("Prepare Segment 6 before preparing the output folder.");
    return;
  }
  const payload = isProfileReadonlyMode() ? collectProfileRunPayload() : collectPayload();
  state = await designerApi.exportRunSequenceBridge(payload);
  renderAll();
  showToast("Output folder prepared for data collection");
}

async function prepareExperiment() {
  const profileRunMode = isProfileReadonlyMode();
  if (!profileRunMode && !ensureEditableProject()) return;
  const prepared = Boolean(state.run_sequence_setup?.prepared || projectSegment("6_experiment_run_setup").status === "ready");
  if (!prepared && !profileRunMode) {
    state = await designerApi.prepareRunSequence(collectPayload());
  }
  state = await designerApi.openRunner(profileRunMode ? collectProfileRunPayload() : collectPayload());
  renderAll();
  const toastMessage = prepared
    ? "Local runner opened"
    : (profileRunMode ? "Profile run prepared; local runner opened" : "Design saved; local runner opened");
  showToast(toastMessage);
  scrollToStep("run");
}

async function openLocalFolder(path) {
  if (!path) return;
  await designerApi.openLocalFolder({ path });
  showToast("Opened local folder");
}

async function pollJob(jobId) {
  if (!jobId || activePolls.has(jobId)) return;
  activePolls.add(jobId);
  for (;;) {
    await delay(900);
    const job = await designerApi.job(jobId);
    await loadState();
    if (["succeeded", "failed", "cancelled"].includes(job.status)) {
      activePolls.delete(jobId);
      if (job.status === "succeeded" && (job.kind === "stimulus_bake" || job.kind === "block_csv_preview")) {
        if (!["audiotactile_trial_batch", "trial_sequence_batch", "trial_repetition_pool"].includes(job.result?.source_kind)) {
          selectedIngredientLabel = String(job.result?.source_label || job.result?.source?.label || selectedIngredientLabel || "");
          ingredientEditorDirty = false;
          ingredientEditorMode = "inspect";
          pendingBakeRecipe = null;
          if (job.kind === "stimulus_bake") $("generated-noise-select").value = "";
        }
        if (job.kind === "stimulus_bake") renderStimulus();
        else renderBakePanel();
        renderBaselineTactileSummary();
        renderBlockCsvPreview();
        renderProtocolSummary();
      }
      showToast(`${humanize(job.kind)} ${job.status}`);
      break;
    }
  }
}

function trajectoryViewerWindow() {
  const frame = $("trajectory-frame");
  try {
    return frame?.contentWindow || null;
  } catch {
    return null;
  }
}

function updateViewer() {
  if (!state) return;
  const payload = trajectoryPayloadFromControls();
  syncPreviewModeControls(payload.preview_mode);
  const viewerWindow = trajectoryViewerWindow();
  const updateTrajectory = viewerWindow?.updateTrajectory;
  if (typeof updateTrajectory !== "function") {
    viewerReady = false;
    window.setTimeout(updateViewer, 150);
    return;
  }
  viewerReady = true;
  updateTrajectory(payload);
  // Frame the opening view: once the viewer has rendered real data, center and
  // fit the PPS radius square so it is correct for the actual radius (the
  // default render uses a 1.1 m placeholder). Runs once per (re)load.
  if (!viewerInitialFitDone) {
    viewerInitialFitDone = true;
    const fitRadius = viewerWindow?.fitTrajectoryRadius;
    if (typeof fitRadius === "function") fitRadius();
  }
}

function setPreviewMode(mode) {
  const nextMode = mode === "3d" ? "3d" : "2d";
  $("preview-mode").value = nextMode;
  syncPreviewModeControls(nextMode);
  updateViewer();
}

function callTrajectoryViewer(method, ...args) {
  const viewerWindow = trajectoryViewerWindow();
  const fn = viewerWindow?.[method];
  if (typeof fn === "function") fn(...args);
}

function currentTrajectoryControls() {
  return {
    start_distance_cm: clampNumber(numberValue("start-distance", 110), 1, 1000, 110),
    end_distance_cm: clampNumber(numberValue("end-distance", 10), 1, 1000, 10),
    start_rotation_deg: normalizeRotationDeg(numberValue("start-rotation", 0)),
    end_rotation_deg: normalizeRotationDeg(numberValue("end-rotation", 0)),
    movement_duration_s: clampNumber(numberValue("movement-duration", 3), 0.1, 30, 3),
    start_hold_s: clampNumber(numberValue("start-hold", 0.5), 0, 30, 0.5),
    end_hold_s: clampNumber(numberValue("end-hold", 0.5), 0, 30, 0.5)
  };
}

function trajectoryPayloadFromControls() {
  const controls = currentTrajectoryControls();
  const start = pointFromDistanceRotation(controls.start_distance_cm, controls.start_rotation_deg);
  const end = pointFromDistanceRotation(controls.end_distance_cm, controls.end_rotation_deg);
  const pathLength = distance3d(start, end);
  const sourceTrajectories = sourceTrajectoriesFromDom();
  const sourceRadius = maxSourceTrajectoryRadius(sourceTrajectories);
  const radius = Math.max(0.1, controls.start_distance_cm / 100, controls.end_distance_cm / 100, sourceRadius);
  const selectedEntry = ingredientEntryForLabel(selectedIngredientLabel);
  const draftHasTrajectory = Boolean(pendingBakeRecipe && pendingBakeRecipe.kind !== "fixed_audio");
  return {
    ...clone(state.viewer_payload || {}),
    preview_mode: $("preview-mode").value || "2d",
    radius_m: radius,
    path_length_m: pathLength,
    movement_duration_s: controls.movement_duration_s,
    start,
    end,
    controls,
    source_trajectories: sourceTrajectories,
    active_source_label: selectedIngredientLabel,
    active_source_has_trajectory: selectedIngredientLabel
      ? Boolean(selectedEntry && selectedEntry.kind !== "fixed_audio")
      : draftHasTrajectory,
    editable: trajectoryEditingEnabled()
  };
}

function sourceTrajectoriesFromDom() {
  const rows = [];
  for (const card of $("noise-list").querySelectorAll(".noise-source-card")) {
    const field = (name) => card.querySelector(`[data-field="${name}"]`);
    const label = field("label")?.value.trim() || "Generated noise";
    const toneType = field("noise_type")?.value || "pink";
    const snapshot = readJsonField(field("trajectory_snapshot"));
    const row = sourceTrajectoryRow({
      label,
      sourceKind: "generated_noise",
      toneType,
      localPath: field("prebaked_path")?.value || "",
      snapshot
    });
    if (row) rows.push(row);
  }
  for (const card of [
    ...$("noise-list").querySelectorAll(".audio-source-card"),
    ...$("audio-list").querySelectorAll(".audio-source-card"),
  ]) {
    const field = (name) => card.querySelector(`[data-field="${name}"]`);
    const role = field("audio_role")?.value || "preserve";
    if (role === "prestimulus") continue;
    const label = field("label")?.value.trim() || audioRoleTitle(role);
    const toneType = field("tone_type")?.value || "custom_audio";
    const row = sourceTrajectoryRow({
      label,
      sourceKind: "imported_audio",
      toneType,
      colorHex: field("display_color_hex")?.value || "",
      localPath: field("path")?.value || "",
      snapshot: readJsonField(field("trajectory_snapshot"))
    });
    if (row) rows.push(row);
  }
  return rows.length ? rows : clone(state.viewer_payload?.source_trajectories || []);
}

function sourceTrajectoryRow({ label, sourceKind, toneType, colorHex = "", localPath, snapshot }) {
  if (!snapshot || !snapshot.start || !snapshot.end) return null;
  return {
    label,
    source_kind: sourceKind,
    tone_type: toneType,
    color_hex: normalizeDisplayColorHex(colorHex) || sourceColor(toneType),
    local_path: localPath,
    trajectory_snapshot: snapshot,
    start: snapshot.start,
    end: snapshot.end,
    path_length_m: snapshot.path_length_m,
    movement_duration_s: snapshot.movement_duration_s
  };
}

function maxSourceTrajectoryRadius(rows = []) {
  let radius = 0;
  for (const row of rows) {
    for (const point of [row.start, row.end]) {
      if (!point) continue;
      const value = Math.sqrt(Number(point.x_m || 0) ** 2 + Number(point.y_m || 0) ** 2 + Number(point.z_m || 0) ** 2);
      if (Number.isFinite(value)) radius = Math.max(radius, value);
    }
  }
  return radius;
}

function pointFromDistanceRotation(distanceCm, rotationDeg) {
  const radiusM = distanceCm / 100;
  const radians = (normalizeSignedRotationDeg(rotationDeg) * Math.PI) / 180;
  return {
    x_m: radiusM * Math.sin(radians),
    y_m: radiusM * Math.cos(radians),
    z_m: 0
  };
}

function distance3d(start, end) {
  const dx = Number(start.x_m || 0) - Number(end.x_m || 0);
  const dy = Number(start.y_m || 0) - Number(end.y_m || 0);
  const dz = Number(start.z_m || 0) - Number(end.z_m || 0);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function normalizeRotationDeg(value) {
  const rotation = Number(value);
  if (!Number.isFinite(rotation)) return 0;
  return ((rotation % 360) + 360) % 360;
}

function normalizeSignedRotationDeg(value) {
  return ((normalizeRotationDeg(value) + 180) % 360) - 180;
}

function applyTrajectoryControlUpdate(controls) {
  const fields = {
    start_distance_cm: "start-distance",
    end_distance_cm: "end-distance",
    start_rotation_deg: "start-rotation",
    end_rotation_deg: "end-rotation",
    movement_duration_s: "movement-duration",
    start_hold_s: "start-hold",
    end_hold_s: "end-hold"
  };
  for (const [key, id] of Object.entries(fields)) {
    if (controls[key] === undefined) continue;
    $(id).value = formatTrajectoryValue(key, controls[key]);
  }
  ingredientEditorDirty = true;
  updateViewer();
}

function formatTrajectoryValue(key, value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  const decimals = key.endsWith("_deg") || key.endsWith("_cm") ? 1 : 2;
  return String(Number(numeric.toFixed(decimals)));
}

function syncPreviewModeControls(mode) {
  const current = mode === "3d" ? "3d" : "2d";
  for (const button of document.querySelectorAll("[data-preview-mode]")) {
    button.classList.toggle("active", button.dataset.previewMode === current);
    button.setAttribute("aria-pressed", String(button.dataset.previewMode === current));
  }
  const presetControl = $("view-preset-control");
  if (presetControl) {
    presetControl.hidden = current !== "3d";
  }
}

function getWorkflowStep(stepId) {
  return (state.custom_workflow?.steps || []).find((step) => step.id === stepId);
}

function ensureWorkflowAction(key) {
  const workflow = state.custom_workflow || {};
  if (!workflow.is_custom || workflow[key]) return true;
  const missing = workflow.missing || [];
  showToast(missing.length ? missing.join(" ") : "Complete the custom design steps first.");
  scrollToStep(workflow.current_step || "study");
  return false;
}

function stepLabel(stepId) {
  return (getWorkflowStep(stepId)?.label || humanize(stepId)).toLowerCase();
}

function scrollToStep(stepId) {
  const target = $(STEP_TARGETS[stepId] || stepId);
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function updateActiveNav() {
  if (activePage !== "toolkit") {
    for (const link of document.querySelectorAll("[data-step-link]")) {
      link.classList.remove("active");
    }
    const pageLinks = [...document.querySelectorAll(`[data-page-section-page="${cssEscape(activePage)}"]`)];
    let activeSection = pageLinks[0]?.dataset.pageSectionLink || "";
    const viewportTop = 96;
    for (const link of pageLinks) {
      const target = $(link.dataset.pageSectionLink);
      if (!target) continue;
      if (target.getBoundingClientRect().top <= viewportTop) {
        activeSection = link.dataset.pageSectionLink;
      }
    }
    const hashSection = String(topLevelHash() || "").replace(/^#/, "");
    if (pageLinks.some((link) => link.dataset.pageSectionLink === hashSection)) {
      activeSection = hashSection;
    }
    for (const link of document.querySelectorAll("[data-page-section-link]")) {
      link.classList.toggle(
        "active",
        link.dataset.pageSectionPage === activePage && link.dataset.pageSectionLink === activeSection
      );
    }
    syncMobileRailSummary();
    return;
  }
  for (const link of document.querySelectorAll("[data-page-section-link]")) {
    link.classList.remove("active");
  }
  let active = "study";
  const viewportTop = 96;
  for (const stepId of WORKFLOW_STEPS) {
    const target = $(STEP_TARGETS[stepId]);
    if (!target) continue;
    if (target.getBoundingClientRect().top <= viewportTop) {
      active = stepId;
    }
  }
  for (const link of document.querySelectorAll("[data-step-link]")) {
    link.classList.toggle("active", link.dataset.stepLink === active);
  }
  syncMobileRailSummary();
}

function scheduleActiveNavUpdate() {
  if (activeNavFrame) return;
  activeNavFrame = window.requestAnimationFrame(() => {
    activeNavFrame = 0;
    updateActiveNav();
  });
}

function loadResizableLayoutSettings() {
  applySplitSetting("sideWidth", Number(localStorage.getItem(splitStorageKey("sideWidth"))) || SPLIT_DEFAULTS.sideWidth, false);
  applySplitSetting("ordersWidth", Number(localStorage.getItem(splitStorageKey("ordersWidth"))) || SPLIT_DEFAULTS.ordersWidth, false);
  initializeResizablePanels();
}

function initializeResizablePanels() {
  const mobileLayout = window.matchMedia("(max-width: 760px)").matches;
  for (const panel of document.querySelectorAll("[data-resizable-panel]")) {
    if (mobileLayout) {
      panel.querySelector(":scope > .panel-resize-handle")?.remove();
      panel.classList.remove("user-sized");
      panel.style.removeProperty("--panel-user-height");
      continue;
    }
    const panelId = panel.dataset.panelId || panel.id;
    const storedHeight = Number(localStorage.getItem(panelStorageKey(panelId)));
    if (Number.isFinite(storedHeight) && storedHeight > 0) {
      setPanelHeight(panel, storedHeight, false);
    }
    if (panel.querySelector(":scope > .panel-resize-handle")) continue;
    const handle = document.createElement("div");
    handle.className = "panel-resize-handle";
    handle.setAttribute("role", "separator");
    handle.setAttribute("aria-orientation", "horizontal");
    handle.setAttribute("aria-label", `Resize ${panelId.replaceAll("-", " ")} panel`);
    handle.tabIndex = 0;
    handle.addEventListener("pointerdown", (event) => startPanelResize(event, panel, handle));
    handle.addEventListener("keydown", (event) => resizePanelFromKeyboard(event, panel));
    panel.appendChild(handle);
  }
}

function setPanelHeight(panel, value, persist = true) {
  const panelId = panel.dataset.panelId || panel.id;
  const height = snapNumber(clampNumber(Number(value), PANEL_HEIGHT_MIN, PANEL_HEIGHT_MAX, PANEL_HEIGHT_MIN));
  panel.style.setProperty("--panel-user-height", `${height}px`);
  panel.classList.add("user-sized");
  if (persist) {
    localStorage.setItem(panelStorageKey(panelId), String(height));
  }
  if (panel.dataset.panelId === "trajectory-preview") {
    window.requestAnimationFrame(updateViewer);
  }
}

function startPanelResize(event, panel, handle) {
  if (event.button !== 0) return;
  event.preventDefault();
  const startY = event.clientY;
  const startHeight = panel.getBoundingClientRect().height;
  document.body.classList.add("resizing-panel");
  handle.classList.add("active");

  const onMove = (moveEvent) => {
    setPanelHeight(panel, startHeight + moveEvent.clientY - startY, false);
  };
  const onUp = (upEvent) => {
    setPanelHeight(panel, startHeight + upEvent.clientY - startY, true);
    document.body.classList.remove("resizing-panel");
    handle.classList.remove("active");
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
}

function resizePanelFromKeyboard(event, panel) {
  if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
  event.preventDefault();
  const direction = event.key === "ArrowDown" ? 1 : -1;
  const currentHeight = panel.getBoundingClientRect().height;
  setPanelHeight(panel, currentHeight + direction * PANEL_RESIZE_SNAP_PX, true);
}

function wireSplitters() {
  for (const gutter of document.querySelectorAll("[data-resize-gutter]")) {
    gutter.addEventListener("pointerdown", (event) => startSplitterResize(event, gutter));
    gutter.addEventListener("keydown", (event) => resizeSplitterFromKeyboard(event, gutter));
  }
}

function startSplitterResize(event, gutter) {
  if (event.button !== 0) return;
  event.preventDefault();
  const key = splitKeyForGutter(gutter);
  const container = gutter.closest(".grid, .table-grid, .stimulus-grid, .run-grid");
  if (!container) return;
  const rect = container.getBoundingClientRect();
  document.body.classList.add("resizing-layout");
  gutter.classList.add("active");

  const onMove = (moveEvent) => {
    applySplitSetting(key, rect.right - moveEvent.clientX, false, rect);
  };
  const onUp = (upEvent) => {
    applySplitSetting(key, rect.right - upEvent.clientX, true, rect);
    document.body.classList.remove("resizing-layout");
    gutter.classList.remove("active");
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
}

function resizeSplitterFromKeyboard(event, gutter) {
  if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
  event.preventDefault();
  const key = splitKeyForGutter(gutter);
  const direction = event.key === "ArrowLeft" ? 1 : -1;
  const cssVar = key === "sideWidth" ? "--side-column-width" : "--orders-column-width";
  const current = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(cssVar));
  const container = gutter.closest(".grid, .table-grid, .stimulus-grid, .run-grid");
  if (!container) return;
  applySplitSetting(key, current + direction * PANEL_RESIZE_SNAP_PX, true, container.getBoundingClientRect());
}

function applySplitSetting(key, value, persist = true, containerRect = null) {
  const limits = splitLimitsFor(key, containerRect);
  const width = snapNumber(clampNumber(Number(value), limits.min, limits.max, SPLIT_DEFAULTS[key]));
  const cssVar = key === "sideWidth" ? "--side-column-width" : "--orders-column-width";
  document.documentElement.style.setProperty(cssVar, `${width}px`);
  if (persist) {
    localStorage.setItem(splitStorageKey(key), String(width));
  }
  if (key === "sideWidth") {
    window.requestAnimationFrame(updateViewer);
  }
}

function splitLimitsFor(key, containerRect = null) {
  const limits = SPLIT_LIMITS[key];
  const width = containerRect ? containerRect.width : window.innerWidth - 260;
  const reserved = key === "sideWidth" ? 430 : 320;
  return {
    min: limits.min,
    max: Math.max(limits.min, Math.min(limits.max, width - reserved))
  };
}

function splitKeyForGutter(gutter) {
  return gutter.dataset.resizeGutter === "tables" ? "ordersWidth" : "sideWidth";
}

function splitStorageKey(key) {
  return `ppsDashboard.split.${key}`;
}

function panelStorageKey(panelId) {
  return `ppsDashboard.panelHeight.${panelId}`;
}

function snapNumber(value, step = PANEL_RESIZE_SNAP_PX) {
  return Math.round(value / step) * step;
}

function clampNumber(value, min, max, fallback) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function selectedGeneratedSourceProfile() {
  const selected = document.querySelector('input[name="generated-source-mode"]:checked')?.value || GOLD_STANDARD_SOURCE_PROFILE;
  return selected === CONTINUOUS_SOURCE_PROFILE ? CONTINUOUS_SOURCE_PROFILE : GOLD_STANDARD_SOURCE_PROFILE;
}

function setGeneratedSourceProfileControl(profile) {
  const value = profile === CONTINUOUS_SOURCE_PROFILE ? CONTINUOUS_SOURCE_PROFILE : GOLD_STANDARD_SOURCE_PROFILE;
  const input = document.querySelector(`input[name="generated-source-mode"][value="${value}"]`);
  if (input) input.checked = true;
}

function stageGeneratedNoise(noiseType = "pink") {
  const type = PROCEDURAL_NOISE_TYPES.some((item) => item.value === noiseType) ? noiseType : "pink";
  const remakingSelectedNoise = pendingBakeRecipe?.kind === "generated_noise"
    && pendingBakeRecipe?.action === "remake"
    && Boolean(selectedIngredientLabel);
  if (remakingSelectedNoise) {
    const changed = pendingBakeRecipe.noise_type !== type;
    pendingBakeRecipe.noise_type = type;
    if (changed) ingredientEditorDirty = true;
    renderBakePanel();
    renderNoiseTable();
    updateViewer();
    if (changed) showToast(`${noiseTypeLabel(type)} noise selected for remake`);
    return;
  }
  if (!confirmDiscardIngredientDraft("start a new generated stimulus")) return;
  const label = `${noiseTypeLabel(type)} noise`;
  selectedIngredientLabel = "";
  ingredientEditorMode = "create";
  ingredientEditorDirty = false;
  pendingBakeRecipe = {
    kind: "generated_noise",
    action: "create",
    original_label: "",
    noise_type: type,
    label,
    gain: 1,
    source_profile: selectedGeneratedSourceProfile(),
    loudness_policy: collectLoudnessPolicy()
  };
  applyTrajectoryControls(state.trajectory_controls || {});
  $("bake-label").value = label;
  renderBakePanel();
  renderNoiseTable();
  renderAudioTable();
  updateViewer();
  showToast(`${noiseTypeLabel(type)} noise staged`);
}

function stageImportedAudioForBake(audio, renderMode) {
  const mode = renderMode === "spatialize" ? "spatialize" : "preserve";
  selectedIngredientLabel = "";
  ingredientEditorMode = "create";
  ingredientEditorDirty = false;
  pendingDisplayColorHex = normalizeDisplayColorHex(audio.display_color_hex) || sourceColor(audio.tone_type || "custom_audio");
  pendingBakeRecipe = {
    kind: mode === "preserve" ? "fixed_audio" : "imported_audio",
    action: "create",
    original_label: "",
    render_mode: mode,
    label: audio.label || audioRoleTitle(mode),
    audio,
    display_color_hex: pendingDisplayColorHex,
    gain: 1,
    loudness_policy: collectLoudnessPolicy()
  };
  $("bake-label").value = pendingBakeRecipe.label;
  renderBakePanel();
}

function collectBakeRecipe() {
  if (!pendingBakeRecipe) return null;
  const recipe = clone(pendingBakeRecipe);
  recipe.label = $("bake-label").value.trim();
  if (!recipe.label) return null;
  recipe.gain = 1;
  recipe.loudness_policy = collectLoudnessPolicy();
  recipe.action = recipe.action === "remake" ? "remake" : "create";
  recipe.original_label = recipe.action === "remake" ? String(recipe.original_label || selectedIngredientLabel || "").trim() : "";
  recipe.display_color_hex = normalizeDisplayColorHex(pendingDisplayColorHex, "#1C7A86");
  if (recipe.kind === "generated_noise") {
    recipe.source_profile = selectedGeneratedSourceProfile();
    recipe.source_profile_parameters = recipe.source_profile === GOLD_STANDARD_SOURCE_PROFILE
      ? (recipe.source_profile_parameters || {})
      : {};
  }
  if (recipe.audio) {
    recipe.audio = {
      ...recipe.audio,
      label: recipe.label,
      gain: 1,
      display_color_hex: recipe.display_color_hex,
      target_duration_s: recipe.kind === "fixed_audio"
        ? clampNumber(numberValue("fixed-audio-duration", recipe.audio.target_duration_s || 4), 0.1, 3600, 4)
        : Number(recipe.audio.target_duration_s || currentTrajectoryControls().movement_duration_s),
    };
  }
  return recipe;
}

function startNewIngredient() {
  if (!confirmDiscardIngredientDraft("start another ingredient")) return;
  selectedIngredientLabel = "";
  ingredientEditorMode = "idle";
  ingredientEditorDirty = false;
  pendingBakeRecipe = null;
  applyTrajectoryControls(state.trajectory_controls || {});
  $("bake-label").value = "";
  renderNoiseTable();
  renderAudioTable();
  renderBakePanel();
  updateViewer();
  $("bake-label").focus();
}

function stageManualAudioKind(renderMode) {
  if (!confirmDiscardIngredientDraft(`start a new ${renderMode === "spatialize" ? "custom looming tone" : "fixed clip"}`)) return false;
  const mode = renderMode === "spatialize" ? "spatialize" : "preserve";
  selectedIngredientLabel = "";
  ingredientEditorMode = "create";
  ingredientEditorDirty = false;
  pendingDisplayColorHex = "#1C7A86";
  pendingBakeRecipe = {
    kind: mode === "preserve" ? "fixed_audio" : "imported_audio",
    action: "create",
    original_label: "",
    render_mode: mode,
    label: mode === "preserve" ? "Custom audio clip" : "Custom looming tone",
    audio: null,
    display_color_hex: pendingDisplayColorHex,
    gain: 1,
    loudness_policy: collectLoudnessPolicy(),
  };
  applyTrajectoryControls(state.trajectory_controls || {});
  $("bake-label").value = pendingBakeRecipe.label;
  renderNoiseTable();
  renderAudioTable();
  renderBakePanel();
  updateViewer();
  return true;
}

function addNoiseRow(noiseType = "pink") {
  const type = PROCEDURAL_NOISE_TYPES.some((item) => item.value === noiseType) ? noiseType : "pink";
  state.design.noises = state.design.noises || [];
  const label = noiseTypeLabel(type);
  state.design.noises.push({
    label: `${label} noise`,
    noise_type: type,
    azimuth_deg: 0,
    elevation_deg: 0,
    gain: 1,
    source_profile: GOLD_STANDARD_SOURCE_PROFILE,
    source_profile_parameters: {},
    motion_mode: "looming"
  });
  renderNoiseTable();
  refreshAssemblyTargetOptions();
  syncFilmstripSourceOptions();
  renderSourceCounts();
}

function noiseTypeLabel(noiseType) {
  return PROCEDURAL_NOISE_TYPES.find((item) => item.value === noiseType)?.label || "Generated";
}

function audioRoleTitle(role) {
  if (role === "spatialize") return "Custom looming tone";
  if (role === "prestimulus") return "Custom clip";
  return "Custom audio clip";
}

function openAudioPicker(renderMode) {
  pendingInstructionSlot = "";
  pendingAudioImportMode = ["spatialize", "prestimulus"].includes(renderMode) ? renderMode : "preserve";
  $("audio-file-input").click();
}

function openInstructionAudioPicker(slot) {
  pendingInstructionSlot = slot;
  pendingAudioImportMode = "instruction";
  $("audio-file-input").click();
}

async function importAudioFromPicker() {
  const input = $("audio-file-input");
  const file = input.files && input.files[0];
  input.value = "";
  if (!file) return;
  if (staticModeActive) {
    if (pendingAudioImportMode === "spatialize") {
      showToast("Hosted mode can use fixed local audio, but cannot spatialize or generate looming audio.");
      return;
    }
    const stored = await window.PPSDesigner?.drafts?.storeAudio(file);
    if (!stored) throw new Error("Browser audio storage is unavailable. Export the profile now or use the desktop app.");
    const audio = {
      label: file.name.replace(/\.[^.]+$/, ""),
      path: `browser-asset:${stored.id}`,
      browser_preview_url: stored.url,
      browser_asset_id: stored.id,
      target_duration_s: 4,
      gain: 1,
      render_mode: "preserve",
      motion_mode: "stationary",
      tone_type: "custom_audio",
      display_color_hex: pendingDisplayColorHex,
      source_input_path: `browser-asset:${stored.id}`,
      trajectory_snapshot: {}
    };
    if (pendingInstructionSlot) {
      const profile = state.design.study_profile_reference_parameters?.dashboard_run_setup || {};
      profile.instruction_profile = profile.instruction_profile || { slots: [] };
      profile.instruction_profile.slots = (profile.instruction_profile.slots || []).filter((item) => item.slot !== pendingInstructionSlot);
      profile.instruction_profile.slots.push({ slot: pendingInstructionSlot, label: audio.label, path: audio.path, browser_asset_id: stored.id });
      state.design.study_profile_reference_parameters.dashboard_run_setup = profile;
      pendingInstructionSlot = "";
    } else {
      const remake = pendingBakeRecipe?.action === "remake";
      if (remake) {
        pendingBakeRecipe.audio = { ...audio, label: $("bake-label").value.trim() || audio.label };
        pendingBakeRecipe.display_color_hex = pendingDisplayColorHex;
        ingredientEditorDirty = true;
      } else {
        stageImportedAudioForBake(audio, "preserve");
      }
    }
    renderBakePanel();
    showToast("Local audio staged in this browser; create the clip to commit it");
    return;
  }
  const contentBase64 = await fileToBase64(file);
  if (pendingInstructionSlot) {
    const current = collectRunInstructionProfile().slots.find((slot) => slot.slot === pendingInstructionSlot) || {};
    const importedState = await designerApi.importRunInstructions({
      slot: pendingInstructionSlot,
      filename: file.name,
      content_base64: contentBase64,
      label: current.label || file.name.replace(/\.[^.]+$/, ""),
    });
    state = importedState;
    pendingInstructionSlot = "";
    markDesignerSaved();
    renderAll();
    showToast("Instruction audio clip imported locally");
    return;
  }
  const imported = await designerApi.importAudio({
    filename: file.name,
    content_base64: contentBase64,
    use: pendingAudioImportMode === "prestimulus" ? "prestimulus" : "looming",
    render_mode: pendingAudioImportMode === "spatialize" ? "spatialize" : "preserve",
    motion_mode: pendingAudioImportMode === "preserve" || pendingAudioImportMode === "prestimulus" ? "stationary" : "looming",
    placement: "before",
    target_source_label: "",
    phase: "",
    gap_s: 0,
    display_color_hex: pendingDisplayColorHex,
    stage_only: true,
  });
  if (pendingBakeRecipe?.action === "remake") {
    pendingBakeRecipe.audio = {
      ...pendingBakeRecipe.audio,
      ...imported.audio,
      label: $("bake-label").value.trim() || imported.audio.label,
      display_color_hex: pendingDisplayColorHex,
    };
    pendingBakeRecipe.display_color_hex = pendingDisplayColorHex;
    ingredientEditorDirty = true;
    renderBakePanel();
    showToast("Replacement audio staged; use the remake action to commit it");
  } else {
    stageImportedAudioForBake(imported.audio, pendingAudioImportMode);
    showToast("Audio staged; use the create action to commit it");
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",").pop() : result);
    });
    reader.addEventListener("error", () => reject(reader.error || new Error("Could not read audio file")));
    reader.readAsDataURL(file);
  });
}

function removeSourceCard(button) {
  const card = button.closest(".source-card");
  const removedLabel = sourceCardCurrentLabel(card);
  if (card) card.remove();
  if (normalizeSourceKey(removedLabel) === normalizeSourceKey(selectedIngredientLabel)) {
    selectedIngredientLabel = "";
    ingredientEditorMode = "idle";
    ingredientEditorDirty = false;
    pendingBakeRecipe = null;
    renderBakePanel();
    updateViewer();
  }
  if (removedLabel) {
    reconcileTrialStripLabelsWithSourcePool({ [removedLabel]: "" });
  }
  refreshAssemblyTargetOptions();
  syncFilmstripSourceOptions();
  renderSourceCounts();
}

function delay(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function humanize(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeAttr(value) {
  return escapeHtml(value).replaceAll("\n", " ");
}

function doiUrl(value) {
  const doi = String(value || "").trim();
  if (/^https?:\/\//i.test(doi)) {
    return doi;
  }
  return `https://doi.org/${doi.replace(/^doi:\s*/i, "")}`;
}

function enforceExternalLinkTargets(root = document) {
  for (const link of root.querySelectorAll("a[href]")) {
    let url;
    try {
      url = new URL(link.getAttribute("href"), window.location.href);
    } catch (_err) {
      continue;
    }
    if (!/^https?:$/i.test(url.protocol) || url.origin === window.location.origin) continue;
    link.target = "_blank";
    const relTokens = new Set((link.getAttribute("rel") || "").split(/\s+/).filter(Boolean));
    relTokens.add("noopener");
    relTokens.add("noreferrer");
    link.setAttribute("rel", Array.from(relTokens).join(" "));
  }
}

function handoffExternalLinkToNative(event) {
  const link = event.target.closest?.("a[href]");
  const nativeOpener = window.pywebview?.api?.open_external;
  if (!link || typeof nativeOpener !== "function") return;
  let url;
  try {
    url = new URL(link.getAttribute("href"), window.location.href);
  } catch (_err) {
    return;
  }
  const designerTemplateLink = link.hasAttribute("data-open-designer-template");
  if (!/^(https?|mailto):$/i.test(url.protocol) || (url.origin === window.location.origin && !designerTemplateLink)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  Promise.resolve(nativeOpener(url.href))
    .then((opened) => {
      if (opened === false) showToast("The system browser could not open this link.");
    })
    .catch((error) => {
      console.error(error);
      showToast("The system browser could not open this link.");
    });
}

function renderHardwarePixelArt() {
  const store = window.HARDWARE_PIXEL_ART || {};
  for (const canvas of document.querySelectorAll("canvas[data-hardware-pixel]")) {
    const key = canvas.dataset.hardwarePixel || "";
    const art = store[key];
    if (!art?.rgb || !art.width || !art.height || canvas.dataset.pixelRendered === "true") continue;
    const width = Number(art.width);
    const height = Number(art.height);
    const binary = window.atob(art.rgb);
    const rgba = new Uint8ClampedArray(width * height * 4);
    for (let source = 0, target = 0; source < binary.length; source += 3, target += 4) {
      rgba[target] = binary.charCodeAt(source);
      rgba[target + 1] = binary.charCodeAt(source + 1);
      rgba[target + 2] = binary.charCodeAt(source + 2);
      rgba[target + 3] = 255;
    }
    canvas.width = width;
    canvas.height = height;
    canvas.dataset.pixelRendered = "true";
    const context = canvas.getContext("2d");
    context.imageSmoothingEnabled = false;
    context.putImageData(new ImageData(rgba, width, height), 0, 0);
  }
}

function wireEvents() {
  document.addEventListener("click", handoffExternalLinkToNative, true);
  document.addEventListener("pointerdown", (event) => {
    const control = event.target.closest?.("#toolkit-page input:disabled, #toolkit-page select:disabled, #toolkit-page textarea:disabled, #toolkit-page button:disabled");
    if (!control || !isProfileReadonlyMode() || control.matches("[data-preview-view-control]")) return;
    event.preventDefault();
    openCustomizeModal();
  }, true);
  $("edit-mode-button")?.addEventListener("click", () => setEditMode(!editModeActive).catch(reportError));
  $("connect-backend").addEventListener("click", () => reconnectBackendFromControls().catch(reportError));
  $("companion-token")?.addEventListener("change", () => saveCompanionToken($("companion-token").value));
  const pageTabs = [...document.querySelectorAll("[data-page-tab]")];
  for (const button of pageTabs) {
    button.addEventListener("click", () => setActivePage(button.dataset.pageTab, { updateHash: true, scrollTop: true }));
    button.addEventListener("keydown", (event) => {
      const keyDirections = { ArrowLeft: -1, ArrowRight: 1 };
      if (!(event.key in keyDirections) && !["Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const currentIndex = Math.max(0, pageTabs.indexOf(button));
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? pageTabs.length - 1
          : (currentIndex + keyDirections[event.key] + pageTabs.length) % pageTabs.length;
      const next = pageTabs[nextIndex];
      setActivePage(next.dataset.pageTab, { updateHash: true, scrollTop: true });
      next.focus();
    });
  }
  $("mobile-rail-nav-toggle")?.addEventListener("click", () => toggleMobileRailDisclosure("sections"));
  $("mobile-companion-toggle")?.addEventListener("click", () => toggleMobileRailDisclosure("companion"));
  for (const link of document.querySelectorAll("[data-page-section-link]")) {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const targetId = link.dataset.pageSectionLink;
      const page = link.dataset.pageSectionPage;
      const fromMobileSections = mobileSectionsDisclosureOpen();
      const moveFocus = fromMobileSections && event.detail === 0;
      setActivePage(page, {
        updateHash: false,
        scrollTop: false,
        preserveMobileDisclosures: fromMobileSections
      });
      const target = $(targetId);
      if (target) {
        replaceRouteForPage(page, targetId);
        setActivePageSection(targetId);
        if (moveFocus) focusSectionNavigationTarget(target);
        closeMobileRailDisclosures();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        window.setTimeout(() => setActivePageSection(targetId), 350);
      } else {
        closeMobileRailDisclosures();
      }
    });
  }
  window.addEventListener("hashchange", () => {
    const nextPage = pageFromLocation();
    if (nextPage) {
      setActivePage(nextPage, { scrollTop: false });
      scrollToHashTarget();
    }
  });
  window.addEventListener("scroll", scheduleActiveNavUpdate, { passive: true });
  for (const button of document.querySelectorAll("[data-segment-info]")) {
    button.addEventListener("click", () => openSegmentInfoModal(button.dataset.segmentInfo, button));
  }
  $("segment-info-modal-close")?.addEventListener("click", closeSegmentInfoModal);
  $("segment-info-modal")?.addEventListener("click", (event) => {
    if (event.target === $("segment-info-modal")) closeSegmentInfoModal();
  });
  $("start-new-custom-design")?.addEventListener("click", openNewCustomDesignModal);
  $("customize-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    submitCustomizeModal().catch(reportError);
  });
  $("customize-cancel")?.addEventListener("click", closeCustomizeModal);
  $("customize-modal-close")?.addEventListener("click", closeCustomizeModal);
  $("customize-modal")?.addEventListener("click", (event) => {
    if (event.target === $("customize-modal")) closeCustomizeModal();
  });
  $("save-profile-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    submitSaveProfileModal().catch(reportError);
  });
  $("save-profile-cancel")?.addEventListener("click", closeSaveProfileModal);
  $("save-profile-modal-close")?.addEventListener("click", closeSaveProfileModal);
  $("save-profile-modal")?.addEventListener("click", (event) => {
    if (event.target === $("save-profile-modal")) closeSaveProfileModal();
  });
  document.addEventListener("keydown", (event) => {
    if (trapModalFocus(event)) return;
    if (event.key !== "Escape") return;
    if (!$("segment-info-modal")?.hidden) {
      closeSegmentInfoModal();
    } else if (!$("customize-modal")?.hidden) {
      closeCustomizeModal();
    } else if (!$("save-profile-modal")?.hidden) {
      closeSaveProfileModal();
    } else if (!$("audio-color-modal")?.hidden) {
      closeAudioColorModal();
    }
  });
  $("backend-url").addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      reconnectBackendFromControls({ includeToken: false }).catch(reportError);
    }
  });
  $("template-select").addEventListener("change", () => {
    renderProfileSummary();
    loadTemplate().catch(reportError);
  });
  $("bake-stimulus").addEventListener("click", () => startBakeStimulus().catch(reportError));
  $("new-ingredient")?.addEventListener("click", startNewIngredient);
  $("bake-trial-sequences")?.addEventListener("click", () => startBakeTrialSequences().catch(reportError));
  $("bake-trial-files")?.addEventListener("click", () => startBakeTrialFiles().catch(reportError));
  $("bake-trial-pool")?.addEventListener("click", () => startBakeTrialPool().catch(reportError));
  $("regenerate-block-csvs")?.addEventListener("click", () => startBakeBlockCsvs().catch(reportError));
  $("accept-block-csvs")?.addEventListener("click", () => acceptBlockCsvs().catch((error) => {
    const button = $("accept-block-csvs");
    if (button) button.disabled = false;
    reportError(error);
  }));
  $("download-block-randomization")?.addEventListener("click", () => downloadBlockRandomization().catch(reportError));
  $("open-ingredient-folder")?.addEventListener("click", () => {
    const path = $("open-ingredient-folder")?.dataset.folderPath || projectSegment("1_core_audio_ingredients").folder_path || "";
    openLocalFolder(path).catch(reportError);
  });
  $("open-trial-sequence-folder")?.addEventListener("click", () => {
    const path = $("open-trial-sequence-folder")?.dataset.folderPath || projectSegment("2_trial_sequence_designs").folder_path || "";
    openLocalFolder(path).catch(reportError);
  });
  $("open-trial-file-folder")?.addEventListener("click", () => {
    const path = $("open-trial-file-folder")?.dataset.folderPath || state.trial_file_bake?.root || "";
    openLocalFolder(path).catch(reportError);
  });
  $("open-trial-pool-folder")?.addEventListener("click", () => {
    const path = $("open-trial-pool-folder")?.dataset.folderPath || state.trial_pool_bake?.root || "";
    openLocalFolder(path).catch(reportError);
  });
  $("regenerate-run-sequence")?.addEventListener("click", () => regenerateRunSequence().catch(reportError));
  $("save-study-profile")?.addEventListener("click", () => savePreparedStudyProfile().catch(reportError));
  $("export-output-folder")?.addEventListener("click", () => exportOutputFolder().catch(reportError));
  $("prepare-experiment")?.addEventListener("click", () => prepareExperiment().catch(reportError));
  $("generated-noise-select").addEventListener("change", () => {
    const selectedNoise = $("generated-noise-select").value;
    if (!selectedNoise) return;
    if (!ensureEditableProject()) {
      renderBakePanel();
      return;
    }
    stageGeneratedNoise(selectedNoise);
  });
  for (const button of document.querySelectorAll(".noise-type-button")) {
    button.addEventListener("click", () => {
      const type = button.dataset.noiseType;
      if (!ensureEditableProject()) return;
      if (type) stageGeneratedNoise(type);
    });
  }
  $("bake-label").addEventListener("input", () => {
    if (pendingBakeRecipe) {
      pendingBakeRecipe.label = $("bake-label").value.trim();
      ingredientEditorDirty = true;
      renderBakePanel();
    }
  });
  for (const input of document.querySelectorAll('input[name="generated-source-mode"]')) {
    input.addEventListener("change", () => {
      if (pendingBakeRecipe?.kind === "generated_noise") {
        pendingBakeRecipe.source_profile = selectedGeneratedSourceProfile();
        if (pendingBakeRecipe.source_profile !== GOLD_STANDARD_SOURCE_PROFILE) {
          pendingBakeRecipe.source_profile_parameters = {};
        }
        ingredientEditorDirty = true;
        renderBakePanel();
      }
    });
  }
  for (const id of ["loudness-start-spl", "loudness-end-spl", "instruction-offset-db", "estimated-full-scale-spl", "audio-peak-ceiling-dbfs"]) {
    $(id)?.addEventListener("input", () => {
      updateLoudnessDerived();
      if (pendingBakeRecipe) pendingBakeRecipe.loudness_policy = collectLoudnessPolicy();
    });
  }
  $("import-audio-spatialize").addEventListener("click", () => {
    if (stageManualAudioKind("spatialize")) openAudioPicker("spatialize");
  });
  $("import-audio-preserve").addEventListener("click", () => {
    if (stageManualAudioKind("preserve")) openAudioPicker("preserve");
  });
  $("choose-editor-audio")?.addEventListener("click", () => openAudioPicker(pendingBakeRecipe?.render_mode || "preserve"));
  $("manual-audio-color")?.addEventListener("click", openAudioColorModal);
  $("audio-color-native")?.addEventListener("input", () => {
    const value = normalizeDisplayColorHex($("audio-color-native").value, "#1C7A86");
    $("audio-color-hex").value = value;
    renderAudioColorPreview(value);
  });
  $("audio-color-hex")?.addEventListener("input", () => renderAudioColorPreview($("audio-color-hex").value));
  $("audio-color-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    applyAudioColorModal();
  });
  $("audio-color-cancel")?.addEventListener("click", closeAudioColorModal);
  $("audio-color-modal-close")?.addEventListener("click", closeAudioColorModal);
  $("audio-color-modal")?.addEventListener("click", (event) => {
    if (event.target === $("audio-color-modal")) closeAudioColorModal();
  });
  $("fixed-audio-duration")?.addEventListener("input", () => {
    if (!pendingBakeRecipe) return;
    ingredientEditorDirty = true;
    renderBakePanel();
  });
  $("import-audio-prestimulus")?.addEventListener("click", () => openAudioPicker("prestimulus"));
  $("audio-file-input").addEventListener("change", () => importAudioFromPicker().catch(reportError));
  $("soa-values").addEventListener("input", updateFilmstripCounts);
  $("block-soa-values").addEventListener("input", () => {
    syncCompositionSoaInputs($("block-soa-values"));
    updateFilmstripCounts();
  });
  $("repetitions").addEventListener("input", () => {
    trialPoolRepetitionDraft.defaultRepetitions = normalizeRepetitionValue(numberValue("repetitions", 0), 0);
    updateFilmstripCounts();
  });
  $("apply-trial-pool-repetitions")?.addEventListener("click", () => {
    const next = normalizeRepetitionValue(numberValue("repetitions", 0), 0);
    trialPoolRepetitionDraft.defaultRepetitions = next;
    trialPoolRepetitionDraft.familyRepetitions = {};
    trialPoolRepetitionDraft.folderRepetitions = {};
    trialPoolRepetitionDraft.fileRepetitionOverrides = {};
    for (const group of trialPoolFolderGroups()) {
      trialPoolRepetitionDraft.folderRepetitions[group.folderKey] = next;
    }
    markDesignerDirty();
    updateFilmstripCounts();
  });
  $("blocks").addEventListener("input", updateFilmstripCounts);
  $("block-count")?.addEventListener("input", () => {
    const blockCount = Math.max(1, Math.round(numberValue("block-count", 1)));
    if ($("blocks")) $("blocks").value = blockCount;
    renderBlockCsvPreview();
  });
  $("block-randomization-seed")?.addEventListener("input", () => {
    const seed = normalizedRandomizationSeed($("block-randomization-seed").value);
    state.design.protocol.random_seed = seed;
    renderBlockPolicySummary({});
    markDesignerDirty();
  });
  $("generate-block-randomization-seed")?.addEventListener("click", () => {
    if (!ensureEditableProject()) return;
    const seed = newRandomizationSeed();
    $("block-randomization-seed").value = seed;
    state.design.protocol.random_seed = seed;
    renderBlockPolicySummary({});
    markDesignerDirty();
  });
  $("participants").addEventListener("input", scheduleRunSequencePreview);
  for (const input of document.querySelectorAll('input[name="experiment-structure"]')) {
    input.addEventListener("change", scheduleRunSequencePreview);
  }
  $("run-instruction-slots")?.addEventListener("click", (event) => {
    const button = event.target.closest?.("[data-instruction-import]");
    if (!button) return;
    openInstructionAudioPicker(button.dataset.instructionImport || "");
  });
  $("run-instruction-slots")?.addEventListener("input", scheduleRunSequencePreview);
  $("run-instruction-slots")?.addEventListener("change", scheduleRunSequencePreview);
  $("baseline-enabled").addEventListener("change", () => {
    const previous = $("baseline-strategy").value;
    const nextStrategy = $("baseline-enabled").checked
      ? (previous && previous !== "none" ? previous : "tactile_only")
      : "";
    setBaselineStrategy(nextStrategy);
    if (nextStrategy === "none") applyBaselineDefaultToTrialRows();
    updateFilmstripCounts();
  });
  $("baseline-strategy").addEventListener("change", () => {
    const nextStrategy = $("baseline-strategy").value || "";
    setBaselineStrategy(nextStrategy);
    if (nextStrategy === "none") applyBaselineDefaultToTrialRows();
    updateFilmstripCounts();
  });
  for (const input of baselineOptionInputs()) {
    input.addEventListener("change", () => {
      const nextStrategy = strategyFromBaselineOptions(input);
      setBaselineStrategy(nextStrategy);
      if (!baselineStrategyIsActive(nextStrategy)) applyBaselineDefaultToTrialRows();
      updateFilmstripCounts();
    });
  }
  $("baseline-percent").addEventListener("input", () => {
    enforceGlobalPercentBounds("baseline-percent");
    applyBaselineDefaultToTrialRows();
    updateFilmstripCounts();
  });
  $("catch-percent").addEventListener("input", () => {
    enforceGlobalPercentBounds("catch-percent");
    applyBaselineDefaultToTrialRows();
    updateFilmstripCounts();
  });
  $("include-catch-trials")?.addEventListener("change", () => {
    updateBaselineDecision();
    updateFilmstripCounts();
  });
  $("include-auditory-only-trials")?.addEventListener("change", () => {
    updateBaselineDecision();
    updateFilmstripCounts();
  });
  $("baseline-soa-values").addEventListener("input", () => {
    updateBaselineDecision();
    updateFilmstripCounts();
  });
  $("baseline-custom-audio-tactile")?.addEventListener("change", () => {
    updateBaselineDecision();
    updateFilmstripCounts();
  });
  $("row-mix-overrides")?.addEventListener("input", (event) => {
    const input = event.target.closest?.("[data-row-mix-index][data-row-mix-field]");
    if (!input) return;
    const row = [...$("filmstrip-list").querySelectorAll(".filmstrip-row")][Number(input.dataset.rowMixIndex || 0)];
    const field = row?.querySelector(`[data-strip-field="${input.dataset.rowMixField}"]`);
    if (!field) return;
    const next = Math.max(0, Math.min(100, Number(input.value || 0)));
    input.value = String(next);
    field.value = String(next);
    field.dataset.rowMixOverride = "true";
    updateFilmstripCounts();
    const counts = rowCompositionCounts(row);
    const totalCell = input.closest("tr")?.children?.[4];
    if (totalCell) totalCell.textContent = String(counts.totalPerBlock);
  });
  $("trial-pool-folder-list")?.addEventListener("input", (event) => {
    const folderInput = event.target.closest?.("[data-trial-pool-folder]");
    const fileInput = event.target.closest?.("[data-trial-pool-file]");
    if (folderInput) {
      const key = folderInput.dataset.trialPoolFolder || "";
      const value = normalizeRepetitionValue(folderInput.value || 0, 0);
      folderInput.value = formatRepetitionValue(value);
      if (key) trialPoolRepetitionDraft.folderRepetitions[key] = value;
      updateFilmstripCounts();
      return;
    }
    if (fileInput) {
      const key = fileInput.dataset.trialPoolFile || "";
      const value = normalizeRepetitionValue(fileInput.value || 0, 0);
      fileInput.value = formatRepetitionValue(value);
      if (key) trialPoolRepetitionDraft.fileRepetitionOverrides[key] = value;
      updateFilmstripCounts();
    }
  });
  $("reset-camera").addEventListener("click", () => {
    callTrajectoryViewer("resetTrajectoryCamera");
  });
  $("fit-radius-camera").addEventListener("click", () => callTrajectoryViewer("fitTrajectoryRadius"));
  $("zoom-in-camera").addEventListener("click", () => callTrajectoryViewer("zoomTrajectoryCamera", "in"));
  $("zoom-out-camera").addEventListener("click", () => callTrajectoryViewer("zoomTrajectoryCamera", "out"));
  for (const button of document.querySelectorAll("[data-view-preset]")) {
    button.addEventListener("click", () => callTrajectoryViewer("snapTrajectoryView", button.dataset.viewPreset));
  }
  $("preview-mode").addEventListener("change", () => setPreviewMode($("preview-mode").value));
  for (const id of TRAJECTORY_FIELD_IDS) {
    $(id).addEventListener("input", () => {
      updateViewer();
      updateLoudnessDerived();
      if (pendingBakeRecipe) pendingBakeRecipe.loudness_policy = collectLoudnessPolicy();
      if (pendingBakeRecipe) ingredientEditorDirty = true;
    });
  }
  wireSplitters();
  for (const button of document.querySelectorAll("[data-preview-mode]")) {
    button.addEventListener("click", () => setPreviewMode(button.dataset.previewMode));
  }
  for (const button of document.querySelectorAll("[data-continue-step]")) {
    button.addEventListener("click", () => continueWorkflowStep(button.dataset.continueStep).catch(reportError));
  }
  for (const button of document.querySelectorAll("[data-reopen-step]")) {
    button.addEventListener("click", () => reopenWorkflowStep(button.dataset.reopenStep).catch(reportError));
  }
  for (const link of document.querySelectorAll("[data-step-link]")) {
    link.addEventListener("click", (event) => {
      const stepId = link.dataset.stepLink;
      event.preventDefault();
      const fromMobileSections = mobileSectionsDisclosureOpen();
      const moveFocus = fromMobileSections && event.detail === 0;
      setActivePage("toolkit", {
        updateHash: true,
        preserveMobileDisclosures: fromMobileSections
      });
      const target = $(STEP_TARGETS[stepId] || stepId);
      if (moveFocus) focusSectionNavigationTarget(target);
      closeMobileRailDisclosures();
      scrollToStep(stepId);
      if (link.classList.contains("locked")) {
        const active = normalizedDesignerProgress().edit_step;
        showToast(`${stepLabel(stepId)} is read-only while ${stepLabel(active)} is being edited.`);
      }
    });
  }
  window.addEventListener("scroll", updateActiveNav, { passive: true });
  window.matchMedia("(max-width: 760px)").addEventListener("change", () => initializeResizablePanels());
  window.addEventListener("message", (event) => {
    const frame = $("trajectory-frame");
    if (event.source !== frame.contentWindow) return;
    const data = event.data || {};
    if (data.type === "pps-source-trajectory-select") {
      selectIngredient(String(data.source_label || ""));
      return;
    }
    if (data.type !== "pps-trajectory-control-change") return;
    // Ignore preview marker drags unless editing is enabled, so the preview is
    // look-only in read-only/locked mode.
    if (!trajectoryEditingEnabled()) return;
    applyTrajectoryControlUpdate(data.controls || {});
  });
  $("trajectory-frame").addEventListener("load", () => {
    viewerReady = true;
    viewerInitialFitDone = false;
    updateViewer();
  });
  document.addEventListener("click", (event) => {
    const mobileTableToggle = event.target.closest?.("[data-mobile-table-toggle]");
    if (mobileTableToggle) {
      const table = $(mobileTableToggle.dataset.mobileTableToggle || "");
      if (!table) return;
      const expanded = !table.classList.contains("mobile-show-all");
      table.classList.toggle("mobile-show-all", expanded);
      mobileTableToggle.setAttribute("aria-expanded", String(expanded));
      mobileTableToggle.textContent = expanded
        ? "Show first 6 rows"
        : `Show all ${mobileTableToggle.dataset.rowCount || ""} rows`;
      return;
    }
    const structuralMutation = event.target.closest?.(
      "[data-remove-noise], [data-remove-audio], [data-remove-strip], [data-remove-strip-element], [data-add-strip-element], [data-add-strip-row], [data-add-box-label], [data-remove-box-label], [data-strip-move]"
    );
    const previewButton = event.target.closest?.("[data-preview-strip]");
    if (previewButton) {
      previewFilmstripRow(previewButton).catch(reportError);
      return;
    }
    const sourcePreviewButton = event.target.closest?.("[data-preview-source-label]");
    if (sourcePreviewButton) {
      previewSourceLabel(sourcePreviewButton).catch(reportError);
      return;
    }
    const ingredientCard = event.target.closest?.("[data-select-ingredient]");
    if (ingredientCard && !event.target.closest("button, a, input, select, textarea")) {
      selectIngredient(ingredientCard.dataset.selectIngredient || "");
      return;
    }
    if (event.target.matches("[data-open-folder]")) {
      openLocalFolder(event.target.dataset.openFolder).catch(reportError);
      return;
    }
    if (event.target.matches("[data-remove-noise], [data-remove-audio]")) {
      removeSourceCard(event.target);
    }
    if (event.target.matches("[data-remove-strip]")) {
      removeFilmstripRow(event.target);
    }
    if (event.target.matches("[data-remove-strip-element]")) {
      removeFilmstripElement(event.target);
    }
    if (event.target.matches("[data-add-strip-element]")) {
      addFilmstripElement(event.target, event.target.dataset.addStripElement);
    }
    if (event.target.matches("[data-add-strip-row]")) {
      addFilmstripRow("fixed_audio", event.target.dataset.insertRowAfter);
    }
    if (event.target.matches("[data-add-box-label]")) {
      addBoxLabel(event.target);
    }
    if (event.target.matches("[data-remove-box-label]")) {
      removeBoxLabel(event.target);
    }
    if (event.target.matches("[data-strip-move]")) {
      moveFilmstripRow(event.target, event.target.dataset.stripMove);
    }
    if (structuralMutation) markDesignerDirty();
  });
  document.addEventListener("keydown", (event) => {
    const card = event.target.closest?.("[data-select-ingredient]");
    if (!card || !["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    selectIngredient(card.dataset.selectIngredient || "");
  });
  document.addEventListener("input", (event) => {
    if (event.target.closest?.(".ingredient-editor") || event.target.closest?.("#trajectory-settings")) {
      if (pendingBakeRecipe) ingredientEditorDirty = true;
      return;
    }
    const card = event.target.closest?.(".source-card");
    if (card && event.target.matches('[data-field="label"]')) {
      refreshAssemblyTargetOptions();
      syncFilmstripSourceOptions();
      updateViewer();
    }
    if (event.target.closest?.(".filmstrip-row")) {
      state.design.protocol.trial_strips = collectTrialStrips();
      updateFilmstripCounts();
    }
    if (event.target.closest?.("#toolkit-page") && !event.target.matches?.("[data-preview-view-control]")) markDesignerDirty();
  });
  document.addEventListener("change", (event) => {
    if (event.target.closest?.(".ingredient-editor") || event.target.closest?.("#trajectory-settings")) {
      if (pendingBakeRecipe) ingredientEditorDirty = true;
      return;
    }
    const sourceLabelCard = event.target.closest?.(".source-card");
    if (sourceLabelCard && event.target.matches('[data-field="label"]')) {
      reconcileSourceCardLabel(sourceLabelCard);
      refreshAssemblyTargetOptions();
      renderSourceCounts();
      updateViewer();
    }
    if (event.target.matches('[data-field="audio_role"]')) {
      const card = event.target.closest(".audio-source-card");
      const title = card?.querySelector(".source-card-heading strong");
      if (card) card.dataset.audioRole = event.target.value;
      if (title) title.textContent = audioRoleTitle(event.target.value);
      const motionField = card?.querySelector('[data-field="motion_mode"]');
      if (motionField) {
        motionField.value = event.target.value === "preserve" || event.target.value === "prestimulus" ? "stationary" : "looming";
      }
      applySourceCardColor(card, event.target.value === "prestimulus" ? "prestimulus" : card?.querySelector('[data-field="tone_type"]')?.value || "custom_audio");
      refreshAssemblyTargetOptions();
      syncFilmstripSourceOptions();
      renderSourceCounts();
      updateViewer();
    }
    if (event.target.matches('.audio-source-card [data-field="tone_type"]')) {
      const card = event.target.closest(".audio-source-card");
      applySourceCardColor(card, event.target.value);
      syncFilmstripSourceOptions();
      updateViewer();
    }
    if (event.target.matches('.noise-source-card [data-field="noise_type"]')) {
      const card = event.target.closest(".noise-source-card");
      const title = card?.querySelector(".source-card-heading strong");
      if (title) title.textContent = `${noiseTypeLabel(event.target.value)} noise`;
      applySourceCardColor(card, event.target.value);
      syncFilmstripSourceOptions();
      updateViewer();
    }
    if (event.target.matches('[data-element-field="is_jitter"]')) {
      setFilmstripElementMode(event.target);
      markDesignerDirty();
      return;
    }
    if (event.target.closest?.(".filmstrip-row")) {
      state.design.protocol.trial_strips = collectTrialStrips();
      updateFilmstripCounts();
    }
    if (event.target.closest?.("#toolkit-page") && !event.target.matches?.("[data-preview-view-control]")) markDesignerDirty();
  });
  window.addEventListener("beforeunload", (event) => {
    if (!designerDirty && !ingredientEditorDirty) return;
    event.preventDefault();
    event.returnValue = "";
  });
}

function reportError(error) {
  console.error(error);
  showToast(error.message || String(error));
}

loadApiBase();
loadCompanionToken();
loadResizableLayoutSettings();
enforceExternalLinkTargets();
exposeDashboardAuditHook();
initializeBoundedSelects();
wireEvents();
initializePageTabs();
initializeDocumentationTypography();
initializeLazySurfaces();
initializePublicationNetworkSurface();
window.PPSDesignerApp = Object.freeze({
  getState: () => clone(state),
  exportProfile: (format) => {
    if (!["json", "bundle"].includes(format)) throw new Error("Unsupported profile export.");
    return api(`/api/profiles/export-${format}`, { method: "POST", rawResponse: true,
      body: JSON.stringify({ expected_revision: normalizedDesignerProgress().revision }) });
  },
  isHosted: () => staticModeActive,
  restoreHostedDraft: (snapshot) => {
    if (!snapshot?.design) return false;
    state = clone(snapshot);
    staticModeActive = true;
    editModeActive = false;
    markDesignerSaved();
    renderAll();
    updateViewer();
    return true;
  },
  isDirty: () => designerDirty || ingredientEditorDirty,
  markSaved: markDesignerSaved,
});

loadState({ resetEditMode: true }).catch(reportError);
